// ../internal/src/providers/attachment-provider/attachment-provider.tsx
import React4 from "react";

// ../internal/src/helpers/constants.tsx
var maxFileSizeinInteger = 25e6;
var excessCharactersThreshold = 20;
var VacServiceHeaders = {
  BusinessSegmentId: "Business-Segment-Id",
  ChannelOriginId: "Channel-Origin-Id",
  XCpAuthorization: "x-cp-authorization",
  IsCgpEnabled: "Is-Cgp-Enabled"
};
var AttachmentOperationTypes = {
  DOWNLOAD: "download",
  UPLOAD: "upload"
};
var LocalizationContentKeys = {
  ATTACHMENT: "attachment",
  DIALOG: "dialog",
  ACTIVITY_INDICATOR: "activityIndicator",
  ERROR: "error",
  MESSAGE: "message",
  PARTICIPANT: "participant",
  LOADING: "loading"
};
var DebugIdToken = "_DEBUG_";

// ../internal/src/hooks/use-authentication-config.tsx
import React2 from "react";
import { useProvidedState } from "@shared-ui/provided-state-context";

// ../internal/src/helpers/query-utils.ts
function getConversationContext(conversationId, participantId, conversationType) {
  var _a;
  return {
    conversationId,
    participantId,
    conversationType,
    attributes: typeof window !== "undefined" && ((_a = window.location) == null ? void 0 : _a.href) ? {
      currentPageUrl: window.location.href
    } : null
  };
}
function getAuthenticationConfig(orchestratorState, chatInstance, channelOriginId) {
  var _a, _b;
  return {
    authToken: (_a = orchestratorState == null ? void 0 : orchestratorState.vacConfig) == null ? void 0 : _a.authToken,
    channelOriginId: ((_b = chatInstance == null ? void 0 : chatInstance.chatConfig) == null ? void 0 : _b.channelOriginId) || channelOriginId
  };
}
function getClientContext() {
  if (typeof Intl !== "undefined") {
    return {
      timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }
  return void 0;
}

// ../internal/src/providers/vac-id/vac-id-provider.tsx
import React, { useState } from "react";
import { v4 as uuid } from "uuid";
var VacIdContext = React.createContext(void 0);
var VacIdProvider = ({
  value,
  children
}) => {
  return /* @__PURE__ */React.createElement(VacIdContext.Provider, {
    value
  }, children);
};
VacIdProvider.displayName = "VacIdProvider";
var useVacId = () => {
  const vacId = React.useContext(VacIdContext);
  if (!vacId) {
    throw new Error("useVacId must be used with VacIdProvider");
  }
  return vacId;
};
var withVacId = WrappedComponent => {
  const Wrapper = props => {
    const {
      vacId: vacIdProp
    } = props;
    const [vacId] = useState(vacIdProp || uuid());
    const updatedProps = vacIdProp ? props : {
      ...props,
      vacId
    };
    return /* @__PURE__ */React.createElement(VacIdProvider, {
      value: vacId
    }, /* @__PURE__ */React.createElement(WrappedComponent, {
      ...updatedProps
    }));
  };
  Wrapper.displayName = "withVacId";
  return Wrapper;
};

// ../internal/src/hooks/use-authentication-config.tsx
function useAuthenticationConfig(channelOriginId) {
  var _a;
  const [state] = useProvidedState();
  const vacId = useVacId();
  const orchestratorState = state == null ? void 0 : state.orchestratorState;
  const chatInstance = (_a = state == null ? void 0 : state.chatInstancesState) == null ? void 0 : _a[vacId];
  const authenticationConfig = React2.useMemo(() => getAuthenticationConfig(orchestratorState, chatInstance, channelOriginId), [orchestratorState, chatInstance, channelOriginId]);
  return [authenticationConfig];
}

// ../internal/src/hooks/use-chat-instance-state.tsx
import React3 from "react";
import { useProvidedState as useProvidedState2 } from "@shared-ui/provided-state-context";
function useChatInstanceState() {
  var _a;
  const vacId = useVacId();
  const [state, dispatch] = useProvidedState2();
  const chatInstance = (_a = state == null ? void 0 : state.chatInstancesState) == null ? void 0 : _a[vacId];
  const chatInstanceDispatch = React3.useCallback(({
    type,
    payload: payload2
  }) => {
    const chatInstanceAction = {
      type,
      payload: {
        ...payload2,
        vacId
      }
    };
    dispatch(chatInstanceAction);
  }, [dispatch, vacId]);
  return [chatInstance, chatInstanceDispatch];
}

// ../internal/src/providers/attachment-provider/attachment-provider.tsx
var defaults = {
  attachmentConfig: {}
};
var AttachmentContext = React4.createContext(defaults);
var AttachmentProvider = props => {
  const {
    children,
    attachmentConfig
  } = props;
  return /* @__PURE__ */React4.createElement(AttachmentContext.Provider, {
    value: {
      attachmentConfig
    }
  }, children);
};
AttachmentProvider.displayName = "AttachmentProvider";
var useAttachment = () => {
  const {
    attachmentConfig
  } = React4.useContext(AttachmentContext);
  const [authenticationConfig] = useAuthenticationConfig();
  if (!attachmentConfig) {
    throw new Error("useAttachment requires and must be used within AttachmentProvider");
  }
  const uploadFile = async file => {
    const {
      partnerId,
      conversationId
    } = attachmentConfig;
    const requestHeaders = new Headers();
    requestHeaders.set(VacServiceHeaders.XCpAuthorization, `${authenticationConfig == null ? void 0 : authenticationConfig.authToken}`);
    const urlPrefix = window.location.host.includes("localhost") ? "https://wwwexpediacom.trunk.sb.karmalab.net" : "";
    const fileFormData = new FormData();
    fileFormData.append("upload", file, file.name);
    const response = await fetch(`${urlPrefix}/vacservice/api/v2/partners/${partnerId}/conversations/${conversationId}/files/upload`, {
      method: "POST",
      body: fileFormData,
      headers: requestHeaders
    });
    const result = await response.json();
    return result;
  };
  const downloadFile2 = async mediaUrl => {
    const requestHeaders = new Headers();
    requestHeaders.set(VacServiceHeaders.XCpAuthorization, `${authenticationConfig == null ? void 0 : authenticationConfig.authToken}`);
    const urlPrefix = window.location.host.includes("localhost") ? "https://wwwexpediacom.trunk.sb.karmalab.net" : "";
    const response = await fetch(`${urlPrefix}${mediaUrl}`, {
      method: "GET",
      headers: requestHeaders
    });
    const result = await response.json();
    return result;
  };
  return [uploadFile, downloadFile2];
};
var withAttachment = WrappedComponent => {
  const Wrapper = props => {
    const [chatInstance] = useChatInstanceState();
    const {
      chatConfig
    } = chatInstance || {};
    const conversationId = chatInstance == null ? void 0 : chatInstance.activeConversationId;
    const {
      partnerGuid: partnerId
    } = chatConfig || {};
    return /* @__PURE__ */React4.createElement(AttachmentProvider, {
      attachmentConfig: {
        partnerId,
        conversationId
      }
    }, /* @__PURE__ */React4.createElement(WrappedComponent, {
      ...props
    }));
  };
  Wrapper.displayName = "withAttachment";
  return Wrapper;
};

// ../internal/src/providers/registered-operations/registered-operations-provider.tsx
import React5, { useMemo } from "react";
var DEFAULT = {
  registry: {},
  update: () => null
};
var RegisteredOperationsContext = React5.createContext(DEFAULT);
var RegisteredOperationsProvider = ({
  value,
  children
}) => {
  const [registry4, setRegistry] = React5.useState(value != null ? value : {});
  return /* @__PURE__ */React5.createElement(RegisteredOperationsContext.Provider, {
    value: useMemo(() => ({
      registry: registry4,
      update: setRegistry
    }), [registry4, setRegistry])
  }, children);
};
RegisteredOperationsProvider.displayName = "RegisteredOperationsProvider";
var useOperation = o => {
  var _a;
  const operations = React5.useContext(RegisteredOperationsContext);
  if (!operations) {
    throw new Error("useOperation requires and must be used within RegisteredOperationsProvider");
  }
  const {
    registry: registry4
  } = operations;
  return (_a = registry4[o]) != null ? _a : null;
};
var useOperationsRegistry = newRegistry => {
  const operations = React5.useContext(RegisteredOperationsContext);
  if (!operations) {
    throw new Error("useOperation requires and must be used within RegisteredOperationsProvider");
  }
  const {
    registry: registry4,
    update
  } = operations;
  update(Object.assign(registry4, newRegistry));
};
var withRegisteredOperations = WrappedComponent => {
  const Wrapper = props => {
    const {
      value
    } = props;
    const [registry4] = React5.useState(value || {});
    const updatedProps = value ? props : {
      ...props,
      registry: registry4
    };
    return /* @__PURE__ */React5.createElement(RegisteredOperationsProvider, {
      value: registry4
    }, /* @__PURE__ */React5.createElement(WrappedComponent, {
      ...updatedProps
    }));
  };
  Wrapper.displayName = "withRegisteredOperations";
  return Wrapper;
};

// src/components/chat/chat.tsx
import React104, { useState as useState6 } from "react";
import { v4 as uuidv417 } from "uuid";
import { EGDSLayoutFlex as EGDSLayoutFlex40 } from "@egds/react-core/layout-flex";
import { EGDSLoader as EGDSLoader5 } from "@egds/react-core/loader";
import { EGDSSkipLink } from "@egds/react-core/skip-link";
import { EGDSSpacing as EGDSSpacing25 } from "@egds/react-core/spacing";
import { EGDSHeading as EGDSHeading12 } from "@egds/react-core/text";
import { EGDSToast } from "@egds/react-core/toast";
import { ChatContainer, ChatContainerContent, ChatContainerFooter, ChatContainerHeader, ScrollChatContentToBottomEvent as ScrollChatContentToBottomEvent2 } from "@egds/react-virtual-agent-control";
import { useBexApi as useBexApi9 } from "@shared-ui/bex-api-context";
import { EgClickstreamImpressionTracker } from "@shared-ui/clickstream-analytics-context";
import { useInteractionRumTracker as useInteractionRumTracker8 } from "@shared-ui/core-real-user-monitoring/context";
import { useSignalPublisher as useSignalPublisher10, useSignalSubscriber as useSignalSubscriber7 } from "@shared-ui/core-signal-manager/context";
import { DialogProvider } from "@shared-ui/dialog-context";
import { SystemEventLevel as SystemEventLevel17, useLoggerEvents as useLoggerEvents3 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState24 } from "@shared-ui/provided-state-context";
import { VacChatMessageSentSignal as VacChatMessageSentSignal2, VacChatWindowVisibleSignal, VacConnectedSignal as VacConnectedSignal2, VacConnectWebsocketSignal, VacConversationCreatedSignal as VacConversationCreatedSignal2, VacCreateConversationSignal, VacOrchestratorQuerySignal, VirtualAgentControlMessageType } from "@shared-ui/retail-signals-virtual-agent";

// ../internal/src/components/errors/full-page-error-view.tsx
import React8, { useEffect } from "react";
import { EGDSButtonSize, EGDSTertiaryButton } from "@egds/react-core/button";
import { EGDSCard } from "@egds/react-core/cards";
import { EGDSIcon, EGDSIconSize } from "@egds/react-core/icons";
import { EGDSLayoutFlex, EGDSLayoutFlexItem } from "@egds/react-core/layout-flex";
import { EGDSLayoutPosition } from "@egds/react-core/layout-position";
import { EGDSText } from "@egds/react-core/text";
import { EGDSToolbar } from "@egds/react-core/toolbar";
import { useInteractionRumTracker } from "@shared-ui/core-real-user-monitoring/context";
import { SystemEventLevel as SystemEventLevel4 } from "@shared-ui/logger-context";

// ../internal/src/helpers/clickstream-events-payload.ts
var VIRTUAL_AGENT_ICON_VIEWED = {
  event: {
    event_name: "virtual_agent_icon.viewed",
    event_type: "Impression",
    event_category: "virtual_agent.vac_sdui",
    event_version: "5.11.1"
  }
};
var VIRTUAL_AGENT_ICON_SELECTED = {
  event: {
    event_name: "virtual_agent_icon.selected",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "3.12.1",
    action_location: "first_action_button_icon"
  }
};
var VIRTUAL_AGENT_OPENED = {
  event: {
    event_name: "virtual_agent.opened",
    event_type: "Outcome",
    event_category: "virtual_agent.vac_sdui",
    event_version: "7.14.1",
    action_location: "first_action_button_icon"
  }
};
var VIRTUAL_AGENT_OPENED_FAILED = {
  event: {
    event_name: "virtual_agent_opened.failed",
    event_type: "Outcome",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.7.1"
  }
};
var VIRTUAL_AGENT_CLOSED = {
  event: {
    event_name: "virtual_agent.closed",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "6.12.1",
    action_location: "close_action_button"
  }
};
var VIRTUAL_AGENT_ERROR_BANNER_PRESENTED = {
  event: {
    event_name: "virtual_agent_error_banner.presented",
    event_type: "Impression",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.7.1"
  }
};
var VIRTUAL_AGENT_RECONNECT_SELECTED = {
  event: {
    event_name: "virtual_agent_reconnect.selected",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.6.1"
  }
};
var VIRTUAL_AGENT_ATTACHMENT_SELECTED = {
  event: {
    event_name: "virtual_agent_attachment.selected",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.6.1"
  }
};
var ATTACHMENT_FILE_DOWNLOAD_SUBMITTED = {
  event: {
    event_name: "attachment_file_download.submitted",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.7.1"
  }
};
var ATTACHMENT_FILE_DOWNLOAD_SUCCEEDED = {
  event: {
    event_name: "attachment_file_download.succeeded",
    event_type: "Outcome",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.7.1"
  }
};
var ATTACHMENT_FILE_DOWNLOAD_FAILED = {
  event: {
    event_name: "attachment_file_download.failed",
    event_type: "Outcome",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.7.1"
  }
};
var ATTACHMENT_FILE_PREVIEW_SELECTED = {
  event: {
    event_name: "attachment_file_preview.selected",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.7.1"
  }
};
var ATTACHMENT_FILE_UPLOAD_SUCCEEDED = {
  event: {
    event_name: "attachment_file_upload.succeeded",
    event_type: "Outcome",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.6.1"
  }
};
var ATTACHMENT_FILE_UPLOAD_FAILED = {
  event: {
    event_name: "attachment_file_upload.failed",
    event_type: "Outcome",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.6.1"
  }
};
var VIRTUAL_AGENT_SETTINGS_MENU_SELECTED = {
  event: {
    event_name: "virtual_agent_settings_menu.selected",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.12.1"
  }
};
var VIRTUAL_AGENT_SETTINGS_MENU_ITEM_PRESENTED = {
  event: {
    event_name: "virtual_agent_settings_menu_item.presented",
    event_type: "Impression",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.12.1"
  }
};
var VIRTUAL_AGENT_SETTINGS_MENU_ITEM_SELECTED = {
  event: {
    event_name: "virtual_agent_settings_menu_item.selected",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.12.1"
  }
};
var VIRTUAL_AGENT_QUICK_REPLY_PRESENTED = {
  event: {
    event_name: "virtual_agent_quick_reply.presented",
    event_type: "Impression",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.3.1"
  }
};
var VIRTUAL_AGENT_QUICK_REPLY_SELECTED = {
  event: {
    event_name: "virtual_agent_quick_reply.selected",
    event_type: "Interaction",
    event_category: "virtual_agent.vac_sdui",
    event_version: "1.3.1"
  }
};
var EVENT_MAPPER = {
  VIRTUAL_AGENT_ICON_VIEWED,
  VIRTUAL_AGENT_ICON_SELECTED,
  VIRTUAL_AGENT_OPENED,
  VIRTUAL_AGENT_OPENED_FAILED,
  VIRTUAL_AGENT_CLOSED,
  VIRTUAL_AGENT_ERROR_BANNER_PRESENTED,
  VIRTUAL_AGENT_RECONNECT_SELECTED,
  VIRTUAL_AGENT_ATTACHMENT_SELECTED,
  ATTACHMENT_FILE_DOWNLOAD_SUBMITTED,
  ATTACHMENT_FILE_DOWNLOAD_SUCCEEDED,
  ATTACHMENT_FILE_DOWNLOAD_FAILED,
  ATTACHMENT_FILE_PREVIEW_SELECTED,
  ATTACHMENT_FILE_UPLOAD_SUCCEEDED,
  ATTACHMENT_FILE_UPLOAD_FAILED,
  VIRTUAL_AGENT_SETTINGS_MENU_SELECTED,
  VIRTUAL_AGENT_SETTINGS_MENU_ITEM_PRESENTED,
  VIRTUAL_AGENT_SETTINGS_MENU_ITEM_SELECTED,
  VIRTUAL_AGENT_QUICK_REPLY_PRESENTED,
  VIRTUAL_AGENT_QUICK_REPLY_SELECTED
};

// ../internal/src/helpers/build-clickstream-data.ts
var buildClickstreamEventPaylaod = (eventKey, conversationId, conversationType) => {
  if (EVENT_MAPPER[eventKey]) {
    const data = {
      ...EVENT_MAPPER[eventKey],
      virtual_agent: {
        conversation_id: conversationId,
        conversation_type: conversationType
      }
    };
    return data;
  }
};
var buildClickstreamAdditionalContextPayload = (eventKey, conversationId, conversationType, additionalContext, messageId) => {
  if (EVENT_MAPPER[eventKey]) {
    const data = {
      ...EVENT_MAPPER[eventKey],
      virtual_agent: {
        conversation_id: conversationId,
        conversation_type: conversationType,
        ...(messageId && {
          message_id: messageId
        })
      },
      additional_context: additionalContext
    };
    return data;
  }
};
var buildClickstreamErrorListPayload = (eventKey, conversationId, conversationType, errorPayload) => {
  const data = {
    ...EVENT_MAPPER[eventKey],
    virtual_agent: {
      conversation_id: conversationId,
      conversation_type: conversationType
    },
    error_list: [errorPayload && typeof errorPayload === "object" ? {
      ...errorPayload
    } : {}]
  };
  return data;
};
var buildClickstreamAdditionalContextErrorPayload = (eventKey, conversationId, conversationType, additionalContext, errorPayload) => {
  const data = {
    ...EVENT_MAPPER[eventKey],
    virtual_agent: {
      conversation_id: conversationId,
      conversation_type: conversationType
    },
    additional_context: additionalContext,
    error_list: [errorPayload && typeof errorPayload === "object" ? {
      ...errorPayload
    } : {}]
  };
  return data;
};

// ../internal/src/helpers/events.ts
import { SystemEvent, SystemEventLevel } from "@shared-ui/logger-context";
var PREFIX = "VacLogging";
var COMPONENT_NAME = "TRAVELLER_VA";
var EVENTS = {
  ERROR: new SystemEvent(SystemEventLevel.ERROR, `${PREFIX}.${COMPONENT_NAME}.error.message`),
  API_SUCCESS: new SystemEvent(SystemEventLevel.INFO, `${PREFIX}.${COMPONENT_NAME}.api.success`),
  API_ERROR: new SystemEvent(SystemEventLevel.ERROR, `${PREFIX}.${COMPONENT_NAME}.api.error`),
  PERFORMANCE: new SystemEvent(SystemEventLevel.INFO, `${PREFIX}.${COMPONENT_NAME}.performance`),
  RUNTIME: new SystemEvent(SystemEventLevel.ERROR, `${PREFIX}.${COMPONENT_NAME}.runtime`)
};

// ../internal/src/hooks/analytics/use-analytics.ts
import { useCallback as useCallback2 } from "react";
import { useBexApi } from "@shared-ui/bex-api-context";
import { useEgClickstream } from "@shared-ui/clickstream-analytics-context";
import { SystemEventLevel as SystemEventLevel3 } from "@shared-ui/logger-context";

// ../internal/src/hooks/use-logging.tsx
import { useCallback } from "react";
import { SystemEventLevel as SystemEventLevel2, useLoggerEvents } from "@shared-ui/logger-context";

// ../internal/src/helpers/utils.tsx
import { liveAnnounce } from "@egds/react-core/utils";

// src/__generated__/types.ts
var ConversationMode = /* @__PURE__ */(ConversationMode2 => {
  ConversationMode2["CREATE_CONVERSATION"] = "CREATE_CONVERSATION";
  ConversationMode2["JOIN_CONVERSATION"] = "JOIN_CONVERSATION";
  ConversationMode2["RESUME_CONVERSATION"] = "RESUME_CONVERSATION";
  return ConversationMode2;
})(ConversationMode || {});
var ConversationType = /* @__PURE__ */(ConversationType3 => {
  ConversationType3["AI_CONCIERGE"] = "AI_CONCIERGE";
  ConversationType3["CHATGPT"] = "CHATGPT";
  ConversationType3["GOOGLE_ITINERARY"] = "GOOGLE_ITINERARY";
  ConversationType3["MAIN"] = "MAIN";
  ConversationType3["PROPERTY_COMPARE"] = "PROPERTY_COMPARE";
  ConversationType3["ROMIE_V1"] = "ROMIE_V1";
  ConversationType3["ROMIE_V1_VARIANT"] = "ROMIE_V1_VARIANT";
  ConversationType3["SUPPLIER"] = "SUPPLIER";
  ConversationType3["TRIP_COLLABORATION"] = "TRIP_COLLABORATION";
  return ConversationType3;
})(ConversationType || {});

// ../internal/src/helpers/utils.tsx
var parseJson = json => {
  try {
    return JSON.parse(json);
  } catch (err) {
    return void 0;
  }
};
var sizeToByteDisplay = size => {
  const sizeAsNumber = Number.parseInt(size, 10);
  if (!Number.isNaN(sizeAsNumber)) {
    const sizeInBytes = Math.ceil(Math.max(0, sizeAsNumber));
    const kb = sizeInBytes / 1024;
    if (kb < 1) {
      return `${sizeInBytes} bytes`;
    }
    const mb = kb / 1024;
    if (mb < 1) {
      return `${Math.round(kb * 10) / 10} KB`;
    }
    return `${Math.round(mb * 10) / 10} MB`;
  }
  return void 0;
};
var decodeFileBytes = ({
  bytes,
  mediaType
}) => {
  const decodedBytes = window.atob(bytes);
  const arrayBuffer = new ArrayBuffer(decodedBytes.length);
  const byteArray = new Uint8Array(arrayBuffer);
  for (let i = 0; i < decodedBytes.length; i += 1) {
    byteArray[i] = decodedBytes.charCodeAt(i);
  }
  const fileToOpen = new Blob([byteArray], {
    type: mediaType
  });
  return window.URL.createObjectURL(fileToOpen);
};
var payload = obj => {
  return Object.entries(obj).reduce((result, event) => result.concat(" ", `${event[0]}="${event[1]}"`), "");
};
function localizeTemplateLiteral(localizationString, literal, value) {
  if (!localizationString) {
    return "";
  }
  return localizationString.replace(`\${${literal}}`, value);
}
function getEventPayloadAnalytics(analyticsPayload, eventName, version) {
  var _a, _b, _c;
  const parsedPayload = JSON.parse(analyticsPayload);
  const versionShorthand = `v${version.split(".")[0]}`;
  return (_c = (_b = (_a = parsedPayload == null ? void 0 : parsedPayload.events) == null ? void 0 : _a[eventName]) == null ? void 0 : _b[versionShorthand]) == null ? void 0 : _c.event_data;
}
function validateInputString(trimmedUserInput, chatInputLimit, charLimitExists, errorLocalizations) {
  const wordsOverLimit = trimmedUserInput.length - chatInputLimit;
  const showTextOverflowError = wordsOverLimit > 0 && charLimitExists;
  const inputLengthString = `${trimmedUserInput.length} / ${chatInputLimit}`;
  const removeExcessCharacters = localizeTemplateLiteral(errorLocalizations == null ? void 0 : errorLocalizations.removeExcessCharacters, "excessCharacters", `${wordsOverLimit}`);
  const primaryErrorText = wordsOverLimit === 1 ? errorLocalizations == null ? void 0 : errorLocalizations.removeSingleExcessCharacter : removeExcessCharacters;
  const validationProp = showTextOverflowError ? "invalid" : "instruction";
  return {
    wordLimitInfoProps: {
      [validationProp]: {
        primary: showTextOverflowError ? primaryErrorText : "",
        secondary: inputLengthString
      }
    },
    showTextOverflowError
  };
}
function isFakeIntent(messagePayload) {
  const parsedPayload = parseJson(messagePayload);
  return (parsedPayload == null ? void 0 : parsedPayload.intent) === "FAKE_INTENT" /* FAKE_INTENT */;
}
function getPartcipantDisplayName(participant) {
  var _a;
  const user = participant == null ? void 0 : participant.user;
  if (!user) {
    return void 0;
  }
  if ((_a = user.attributes) == null ? void 0 : _a.displayNameOverride) {
    return user.attributes.displayNameOverride;
  }
  if (participant.participantType === "Supplier" /* Supplier */) {
    return user.preferredName;
  }
  if (user.firstName) {
    return user.firstName;
  }
  if (user.preferredName) {
    return user.preferredName;
  }
  return user.userName;
}
var ariaAnnounceLive = (textContent, level, containerRef, announcementId, removeElementFromDom) => {
  liveAnnounce(textContent, level, "is-visually-hidden", containerRef, announcementId);
  setTimeout(() => {
    liveAnnounce("", level, "is-visually-hidden", containerRef, announcementId);
    if (removeElementFromDom) {
      const element = document.querySelector(`#${announcementId}`);
      element == null ? void 0 : element.remove();
    }
  }, 3e3);
};

// ../internal/src/hooks/use-conversation-state.tsx
import React6 from "react";
import { useProvidedState as useProvidedState3 } from "@shared-ui/provided-state-context";
function useConversationState() {
  const [chatInstance] = useChatInstanceState();
  const [state, dispatch] = useProvidedState3();
  const conversationId = chatInstance == null ? void 0 : chatInstance.activeConversationId;
  const conversation = conversationId ? state.conversationsState.conversationsById[conversationId] : void 0;
  const conversationDispatch = React6.useCallback(({
    type,
    payload: payload2
  }) => {
    const conversationAction = {
      type,
      payload: {
        ...payload2,
        conversationId
      }
    };
    dispatch(conversationAction);
  }, [dispatch, conversationId]);
  const conversationContext = React6.useMemo(() => getConversationContext(conversationId, conversation == null ? void 0 : conversation.participantId, conversation == null ? void 0 : conversation.type), [conversationId, conversation == null ? void 0 : conversation.participantId, conversation == null ? void 0 : conversation.type]);
  return [conversation, conversationDispatch, conversationContext];
}

// ../internal/src/hooks/use-logging.tsx
var useLogging = EVENTS3 => {
  const loggingEvents = useLoggerEvents(EVENTS3);
  const [conversation] = useConversationState();
  const [chatInstance] = useChatInstanceState();
  const vacId = useVacId();
  const {
    partnerGuid,
    modeOptions
  } = (chatInstance == null ? void 0 : chatInstance.chatConfig) || {};
  const logger = useCallback((key, data) => {
    if (key in loggingEvents) {
      loggingEvents[key].log(payload({
        flowId: vacId,
        eventlevel: SystemEventLevel2.INFO,
        conversationId: conversation == null ? void 0 : conversation.conversationId,
        conversationType: modeOptions == null ? void 0 : modeOptions.conversationType,
        partnerId: partnerGuid,
        ...data
      }));
    }
  }, [loggingEvents, modeOptions == null ? void 0 : modeOptions.conversationType, conversation == null ? void 0 : conversation.conversationId, partnerGuid, vacId]);
  return logger;
};

// ../internal/src/hooks/analytics/use-analytics.ts
var useAnalytics = () => {
  const track = useEgClickstream();
  const loggerV2 = useLogging(EVENTS);
  const {
    context
  } = useBexApi();
  const trackEvent = useCallback2(analyticsObject => {
    try {
      const eventPayload = analyticsObject;
      const analyticsTrackerPayload = {
        ...eventPayload
      };
      track(analyticsTrackerPayload);
    } catch (e) {
      loggerV2("RUNTIME", {
        eventName: "vac.runtime" /* RUNTIME */,
        eventlevel: SystemEventLevel3.INFO,
        operationName: "VAC_EgClickstreamError" /* VAC_EG_CLICKSTREAM_ERROR */,
        logSource: PREFIX
      });
    }
  }, [context, track]);
  return {
    trackEvent
  };
};

// ../internal/src/hooks/use-error-state.tsx
import React7 from "react";
import { useProvidedState as useProvidedState4 } from "@shared-ui/provided-state-context";

// ../internal/src/store/errors/constants.tsx
var ORCHESTRATOR = "orchestrator";
var defaultErrorBanner = {
  header: "Something went wrong",
  text: "Please refresh the page or try again later",
  retryText: null
};
var defaultErrorFullPageView = {
  ...defaultErrorBanner,
  __typename: "VirtualAgentControlErrorFullPageView",
  icon: {
    __typename: "Icon",
    id: "error",
    description: "error icon",
    title: "error icon"
  }
};
var connectionLostErrorFullPageView = {
  __typename: "VirtualAgentControlErrorFullPageView",
  header: "We're having trouble loading",
  text: "Please refresh the page or try again later",
  retryText: null,
  icon: {
    __typename: "Icon",
    id: "error",
    description: "error icon",
    title: "error icon"
  }
};
var connectionLostErrorBanner = {
  __typename: "VirtualAgentControlErrorBanner",
  header: "We lost connection",
  text: "Try to reconnect or refresh the page later",
  retryText: null
};
var fetchCredentialsFailedErrorBanner = {
  __typename: "VirtualAgentControlErrorBanner",
  text: "Please refresh the page to continue",
  header: "Your session expired",
  retryText: null
};
var fetchCredentialsFailedErrorFullPageView = {
  ...fetchCredentialsFailedErrorBanner,
  __typename: "VirtualAgentControlErrorFullPageView",
  icon: {
    __typename: "Icon",
    description: "error",
    id: "error",
    title: "error"
  }
};
var errorBannerPlaceholders = {
  TRY_TO_RECONNECT: "{tryToReconnect}",
  RETRY: "{retryText}",
  DISMISS: "{dismissText}"
};
function getConnectionLostError(header) {
  if (header === connectionLostErrorFullPageView.header || header === connectionLostErrorBanner.header) {
    return connectionLostErrorBanner.header;
  }
}

// ../internal/src/hooks/use-error-state.tsx
function useChatInstanceError() {
  var _a, _b, _c, _d;
  const [state, dispatch] = useProvidedState4();
  const vacId = useVacId();
  const orchestratorError = (_a = state.errorsState) == null ? void 0 : _a[ORCHESTRATOR];
  const chatInstanceError = (_b = state.errorsState) == null ? void 0 : _b[vacId];
  const activeConversationId = (_d = (_c = state.chatInstancesState) == null ? void 0 : _c[vacId]) == null ? void 0 : _d.activeConversationId;
  let errorBanner;
  let fullPageView;
  if (orchestratorError == null ? void 0 : orchestratorError.active) {
    if (activeConversationId) {
      errorBanner = orchestratorError.errorComponent.errorBanner;
    } else {
      fullPageView = orchestratorError.errorComponent.fullPageView;
    }
  } else if (chatInstanceError == null ? void 0 : chatInstanceError.active) {
    errorBanner = chatInstanceError.errorComponent.errorBanner;
    fullPageView = chatInstanceError.errorComponent.fullPageView;
  }
  const chatInstanceErrorDispatch = React7.useCallback(({
    type,
    payload: payload2
  }) => {
    dispatch({
      type,
      payload: {
        ...payload2,
        source: vacId
      }
    });
  }, [dispatch, vacId]);
  return [errorBanner, fullPageView, chatInstanceErrorDispatch];
}

// ../internal/src/components/errors/full-page-error-view.tsx
var VirtualAgentControlFullPageErrorView = ({
  onClose,
  embedded
}) => {
  const [, fullPageView] = useChatInstanceError();
  const {
    trackEvent
  } = useAnalytics();
  const {
    trackEvent: trackRumEvent
  } = useInteractionRumTracker();
  const [conversation] = useConversationState();
  const loggerV2 = useLogging(EVENTS);
  useEffect(() => {
    if (fullPageView) {
      const errorTracker = {};
      if (fullPageView == null ? void 0 : fullPageView.header) {
        errorTracker.error_name = getConnectionLostError(fullPageView.header) || header;
      } else {
        errorTracker.error_description = (fullPageView == null ? void 0 : fullPageView.text) || "Something went wrong";
      }
      trackEvent(buildClickstreamErrorListPayload("VIRTUAL_AGENT_OPENED_FAILED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, errorTracker));
      loggerV2("PERFORMANCE", {
        eventName: "vac.performance" /* PERFORMANCE */,
        eventlevel: SystemEventLevel4.INFO,
        operationName: "VAC_PERF_ContainerError" /* VAC_PERF_CONTAINER_ERROR */,
        logSource: PREFIX,
        performanceEventTime: (/* @__PURE__ */new Date()).getTime()
      });
      trackRumEvent("action", {
        action: "VAC_PERF_ContainerError" /* VAC_PERF_CONTAINER_ERROR */,
        attributes: {
          performanceEventTime: (/* @__PURE__ */new Date()).getTime()
        }
      });
    }
  }, [fullPageView]);
  if (!fullPageView) {
    return null;
  }
  const {
    header = "Something went wrong",
    text = "Please refresh the page or try again later",
    icon
  } = fullPageView || {};
  const {
    id = "error",
    description = "error icon"
  } = icon || {};
  return /* @__PURE__ */React8.createElement(EGDSLayoutPosition, {
    type: "absolute",
    position: {
      top: "zero",
      left: "zero",
      right: "zero",
      bottom: "zero"
    }
  }, !embedded && /* @__PURE__ */React8.createElement(EGDSToolbar, null, /* @__PURE__ */React8.createElement(EGDSTertiaryButton, {
    size: EGDSButtonSize.LARGE,
    "aria-label": "close",
    onClick: onClose,
    "data-stid": "close"
  }, /* @__PURE__ */React8.createElement(EGDSIcon, {
    name: "remove",
    id: "remove"
  }))), /* @__PURE__ */React8.createElement(EGDSCard, null, /* @__PURE__ */React8.createElement(EGDSLayoutFlex, {
    space: "one",
    direction: "column",
    alignItems: "center",
    justifyContent: "center",
    blockSize: "full_size"
  }, /* @__PURE__ */React8.createElement("div", {
    "aria-hidden": "true"
  }, /* @__PURE__ */React8.createElement(EGDSIcon, {
    id: `vac-full-page-error-${id}`,
    name: id,
    description,
    size: EGDSIconSize.XLARGE
  })), /* @__PURE__ */React8.createElement(EGDSText, {
    align: "center",
    size: 500,
    weight: "bold"
  }, header), /* @__PURE__ */React8.createElement(EGDSLayoutFlexItem, {
    grow: 0
  }, /* @__PURE__ */React8.createElement(EGDSText, {
    size: 300,
    weight: "regular",
    inline: true
  }, text)))));
};

// ../internal/src/components/errors/error-banner.tsx
import React9, { useEffect as useEffect2 } from "react";
import { EGDSBanner } from "@egds/react-core/banner";
import { EGDSIcon as EGDSIcon2 } from "@egds/react-core/icons";
import { EGDSLayoutFlex as EGDSLayoutFlex2, EGDSLayoutFlexItem as EGDSLayoutFlexItem2 } from "@egds/react-core/layout-flex";
import { EGDSLayoutPosition as EGDSLayoutPosition2 } from "@egds/react-core/layout-position";
import { EGDSLink } from "@egds/react-core/link";
import { EGDSSpacing } from "@egds/react-core/spacing";
import { EGDSHeading, EGDSText as EGDSText2 } from "@egds/react-core/text";
import { useSignalPublisher } from "@shared-ui/core-signal-manager/context";
import { SystemEventLevel as SystemEventLevel5 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState5 } from "@shared-ui/provided-state-context";
import { VacReconnectSignal } from "@shared-ui/retail-signals-virtual-agent";
var VirtualAgentControlErrorBanner = () => {
  var _a, _b, _c, _d;
  const [errorBanner, fullPageView, chatInstanceErrorDispatch] = useChatInstanceError();
  const {
    publish
  } = useSignalPublisher();
  const [state] = useProvidedState5();
  const {
    trackEvent
  } = useAnalytics();
  const [conversation] = useConversationState();
  const errorContainerRef = React9.useRef(null);
  const errorStrings = (_b = (_a = state.errorsState) == null ? void 0 : _a.orchestrator) == null ? void 0 : _b.offlineLocalizedErrorStrings;
  const downloadTranscript = useOperation("DOWNLOAD_TRANSCRIPT" /* DOWNLOAD_TRANSCRIPT */);
  const loggerV2 = useLogging(EVENTS);
  useEffect2(() => {
    if (errorBanner && !fullPageView) {
      const errorTracker = {};
      if (errorBanner == null ? void 0 : errorBanner.header) {
        errorTracker.error_name = getConnectionLostError(errorBanner.header) || errorBanner.header;
      } else {
        errorTracker.error_description = (errorBanner == null ? void 0 : errorBanner.text) || "Something went wrong";
      }
      trackEvent(buildClickstreamErrorListPayload("VIRTUAL_AGENT_ERROR_BANNER_PRESENTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, errorTracker));
      if (errorContainerRef.current) {
        errorContainerRef.current.focus();
      }
      loggerV2("RUNTIME", {
        eventName: "vac.runtime" /* RUNTIME */,
        eventlevel: SystemEventLevel5.ERROR,
        operationName: "VAC_BannerRuntimeError" /* VAC_BANNER_RUNTIME_ERROR */,
        logSource: PREFIX
      });
    }
  }, [errorBanner, fullPageView]);
  if (!errorBanner || fullPageView) {
    return null;
  }
  const {
    header = "Something went wrong",
    text = "Please refresh the page or try again later",
    retryText = "Try again",
    dismissText = "Dismiss",
    icon,
    retryAction
  } = errorBanner || {};
  const handleRefreshClick = () => {
    trackEvent(buildClickstreamEventPaylaod("VIRTUAL_AGENT_RECONNECT_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type));
    publish(new VacReconnectSignal("VAC_RECONNECT" /* VAC_Reconnect */, null));
  };
  const handleRetryClick = () => {
    if ((retryAction == null ? void 0 : retryAction.eventType) === "DOWNLOAD_TRANSCRIPT" /* DOWNLOAD_TRANSCRIPT */ && downloadTranscript) {
      downloadTranscript();
    }
    chatInstanceErrorDispatch({
      type: "CLEAR_ERRORS" /* CLEAR_ERRORS */
    });
  };
  const handleDismissClick = () => {
    chatInstanceErrorDispatch({
      type: "CLEAR_ERRORS" /* CLEAR_ERRORS */
    });
  };
  const renderLink = (linkText, onClick) => /* @__PURE__ */React9.createElement(EGDSLink, {
    inline: true,
    onClick,
    key: linkText
  }, /* @__PURE__ */React9.createElement("button", {
    type: "button"
  }, linkText));
  const renderTextWithLinks = () => {
    const parts = text.replaceAll("$", "").split(/(\{tryToReconnect\}|\{retryText\}|\{dismissText\})/);
    return /* @__PURE__ */React9.createElement(EGDSLayoutFlex2, null, parts.map((part, index) => {
      switch (part) {
        case errorBannerPlaceholders.TRY_TO_RECONNECT:
          return renderLink((errorStrings == null ? void 0 : errorStrings.tryToReconnect) || "Reconnect", handleRefreshClick);
        case errorBannerPlaceholders.RETRY:
          return renderLink(retryText, handleRetryClick);
        case errorBannerPlaceholders.DISMISS:
          return renderLink(dismissText, handleDismissClick);
        default:
          return part.startsWith(" ") ? /* @__PURE__ */React9.createElement(EGDSSpacing, {
            margin: {
              inlinestart: "one"
            },
            key: `${part}-${index}`
          }, /* @__PURE__ */React9.createElement(EGDSText2, null, part)) : /* @__PURE__ */React9.createElement(EGDSText2, {
            key: `${part}-${index}`
          }, part);
      }
    }));
  };
  return /* @__PURE__ */React9.createElement(EGDSLayoutPosition2, {
    type: "absolute",
    position: {
      left: "two",
      right: "two",
      zindex: "layer1"
    }
  }, /* @__PURE__ */React9.createElement(EGDSSpacing, {
    padding: {
      blockstart: "two"
    }
  }, /* @__PURE__ */React9.createElement("div", null, /* @__PURE__ */React9.createElement(EGDSBanner, {
    theme: "error"
  }, /* @__PURE__ */React9.createElement(EGDSLayoutFlex2, {
    alignItems: "center",
    space: "two"
  }, icon && /* @__PURE__ */React9.createElement(EGDSIcon2, {
    name: icon.id,
    id: icon.id,
    description: (_c = icon.description) != null ? _c : void 0,
    title: (_d = icon.title) != null ? _d : void 0,
    spotlight: {
      background: false,
      theme: "standard"
    }
  }), /* @__PURE__ */React9.createElement(EGDSLayoutFlex2, {
    tabIndex: -1,
    domRef: errorContainerRef,
    direction: "column",
    space: "two"
  }, /* @__PURE__ */React9.createElement(EGDSLayoutFlexItem2, null, /* @__PURE__ */React9.createElement("div", null, /* @__PURE__ */React9.createElement(EGDSHeading, {
    size: 6,
    tag: "h2"
  }, header))), /* @__PURE__ */React9.createElement(EGDSLayoutFlexItem2, null, renderTextWithLinks())))))));
};

// ../internal/src/components/footer/virtual-agent-control-chat-footer.tsx
import React27 from "react";
import { v4 as uuidv42 } from "uuid";
import { EGDSButtonSize as EGDSButtonSize3 } from "@egds/react-core/button";
import { EGDSChatTextArea } from "@egds/react-core/input";
import { EGDSLayoutFlex as EGDSLayoutFlex7, EGDSLayoutFlexItem as EGDSLayoutFlexItem4 } from "@egds/react-core/layout-flex";
import { gql as gql15 } from "@shared-ui/apollo-client-wrapper";
import { useDialog as useDialog2 } from "@shared-ui/dialog-context";
import { useProvidedState as useProvidedState8 } from "@shared-ui/provided-state-context";

// ../internal/src/helpers/date-utils.ts
import { useBexApi as useBexApi2 } from "@shared-ui/bex-api-context";

// ../internal/src/hooks/use-prefetch-localization-content.tsx
import React10 from "react";
import Omit from "lodash.omit";
import { gql, useApolloClient } from "@shared-ui/apollo-client-wrapper";
import { useProvidedState as useProvidedState6 } from "@shared-ui/provided-state-context";
var usePrefetchLocalizationContent = localiziedStringKey => {
  var _a, _b;
  const client = useApolloClient();
  const [state] = useProvidedState6();
  const localizedStringsValueSet = React10.useMemo(() => new Set(Object.values(LocalizationContentKeys)), []);
  if (localiziedStringKey === LocalizationContentKeys.ERROR) {
    const errorStrings = (_b = (_a = state.errorsState) == null ? void 0 : _a.orchestrator) == null ? void 0 : _b.offlineLocalizedErrorStrings;
    return errorStrings;
  }
  const localizationFragment = gql`
    fragment VirtualAgentControlLocalizationContentFragment on VirtualAgentControlLocalizationContent {
      attachment {
        downloadError
        downloadProcessing
        downloadSuccess
        uploadError
        uploadFileSizeError
        uploadProcessing
        uploadSuccess
        outboundAttachmentAccessibilityLabelTemplate
      }
      dialog {
        closeWebviewWindow
      }
      activityIndicator {
        activityIndicatorMessage
        humanTyping
        multipleParticipantsTyping
        virtualAgentTyping
        typingIndicatorAccessibilityAnnouncementTemplate
      }
      error {
        havingTroubleLoading
        lostConnection
        refreshOrReconnect
        refreshOrTryLater
        refreshToContinue
        sessionExpired
        somethingWentWrong
        tryToReconnect
        removeExcessCharacters
        removeSingleExcessCharacter
      }
      loading {
        downloadChatTranscript
      }
      message {
        separatorToday
        separatorYesterday
        statusNotDelivered
        statusSending
        statusRetry
        statusDelivered
        dcfUnavailableLine1
        dcfUnavailableLine2
        timestampAccessibilityLabelTemplate
        messagesLoadedNotificationAnnouncement
      }
      participant {
        typeHumanAgent
        eventJoined
        eventLeft
        typeSupplier
        typeTraveler
        typeVirtualAgent
        outboundMessageContextAccessibilityLabel
      }
    }
  `;
  const cachedLocalizedStringsData = client.readFragment({
    id: "VirtualAgentControlLocalizationContent:vac-localization",
    fragment: localizationFragment
  });
  if (!cachedLocalizedStringsData || !localizedStringsValueSet.has(localiziedStringKey)) {
    return null;
  }
  return Omit(cachedLocalizedStringsData[localiziedStringKey], ["__typename"]);
};
var use_prefetch_localization_content_default = usePrefetchLocalizationContent;

// ../internal/src/helpers/date-utils.ts
function toDate(argument) {
  const argStr = Object.prototype.toString.call(argument);
  if (argument instanceof Date || typeof argument === "object" && argStr === "[object Date]") {
    return new argument.constructor(argument.getTime());
  }
  if (typeof argument === "number" || argStr === "[object Number]") {
    return new Date(argument);
  }
  return /* @__PURE__ */new Date(NaN);
}
function addDays(date, amount) {
  const _date = toDate(date);
  if (!amount) {
    return _date;
  }
  _date.setDate(_date.getDate() + amount);
  return _date;
}
function startOfDay(date) {
  const _date = toDate(date);
  _date.setHours(0, 0, 0, 0);
  return _date;
}
function startOfDayUTC(date) {
  const _date = toDate(date);
  _date.setUTCHours(0, 0, 0, 0);
  return _date;
}
function isSameDay(dateLeft, dateRight) {
  const dateLeftStartOfDay = startOfDay(dateLeft);
  const dateRightStartOfDay = startOfDay(dateRight);
  return +dateLeftStartOfDay === +dateRightStartOfDay;
}
function subDays(date, amount) {
  return addDays(date, -amount);
}
function useLocale() {
  const {
    context: {
      locale
    }
  } = useBexApi2();
  const defaultLocale = "en-US";
  try {
    return locale.split("_").join("-");
  } catch (e) {
    return defaultLocale;
  }
}
function useSessionMarkerLabel(val) {
  const today = new Date(Date.now());
  const yesterday = subDays(today, 1);
  const locale = useLocale();
  const compareDate = new Date(val);
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  if (Number.isNaN(compareDate.valueOf())) {
    return val;
  }
  if (isSameDay(today, compareDate)) {
    return (messageContent == null ? void 0 : messageContent.separatorToday) || "Today";
  }
  if (isSameDay(yesterday, compareDate)) {
    return (messageContent == null ? void 0 : messageContent.separatorYesterday) || "Yesterday";
  }
  return compareDate.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}
function useLocaleDateTimestamp(val) {
  const locale = useLocale();
  const date = new Date(val);
  if (Number.isNaN(date.valueOf())) {
    return val;
  }
  return date.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit"
  });
}

// ../internal/src/helpers/acceptedTypes.ts
var acceptedMimeTypes = ["image/bmp", "image/gif", "image/jpeg", "image/png", "image/svg+xml", "image/tiff", "image/heic", "text/plain", "application/pdf"];
var acceptedMsOfficeMimeTypes = ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/excel", "application/x-excel", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/mspowerpoint", "application/powerpoint", "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation", "application/vnd.oasis.opendocument.text", "application/vnd.oasis.opendocument.presentation", "application/vnd.oasis.opendocument.spreadsheet"];

// ../internal/src/hooks/use-messages-state.tsx
import React11 from "react";
import { useProvidedState as useProvidedState7 } from "@shared-ui/provided-state-context";
function useMessagesState() {
  var _a;
  const [chatInstance] = useChatInstanceState();
  const [state, dispatch] = useProvidedState7();
  const conversationId = chatInstance == null ? void 0 : chatInstance.activeConversationId;
  const {
    messagesState
  } = state || {};
  const conversationMessages = (_a = messagesState == null ? void 0 : messagesState.messagesByConversationId) == null ? void 0 : _a[conversationId];
  const messagesDispatch = React11.useCallback(({
    type,
    payload: payload2
  }) => {
    const messagesAction = {
      type,
      payload: {
        conversationId,
        ...payload2
      }
    };
    dispatch(messagesAction);
  }, [dispatch, conversationId]);
  return [messagesState, conversationMessages, messagesDispatch];
}

// ../internal/src/hooks/use-send-file-message.tsx
import React12 from "react";
import { v4 as uuidv4 } from "uuid";
import { useInteractionRumTracker as useInteractionRumTracker2 } from "@shared-ui/core-real-user-monitoring/context";
import { SystemEventLevel as SystemEventLevel6 } from "@shared-ui/logger-context";
import { TIMERS, useTimers } from "@shared-ui/retail-virtual-agent-providers";

// ../internal/src/models/message-creator.tsx
function createOutboundFileMessage(attachmentLink, uniqueAttachmentId, mediaType, status, messageId, outboundMessageContextAccessibilityLabel, mappedFile) {
  return {
    __typename: "VirtualAgentControlOutboundFileMessage",
    status,
    attachmentLink,
    accessibilityLabel: null,
    attachmentMetadata: {
      ...mappedFile,
      uniqueAttachmentId,
      mediaType,
      __typename: "VirtualAgentControlAttachmentData"
    },
    metadata: {
      __typename: "VirtualAgentControlMessageMetadata",
      messageId,
      timeStamp: null,
      messageContextAccessibilityLabel: outboundMessageContextAccessibilityLabel
    }
  };
}
function createOutboundTextMessage(messageText, status, messageId, outboundMessageContextAccessibilityLabel) {
  return {
    __typename: "VirtualAgentControlOutboundTextMessage",
    primary: messageText,
    status,
    metadata: {
      __typename: "VirtualAgentControlMessageMetadata",
      messageId,
      timeStamp: new Date(Date.now()).toUTCString(),
      messageContextAccessibilityLabel: outboundMessageContextAccessibilityLabel
    }
  };
}
function createOutboundMessageElementGroup(messages) {
  return {
    __typename: "VirtualAgentControlOutboundMessageElementGroup",
    messages,
    status: null
  };
}

// ../internal/src/hooks/use-send-file-message.tsx
function useSendFileMessage() {
  const [uploadFile] = useAttachment();
  const [,, messagesDispatch] = useMessagesState();
  const [conversation] = useConversationState();
  const {
    trackEvent
  } = useAnalytics();
  const {
    trackEvent: trackRumEvent
  } = useInteractionRumTracker2();
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  const {
    outboundAttachmentAccessibilityLabelTemplate
  } = use_prefetch_localization_content_default(LocalizationContentKeys.ATTACHMENT) || {};
  const {
    outboundMessageContextAccessibilityLabel
  } = use_prefetch_localization_content_default(LocalizationContentKeys.PARTICIPANT) || {};
  const loggerV2 = useLogging(EVENTS);
  const {
    startTimer,
    getTime
  } = useTimers();
  const sendFailureAnalytics = (fileType, errorName) => {
    trackEvent(buildClickstreamAdditionalContextErrorPayload("ATTACHMENT_FILE_UPLOAD_FAILED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: fileType
      }
    }, {
      error_name: errorName
    }));
  };
  const getFileAttachmentAccessibilityLabel = file => {
    if (!outboundAttachmentAccessibilityLabelTemplate) {
      return "";
    }
    const replaceFileType = localizeTemplateLiteral(outboundAttachmentAccessibilityLabelTemplate, "filetype", file.mediaType);
    return localizeTemplateLiteral(replaceFileType, "filename", file.name);
  };
  const handleSendFileMessage = React12.useCallback((messages, formRef, sendMessage) => {
    var _a;
    startTimer(TIMERS.SEND_FILE_MESSAGE);
    messages.forEach(async ({
      file,
      uniqueAttachmentId
    }) => {
      const mappedFile = {
        name: file.name,
        mediaType: file.type,
        mediaUrl: URL.createObjectURL(file),
        size: `${file.size}`,
        uniqueAttachmentId
      };
      const attachmentLink = {
        text: `(${sizeToByteDisplay(mappedFile.size)}) ${mappedFile.name}`,
        disabled: false,
        size: "SMALL" /* SMALL */,
        action: {
          resource: {
            value: mappedFile.mediaUrl,
            __typename: "SelfReferencedURI"
          },
          accessibility: getFileAttachmentAccessibilityLabel(mappedFile),
          target: "EXTERNAL" /* EXTERNAL */,
          useRelativePath: true,
          __typename: "UILinkAction"
        },
        __typename: "EGDSStandardLink"
      };
      const umid = uuidv4();
      const outboundMessage = createOutboundFileMessage(attachmentLink, uniqueAttachmentId, mappedFile.mediaType, messageContent == null ? void 0 : messageContent.statusSending, umid, outboundMessageContextAccessibilityLabel, mappedFile);
      const outboundMessageGroup = createOutboundMessageElementGroup([outboundMessage]);
      messagesDispatch({
        type: "SET_QUICK_REPLIES" /* SET_QUICK_REPLIES */,
        payload: {
          message: [],
          messageId: null,
          analytics: null
        }
      });
      messagesDispatch({
        type: "UPLOAD_ATTACHMENT" /* UPLOAD_ATTACHMENT */,
        payload: {
          uniqueAttachmentId,
          message: outboundMessageGroup
        }
      });
      if (file.size > maxFileSizeinInteger) {
        const updatedMessage = {
          status: messageContent == null ? void 0 : messageContent.statusNotDelivered
        };
        sendFailureAnalytics(mappedFile.mediaType, "size_limit_exceeded");
        messagesDispatch({
          type: "UPLOAD_ATTACHMENT_FAILURE" /* UPLOAD_ATTACHMENT_FAILURE */,
          payload: {
            uniqueAttachmentId,
            loading: false,
            hasError: true,
            isSuccess: false
          }
        });
        messagesDispatch({
          type: "UPDATE_OUTBOUND_MESSAGE_ELEMENT" /* UPDATE_OUTBOUND_MESSAGE_ELEMENT */,
          payload: {
            umid,
            outboundMessageData: updatedMessage
          }
        });
        loggerV2("RUNTIME", {
          eventName: "vac.runtime" /* RUNTIME */,
          eventlevel: SystemEventLevel6.ERROR,
          operationName: "VAC_FileSizeExceedingLimit" /* VAC_FILE_SIZE_EXCEEDING_LIMIT */,
          logSource: PREFIX
        });
      } else {
        try {
          const {
            file_data: {
              file_metadata: {
                id,
                media_url,
                content_type
              }
            }
          } = await uploadFile(file);
          if (id) {
            trackEvent(buildClickstreamAdditionalContextPayload("ATTACHMENT_FILE_UPLOAD_SUCCEEDED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
              user_interface: {
                component_element_name: content_type
              }
            }));
            messagesDispatch({
              type: "UPLOAD_ATTACHMENT_SUCCESS" /* UPLOAD_ATTACHMENT_SUCCESS */,
              payload: {
                uniqueAttachmentId,
                loading: false,
                isSuccess: true,
                attachmentMetadata: {
                  ...mappedFile,
                  mediaUrl: media_url,
                  uniqueAttachmentId: id
                }
              }
            });
          }
          const timestamp = getTime(TIMERS.SEND_FILE_MESSAGE, true);
          loggerV2("API_SUCCESS", {
            eventName: "vac.api" /* API */,
            eventlevel: SystemEventLevel6.INFO,
            operationName: "VacUploadFile" /* VAC_UPLOAD_FILE */,
            logSource: PREFIX,
            loadingDuration: timestamp
          });
          trackRumEvent("action", {
            action: `${"VacUploadFile" /* VAC_UPLOAD_FILE */}-success`,
            attributes: {
              loadingDuration: timestamp
            }
          });
          sendMessage == null ? void 0 : sendMessage({
            messageType: "FILE" /* FILE */,
            attachment: {
              ...mappedFile,
              mediaUrl: media_url,
              uniqueAttachmentId: id
            },
            umid
          });
        } catch (e) {
          sendFailureAnalytics(mappedFile.mediaType, "network_error");
          loggerV2("RUNTIME", {
            eventName: "vac.runtime" /* RUNTIME */,
            eventlevel: SystemEventLevel6.ERROR,
            operationName: "VAC_DownloadFailureRuntime" /* VAC_DOWNLOAD_FAILURE_RUNTIME */,
            logSource: PREFIX
          });
          messagesDispatch({
            type: "UPLOAD_ATTACHMENT_FAILURE" /* UPLOAD_ATTACHMENT_FAILURE */,
            payload: {
              uniqueAttachmentId,
              loading: false,
              hasError: true
            }
          });
          const timestamp = getTime(TIMERS.SEND_FILE_MESSAGE, true);
          loggerV2("API_ERROR", {
            eventName: "vac.api" /* API */,
            eventlevel: SystemEventLevel6.ERROR,
            operationName: "VacUploadFile" /* VAC_UPLOAD_FILE */,
            logSource: PREFIX,
            loadingDuration: timestamp
          });
          trackRumEvent("action", {
            action: `${"VacUploadFile" /* VAC_UPLOAD_FILE */}-failure`,
            attributes: {
              loadingDuration: timestamp
            }
          });
        }
      }
    });
    (_a = formRef.current) == null ? void 0 : _a.reset();
  }, [messagesDispatch, uploadFile]);
  return [handleSendFileMessage, loggerV2];
}

// ../internal/src/components/common/avatar/avatar-group.tsx
import React13 from "react";
import { EGDSAvatarGroup } from "@egds/react-core/avatar";
import { gql as gql2 } from "@shared-ui/apollo-client-wrapper";
var fragment = gql2`
  fragment AvatarGroupFragment on AvatarGroup {
    items {
      ...AvatarFragment
    }
  }
  fragment AvatarFragment on Avatar {
    image {
      url
      description
    }
    text
    icon {
      id
    }
  }
`;
var AvatarGroup = ({
  data,
  size = "medium"
}) => {
  const {
    items
  } = data;
  if (items.length === 0) {
    return null;
  }
  const getProps = avatar => {
    const {
      image,
      icon,
      text
    } = avatar;
    if (image) {
      return {
        image: {
          alt: image.description,
          src: image.url
        }
      };
    }
    if (icon == null ? void 0 : icon.id) {
      return {
        icon: icon.id
      };
    }
    if (text) {
      return {
        text
      };
    }
    return {};
  };
  return /* @__PURE__ */React13.createElement(EGDSAvatarGroup, {
    items: items.map(getProps),
    size
  });
};
AvatarGroup.fragment = fragment;
AvatarGroup.displayName = "AvatarGroup";

// ../internal/src/components/common/egds-button/egds-button.tsx
import React14 from "react";
import { EGDSButtonSize as EGDSButtonSize2, EGDSPrimaryButton, EGDSSecondaryButton, EGDSTertiaryButton as EGDSTertiaryButton2 } from "@egds/react-core/button";
import { EGDSIcon as EGDSIcon3 } from "@egds/react-core/icons";
import { EGDSSpacing as EGDSSpacing2 } from "@egds/react-core/spacing";
import { gql as gql3 } from "@shared-ui/apollo-client-wrapper";
var fragment2 = gql3`
  fragment EGDSButtonFragment on EGDSButton {
    accessibility
    disabled
    primary
    icon {
      description
      id
      title
    }
  }
`;
var VirtualAgentEGDSButton = ({
  data,
  onClick,
  fullWidth,
  target,
  size,
  href
}) => {
  const {
    disabled,
    primary,
    icon,
    accessibility
  } = data;
  const buttonContent = React14.useMemo(() => {
    var _a, _b, _c, _d;
    if (icon && primary) {
      return /* @__PURE__ */React14.createElement(React14.Fragment, null, /* @__PURE__ */React14.createElement(EGDSSpacing2, {
        margin: {
          inlineend: "two"
        }
      }, /* @__PURE__ */React14.createElement(EGDSIcon3, {
        name: icon.id,
        id: icon.id,
        title: (_a = icon.title) != null ? _a : void 0,
        description: (_b = icon.description) != null ? _b : void 0
      })), primary);
    }
    if (icon) {
      return /* @__PURE__ */React14.createElement(EGDSIcon3, {
        name: icon.id,
        id: icon.id,
        title: (_c = icon.title) != null ? _c : void 0,
        description: (_d = icon.description) != null ? _d : void 0
      });
    }
    if (primary) {
      return /* @__PURE__ */React14.createElement(React14.Fragment, null, primary);
    }
    return null;
  }, [icon, primary]);
  if (!buttonContent) {
    return null;
  }
  const buttonProps = {
    "aria-label": accessibility != null ? accessibility : void 0,
    disabled,
    onClick,
    size: size != null ? size : EGDSButtonSize2.SMALL,
    isFullWidth: fullWidth != null ? fullWidth : false,
    href,
    tag: href ? "a" : "button",
    target
  };
  switch (data.__typename) {
    case "UIPrimaryButton":
      return /* @__PURE__ */React14.createElement(EGDSPrimaryButton, {
        ...buttonProps
      }, buttonContent);
    case "UISecondaryButton":
      return /* @__PURE__ */React14.createElement(EGDSSecondaryButton, {
        ...buttonProps
      }, buttonContent);
    case "UITertiaryButton":
      return /* @__PURE__ */React14.createElement(EGDSTertiaryButton2, {
        ...buttonProps
      }, buttonContent);
    default:
      return null;
  }
};
VirtualAgentEGDSButton.fragment = fragment2;
VirtualAgentEGDSButton.displayName = "VirtualAgentEGDSButton";

// ../internal/src/components/common/egds-standard-link/egds-standard-link.tsx
import React18 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex3 } from "@egds/react-core/layout-flex";
import { EGDSLink as EGDSLink2 } from "@egds/react-core/link";
import { gql as gql7 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/ui-graphic/icon/icon.tsx
import React15 from "react";
import { EGDSIcon as EGDSIcon4, EGDSIconSize as EGDSIconSize2 } from "@egds/react-core/icons";
import { gql as gql4 } from "@shared-ui/apollo-client-wrapper";
var fragment3 = gql4`
  fragment IconFragment on Icon {
    id
    description
    size
    theme
    title
  }
`;
var Icon = ({
  data
}) => {
  const {
    id,
    description,
    size,
    theme,
    title
  } = data;
  const props = {
    name: id,
    id,
    description,
    size: size ? EGDSIconSize2[size] : EGDSIconSize2.SMALL,
    theme: theme != null ? theme : "emphasis" /* emphasis */,
    title: title != null ? title : void 0
  };
  return /* @__PURE__ */React15.createElement(EGDSIcon4, {
    ...props
  });
};
Icon.fragment = fragment3;
Icon.displayName = "Icon";

// ../internal/src/components/common/ui-graphic/mark/mark.tsx
import React16 from "react";
import { EGDSMark, EGDSMarkSize } from "@egds/react-core/mark";
import { gql as gql5 } from "@shared-ui/apollo-client-wrapper";
var fragment4 = gql5`
  fragment MarkFragment on Mark {
    description
    id
    markSize: size
    url {
      value
      relativePath
    }
  }
`;
var Mark = ({
  data
}) => {
  var _a;
  const {
    description,
    id,
    markSize,
    url
  } = data;
  const props = {
    name: id,
    description,
    id,
    size: markSize ? EGDSMarkSize[markSize] : EGDSMarkSize.SMALL,
    url: (_a = url == null ? void 0 : url.value) != null ? _a : void 0
  };
  return /* @__PURE__ */React16.createElement(EGDSMark, {
    ...props
  });
};
Mark.fragment = fragment4;
Mark.displayName = "Mark";

// ../internal/src/components/common/ui-graphic/ui-graphic.tsx
import React17 from "react";
import { gql as gql6 } from "@shared-ui/apollo-client-wrapper";
var fragment5 = gql6`
  fragment UIGraphicFragment on UIGraphic {
    ...IconFragment
    ...MarkFragment
  }
  ${Icon.fragment}
  ${Mark.fragment}
`;
var registry = {
  Icon,
  Mark
};
var UIGraphic = ({
  data
}) => {
  const {
    __typename
  } = data;
  const Component = registry[__typename];
  if (!Component) {
    return null;
  }
  return /* @__PURE__ */React17.createElement(Component, {
    data
  });
};
UIGraphic.fragment = fragment5;
UIGraphic.displayName = "UIGraphic";

// ../internal/src/components/common/egds-standard-link/egds-standard-link.tsx
var fragment6 = gql7`
  fragment EGDSStandardLinkFragment on EGDSStandardLink {
    text
    disabled
    action {
      resource {
        value
      }
    }
    icon {
      ...IconFragment
    }
    iconPosition
  }
  ${Icon.fragment}
`;
var EGDSStandardLink = ({
  data,
  target = "_blank",
  onClick,
  theme = "default",
  rel = ""
}) => {
  const {
    text,
    disabled,
    icon,
    iconPosition,
    action: {
      resource
    }
  } = data;
  const handleClick = React18.useCallback(event => {
    if (!disabled) {
      onClick == null ? void 0 : onClick(event);
    }
  }, [onClick, disabled]);
  return /* @__PURE__ */React18.createElement(EGDSLink2, {
    disabled,
    onClick: handleClick,
    theme
  }, /* @__PURE__ */React18.createElement("a", {
    href: resource.value,
    target,
    rel
  }, /* @__PURE__ */React18.createElement(EGDSLayoutFlex3, {
    direction: iconPosition === "TRAILING" /* TRAILING */ ? "row-reverse" : "row",
    alignItems: "center",
    space: "one"
  }, icon && /* @__PURE__ */React18.createElement(Icon, {
    data: icon
  }), text)));
};
EGDSStandardLink.fragment = fragment6;
EGDSStandardLink.displayName = "EGDSStandardLink";

// ../internal/src/components/common/egds-spannable-text/egds-spannable-text.tsx
import React25 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex5 } from "@egds/react-core/layout-flex";
import { EGDSText as EGDSText6 } from "@egds/react-core/text";
import { gql as gql14 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/egds-spannable-text/egds-spannable-inline-content.tsx
import React24 from "react";
import { EGDSSpacing as EGDSSpacing3 } from "@egds/react-core/spacing";
import { gql as gql13 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/egds-graphic-text/egds-graphic-text.tsx
import React19 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex4 } from "@egds/react-core/layout-flex";
import { EGDSText as EGDSText3 } from "@egds/react-core/text";
import { gql as gql8 } from "@shared-ui/apollo-client-wrapper";
var fragment7 = gql8`
  fragment EGDSGraphicTextFragment on EGDSGraphicText {
    graphic {
      ...UIGraphicFragment
    }
    text
    trailingGraphics {
      ...UIGraphicFragment
    }
  }
  ${UIGraphic.fragment}
`;
var EGDSGraphicText = ({
  data
}) => {
  const {
    graphic,
    text,
    trailingGraphics
  } = data;
  return /* @__PURE__ */React19.createElement(EGDSLayoutFlex4, {
    alignItems: "center",
    space: "one"
  }, /* @__PURE__ */React19.createElement(UIGraphic, {
    data: graphic
  }), /* @__PURE__ */React19.createElement(EGDSText3, null, text), /* @__PURE__ */React19.createElement("div", null, (trailingGraphics == null ? void 0 : trailingGraphics.length) ? trailingGraphics.map((element, i) => /* @__PURE__ */React19.createElement(UIGraphic, {
    key: `trailing-graphic-${i}`,
    data: element
  })) : null));
};
EGDSGraphicText.fragment = fragment7;
EGDSGraphicText.displayName = "EGDSGraphicText";

// ../internal/src/components/common/egds-heading/egds-heading.tsx
import React20 from "react";
import { EGDSHeading as EGDSHeading2 } from "@egds/react-core/text";
import { gql as gql9 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/egds-heading/__private/heading-map.ts
var headingTagMapper = {
  ["H1" /* H1 */]: "h1",
  ["H2" /* H2 */]: "h2",
  ["H3" /* H3 */]: "h3",
  ["H4" /* H4 */]: "h4",
  ["H5" /* H5 */]: "h5",
  ["H6" /* H6 */]: "h6",
  ["H7" /* H7 */]: "h6",
  ["H8" /* H8 */]: "h6"
};

// ../internal/src/components/common/egds-heading/egds-heading.tsx
var fragment8 = gql9`
  fragment EGDSHeadingFragment on EGDSHeading {
    text
    headingType
  }
`;
var VirtualAgentEGDSHeading = ({
  data
}) => {
  const {
    headingType,
    text
  } = data;
  const headingTag = headingType ? headingTagMapper[headingType] : void 0;
  return /* @__PURE__ */React20.createElement(EGDSHeading2, {
    tag: headingTag
  }, text);
};
VirtualAgentEGDSHeading.fragment = fragment8;
VirtualAgentEGDSHeading.displayName = "VirtualAgentEGDSHeading";

// ../internal/src/components/common/egds-inline-link/egds-inline-link.tsx
import React21 from "react";
import { EGDSLink as EGDSLink3 } from "@egds/react-core/link";
import { gql as gql10 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/__private/link-map.ts
import { EGDSIconSize as EGDSIconSize3 } from "@egds/react-core/icons";
var linkSizeMapper = {
  ["SMALL" /* SMALL */]: "small",
  ["MEDIUM" /* MEDIUM */]: "medium",
  ["LARGE" /* LARGE */]: "large"
};
var iconSizeMapper = {
  [EGDSIconSize3.SMALL]: "small",
  [EGDSIconSize3.MEDIUM]: "medium",
  [EGDSIconSize3.LARGE]: "large",
  [EGDSIconSize3.EXTRA_SMALL]: "xsmall",
  [EGDSIconSize3.XLARGE]: "xlarge"
};
var UILinkTargetMap = {
  ["EXTERNAL" /* EXTERNAL */]: "_blank",
  ["INTERNAL" /* INTERNAL */]: "_self"
};

// ../internal/src/components/common/egds-inline-link/egds-inline-link.tsx
var fragment9 = gql10`
  fragment EGDSInlineLinkFragment on EGDSInlineLink {
    text
    size
    action {
      resource {
        value
      }
      accessibility
      useRelativePath
      target
    }
    disabled
  }
`;
var EGDSInlineLink = ({
  data,
  onClick
}) => {
  const {
    text,
    disabled,
    action: {
      resource: {
        value
      },
      accessibility,
      useRelativePath,
      target
    },
    size
  } = data;
  const handleClick = React21.useCallback(event => {
    if (!disabled) {
      onClick == null ? void 0 : onClick(event);
    }
  }, [onClick, disabled]);
  return /* @__PURE__ */React21.createElement(EGDSLink3, {
    disabled,
    onClick: handleClick,
    inline: true,
    size: linkSizeMapper[size]
  }, /* @__PURE__ */React21.createElement("a", {
    href: value,
    target
  }, text));
};
EGDSInlineLink.fragment = fragment9;
EGDSInlineLink.displayName = "EGDSStandardLink";

// ../internal/src/components/common/egds-plain-text/egds-plain-text.tsx
import React22 from "react";
import { EGDSText as EGDSText4 } from "@egds/react-core/text";
import { gql as gql11 } from "@shared-ui/apollo-client-wrapper";
var fragment10 = gql11`
  fragment EGDSPlainTextFragment on EGDSPlainText {
    text
  }
`;
var EGDSPlainText = ({
  inline,
  data
}) => {
  const {
    text
  } = data;
  return /* @__PURE__ */React22.createElement(EGDSText4, {
    inline
  }, text);
};
EGDSPlainText.fragment = fragment10;
EGDSPlainText.displayName = "EGDSPlainText";

// ../internal/src/components/common/egds-spannable-text/egds-stylized-text.tsx
import React23 from "react";
import { EGDSText as EGDSText5 } from "@egds/react-core/text";
import { gql as gql12 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/egds-spannable-text/__private/text-theme.ts
var textThemeMapper = {
  ["LOYALTY" /* LOYALTY */]: "loyalty",
  ["INVERSE" /* INVERSE */]: "inverse",
  ["NEGATIVE" /* NEGATIVE */]: "negative",
  ["POSITIVE" /* POSITIVE */]: "positive",
  ["PRIMARY" /* PRIMARY */]: "emphasis",
  ["SECONDARY" /* SECONDARY */]: "default",
  ["EMPHASIS" /* EMPHASIS */]: "emphasis"
};

// ../internal/src/components/common/egds-spannable-text/__private/text-weight.ts
var textWeightMapper = {
  ["BOLD" /* BOLD */]: "bold",
  ["MEDIUM" /* MEDIUM */]: "medium",
  ["REGULAR" /* REGULAR */]: "regular"
};

// ../internal/src/components/common/egds-spannable-text/egds-stylized-text.tsx
var fragment11 = gql12`
  fragment EGDSStylizedTextFragment on EGDSStylizedText {
    text
    theme
    weight
  }
`;
var EGDSStylizedText = ({
  className,
  inline = false,
  data
}) => {
  const {
    text,
    theme,
    weight
  } = data;
  const themeProps = theme ? textThemeMapper[theme] : void 0;
  const weightProps = weight ? textWeightMapper[weight] : void 0;
  return /* @__PURE__ */React23.createElement(EGDSText5, {
    className,
    inline,
    theme: themeProps,
    weight: weightProps
  }, text);
};
EGDSStylizedText.fragment = fragment11;
EGDSStylizedText.displayName = "EGDSStylizedText";

// ../internal/src/components/common/egds-spannable-text/egds-spannable-inline-content.tsx
var fragment12 = gql13`
  fragment EGDSSpannableInlineContentFragment on EGDSSpannableTextItem {
    ... on EGDSStylizedText {
      ...EGDSStylizedTextFragment
    }
    ... on EGDSGraphicText {
      ...EGDSGraphicTextFragment
    }
    ... on EGDSStandardLink {
      ...EGDSStandardLinkFragment
    }
    ... on EGDSHeading {
      ...EGDSHeadingFragment
    }
    ... on EGDSPlainText {
      ...EGDSPlainTextFragment
    }
    ... on EGDSInlineLink {
      ...EGDSInlineLinkFragment
    }
  }
  ${EGDSStylizedText.fragment}
  ${EGDSGraphicText.fragment}
  ${EGDSStandardLink.fragment}
  ${VirtualAgentEGDSHeading.fragment}
  ${EGDSPlainText.fragment}
  ${EGDSInlineLink.fragment}
`;
var registry2 = {
  EGDSStylizedText,
  EGDSGraphicText,
  EGDSStandardLink,
  EGDSHeading: VirtualAgentEGDSHeading,
  EGDSPlainText,
  EGDSInlineLink
};
var EGDSSpannableInlineContent = ({
  data,
  ...props
}) => {
  const Component = registry2[data.__typename];
  if (!Component) {
    return null;
  }
  return /* @__PURE__ */React24.createElement(EGDSSpacing3, {
    ...props,
    margin: {
      inlineend: "one"
    }
  }, /* @__PURE__ */React24.createElement(Component, {
    data,
    inline: true,
    onClick: props == null ? void 0 : props.onClick
  }));
};
EGDSSpannableInlineContent.fragment = fragment12;
EGDSSpannableInlineContent.displayName = "EGDSSpannableInlineContent";

// ../internal/src/components/common/egds-spannable-text/egds-spannable-text.tsx
var fragment13 = gql14`
  fragment EGDSSpannableTextFragment on EGDSSpannableText {
    text
    inlineContent {
      ...EGDSSpannableInlineContentFragment
    }
  }
  ${EGDSSpannableInlineContent.fragment}
`;
var EGDSSpannableText = ({
  data,
  onClick
}) => {
  const {
    inlineContent,
    text
  } = data;
  if (!inlineContent.length && !text) {
    return null;
  }
  return /* @__PURE__ */React25.createElement("div", null, text && /* @__PURE__ */React25.createElement(EGDSText6, null, text), /* @__PURE__ */React25.createElement(EGDSLayoutFlex5, {
    alignItems: "center",
    space: "one"
  }, inlineContent.map((item, index) => /* @__PURE__ */React25.createElement(EGDSSpannableInlineContent, {
    data: item,
    key: `inline-item-${index}`,
    onClick
  }))));
};
EGDSSpannableText.fragment = fragment13;
EGDSSpannableText.displayName = "EGDSSpannableText";

// ../internal/src/components/footer/virtual-agent-debug-dialog.tsx
import React26 from "react";
import { EGDSSecondaryButton as EGDSSecondaryButton2 } from "@egds/react-core/button";
import { EGDSDialog, EGDSDialogContent } from "@egds/react-core/dialog";
import { EGDSLayoutFlex as EGDSLayoutFlex6, EGDSLayoutFlexItem as EGDSLayoutFlexItem3 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing4 } from "@egds/react-core/spacing";
import { EGDSHeading as EGDSHeading3, EGDSText as EGDSText7 } from "@egds/react-core/text";
import { useBexApi as useBexApi3 } from "@shared-ui/bex-api-context";
import { useDialog } from "@shared-ui/dialog-context";
var DebugDialog = () => {
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog("va-debug-dialog");
  const [chatInstance] = useChatInstanceState();
  const [,, conversationContext] = useConversationState();
  const showToast = useOperation("SHOW_TOAST" /* SHOW_TOAST */);
  const {
    context
  } = useBexApi3();
  const {
    channelOriginId,
    partnerGuid
  } = (chatInstance == null ? void 0 : chatInstance.chatConfig) || {};
  const {
    conversationId,
    participantId,
    conversationType
  } = conversationContext || {};
  const {
    duaid
  } = (context == null ? void 0 : context.identity) || {};
  const copyToClipboard = () => {
    if (navigator.clipboard) {
      const debugPayload = {
        channelOriginId,
        conversationId,
        conversationType,
        partnerGuid,
        participantId,
        duaid
      };
      navigator.clipboard.writeText(JSON.stringify(debugPayload)).then(() => {
        showToast == null ? void 0 : showToast("Payload copied to clipboard!");
      });
    }
  };
  const closeDialog = () => {
    dialogActions.closeDialog();
  };
  return /* @__PURE__ */React26.createElement(DialogComponent, null, /* @__PURE__ */React26.createElement(EGDSDialog, {
    dialogShow: isDialogOpen
  }, /* @__PURE__ */React26.createElement(EGDSDialogContent, null, /* @__PURE__ */React26.createElement(EGDSHeading3, {
    "data-test-id": "debug-info",
    size: 5
  }, "Debug Info"), /* @__PURE__ */React26.createElement(EGDSSpacing4, {
    padding: {
      block: "two"
    }
  }, /* @__PURE__ */React26.createElement("div", null, /* @__PURE__ */React26.createElement(EGDSText7, {
    "data-test-id": "channel-origin-id"
  }, "Channel Origin ID:"), /* @__PURE__ */React26.createElement(EGDSText7, null, channelOriginId))), /* @__PURE__ */React26.createElement(EGDSSpacing4, {
    padding: {
      block: "two"
    }
  }, /* @__PURE__ */React26.createElement("div", null, /* @__PURE__ */React26.createElement(EGDSText7, {
    "data-test-id": "conversation-id"
  }, "Conversation ID:"), /* @__PURE__ */React26.createElement(EGDSText7, null, conversationId))), /* @__PURE__ */React26.createElement(EGDSSpacing4, {
    padding: {
      block: "two"
    }
  }, /* @__PURE__ */React26.createElement("div", null, /* @__PURE__ */React26.createElement(EGDSText7, {
    "data-test-id": "conversation-type"
  }, "Conversation Type:"), /* @__PURE__ */React26.createElement(EGDSText7, null, conversationType))), /* @__PURE__ */React26.createElement(EGDSSpacing4, {
    padding: {
      block: "two"
    }
  }, /* @__PURE__ */React26.createElement("div", null, /* @__PURE__ */React26.createElement(EGDSText7, {
    "data-test-id": "partner-guid"
  }, "Partner GUID:"), /* @__PURE__ */React26.createElement(EGDSText7, null, partnerGuid))), /* @__PURE__ */React26.createElement(EGDSSpacing4, {
    padding: {
      block: "two"
    }
  }, /* @__PURE__ */React26.createElement("div", null, /* @__PURE__ */React26.createElement(EGDSText7, {
    "data-test-id": "participant-id"
  }, "Participant ID:"), /* @__PURE__ */React26.createElement(EGDSText7, null, participantId))), /* @__PURE__ */React26.createElement(EGDSSpacing4, {
    padding: {
      block: "two"
    }
  }, /* @__PURE__ */React26.createElement("div", null, /* @__PURE__ */React26.createElement(EGDSText7, {
    "data-test-id": "duaid"
  }, "DUAID:"), /* @__PURE__ */React26.createElement(EGDSText7, null, duaid))), /* @__PURE__ */React26.createElement(EGDSLayoutFlex6, {
    space: "two"
  }, /* @__PURE__ */React26.createElement(EGDSLayoutFlexItem3, {
    grow: 1
  }, /* @__PURE__ */React26.createElement(EGDSSecondaryButton2, {
    onClick: copyToClipboard
  }, "Copy")), /* @__PURE__ */React26.createElement(EGDSLayoutFlexItem3, {
    grow: 1
  }, /* @__PURE__ */React26.createElement(EGDSSecondaryButton2, {
    onClick: closeDialog
  }, "Done"))))));
};

// ../internal/src/components/footer/virtual-agent-control-chat-footer.tsx
import { validateMessage as creditCardMasking } from "@gco-vnext/data-protection-library/distribution/src/manager/data-protection-manager";
var fragment14 = gql15`
  fragment VirtualAgentControlChatFooterFragment on VirtualAgentControlChatFooter {
    send {
      ...EGDSButtonFragment
    }
    input {
      ... on EGDSTypeaheadInputField {
        label
        placeholder
        readOnly
      }
      ... on EGDSTextInputField {
        label
        placeholder
        readOnly
        validations {
          ... on EGDSMaxLengthInputValidation {
            maxLength
          }
        }
      }
    }
    attach {
      ...EGDSButtonFragment
      accessibility
    }
    disabledInputPlaceholder
    landmarkFooterAccessibilityLabel
  }
  ${VirtualAgentEGDSButton.fragment}
`;
var VirtualAgentControlChatFooter = ({
  data,
  onSendCallback,
  onAttachCallback
}) => {
  var _a, _b;
  const [state, dispatch] = useProvidedState8();
  const [conversation] = useConversationState();
  const [chatInstance] = useChatInstanceState();
  const [, conversationMessages, messagesDispatch] = useMessagesState();
  const [, dialogActions] = useDialog2("va-debug-dialog");
  const {
    send,
    input,
    attach,
    disabledInputPlaceholder
  } = data;
  const [userInput, setUserInput] = React27.useState("");
  const [isTyping, setIsTyping] = React27.useState(false);
  const [chatInputLimit, setChatInputLimit] = React27.useState(0);
  const charLimitExists = React27.useRef(false);
  const [timer, setTimer] = React27.useState(null);
  const sendMessage = useOperation("SEND_MESSAGE" /* SEND_MESSAGE */);
  const sendEvent = useOperation("TYPING_EVENT" /* TYPING_EVENT */);
  const fileRef = React27.useRef(null);
  const formRef = React27.useRef(null);
  const {
    trackEvent
  } = useAnalytics();
  const [handleSendFileMessage] = useSendFileMessage();
  const errorLocalizations = use_prefetch_localization_content_default(LocalizationContentKeys.ERROR);
  const {
    chatFooterState: {
      chatInputEnable = false
    } = {}
  } = conversation || {};
  const trimmedUserInput = userInput.trim();
  React27.useEffect(() => {
    var _a2;
    if (input.__typename === "EGDSTextInputField" && ((_a2 = input.validations) == null ? void 0 : _a2.length)) {
      const maxLengthValidation = input.validations.find(validation => validation.__typename === "EGDSMaxLengthInputValidation");
      setChatInputLimit((maxLengthValidation == null ? void 0 : maxLengthValidation.maxLength) || 0);
      charLimitExists.current = true;
    }
  }, []);
  const {
    wordLimitInfoProps,
    showTextOverflowError
  } = validateInputString(trimmedUserInput, chatInputLimit, charLimitExists.current, errorLocalizations);
  const showChatInputLength = trimmedUserInput.length >= chatInputLimit - excessCharactersThreshold && charLimitExists;
  if (!send.icon && !send.primary) {
    return null;
  }
  const shouldShowDebugDialog = chatInputValue => {
    var _a2;
    return ((_a2 = chatInputValue.trim()) == null ? void 0 : _a2.toUpperCase()) === DebugIdToken.toUpperCase();
  };
  const sendEventData = start => {
    return {
      eventType: "TYPING_INDICATOR",
      content: {
        typingIndicatorEventContent: {
          start
        }
      }
    };
  };
  const checkIfNewSeperatorMessageIsRequired = () => {
    const currentMessages = (conversationMessages == null ? void 0 : conversationMessages.messages) || [];
    const lastReceivedMessageTimeStamp = conversationMessages == null ? void 0 : conversationMessages.lastReceivedMessageTimeStamp;
    if (currentMessages.length && lastReceivedMessageTimeStamp) {
      const timeStamp = new Date(conversationMessages.lastReceivedMessageTimeStamp);
      const today = new Date(Date.now());
      return !isSameDay(timeStamp, today);
    }
  };
  const handleAttach = () => {
    var _a2;
    (_a2 = fileRef.current) == null ? void 0 : _a2.click();
    trackEvent(buildClickstreamEventPaylaod("VIRTUAL_AGENT_ATTACHMENT_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type));
    onAttachCallback == null ? void 0 : onAttachCallback();
  };
  const handleSend = () => {
    if (checkIfNewSeperatorMessageIsRequired()) {
      const todayTimeStamp = startOfDayUTC(/* @__PURE__ */new Date()).toISOString();
      messagesDispatch({
        type: "ADD_SEPERATOR_MESSAGE" /* ADD_SEPERATOR_MESSAGE */,
        payload: {
          primary: todayTimeStamp,
          timeStamp: todayTimeStamp
        }
      });
    }
    if (trimmedUserInput !== "") {
      if (shouldShowDebugDialog(trimmedUserInput)) {
        dialogActions.openDialog();
        setUserInput("");
        return;
      }
      sendMessage == null ? void 0 : sendMessage({
        message: creditCardMasking(trimmedUserInput)
      });
      onSendCallback == null ? void 0 : onSendCallback();
    }
    setUserInput("");
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
    setTimeout(() => {
      setIsTyping(false);
      sendEvent == null ? void 0 : sendEvent(sendEventData(false));
    }, 100);
  };
  const handleChange = ({
    target
  }) => {
    setUserInput(target.value);
    if (!isTyping) {
      sendEvent == null ? void 0 : sendEvent(sendEventData(true));
    }
    setIsTyping(true);
    if (timer) {
      clearTimeout(timer);
    }
    const reset = setTimeout(() => {
      setIsTyping(false);
      sendEvent == null ? void 0 : sendEvent(sendEventData(false));
    }, 3e3);
    setTimer(reset);
  };
  const handleKeyDown = event => {
    if (!event.shiftKey && event.code === "Enter" && !showTextOverflowError) {
      event.preventDefault();
      event.stopPropagation();
      handleSend();
    }
  };
  const handleFileChange = event => {
    const {
      target: {
        files = new FileList()
      }
    } = event;
    event.preventDefault();
    const messages = Array.from(files).map(file => ({
      file,
      uniqueAttachmentId: uuidv42()
    }));
    handleSendFileMessage(messages, formRef, sendMessage);
  };
  return /* @__PURE__ */React27.createElement(EGDSLayoutFlex7, {
    space: "two"
  }, attach && /* @__PURE__ */React27.createElement(EGDSLayoutFlexItem4, {
    grow: 0,
    alignSelf: "start"
  }, /* @__PURE__ */React27.createElement("form", {
    ref: formRef
  }, /* @__PURE__ */React27.createElement(VirtualAgentEGDSButton, {
    data: {
      ...attach,
      disabled: !chatInputEnable
    },
    onClick: handleAttach,
    size: EGDSButtonSize3.LARGE
  }), /* @__PURE__ */React27.createElement("input", {
    tabIndex: -1,
    type: "file",
    "data-stid": "file-upload",
    id: "file-upload",
    ref: fileRef,
    "aria-hidden": "true",
    hidden: true,
    multiple: true,
    accept: [...acceptedMimeTypes, ...acceptedMsOfficeMimeTypes].join(","),
    onChange: handleFileChange,
    "aria-label": attach.accessibility || "",
    disabled: !chatInputEnable
  }))), /* @__PURE__ */React27.createElement(EGDSLayoutFlexItem4, {
    grow: 1
  }, /* @__PURE__ */React27.createElement("div", null, /* @__PURE__ */React27.createElement(EGDSChatTextArea, {
    label: (_a = input.label) != null ? _a : void 0,
    placeholder: chatInputEnable ? (_b = input.placeholder) != null ? _b : void 0 : disabledInputPlaceholder,
    readOnly: input.readOnly,
    minRows: 1,
    maxRows: 2,
    value: userInput,
    id: "vac-input-field",
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    disabled: !chatInputEnable,
    ...(showChatInputLength && wordLimitInfoProps)
  }))), /* @__PURE__ */React27.createElement(EGDSLayoutFlexItem4, {
    alignSelf: "start",
    grow: 0
  }, /* @__PURE__ */React27.createElement("div", null, /* @__PURE__ */React27.createElement(VirtualAgentEGDSButton, {
    data: {
      ...send,
      disabled: !chatInputEnable || !trimmedUserInput.length || showTextOverflowError
    },
    onClick: handleSend,
    size: EGDSButtonSize3.LARGE
  }))), /* @__PURE__ */React27.createElement(DebugDialog, null));
};
VirtualAgentControlChatFooter.fragment = fragment14;
VirtualAgentControlChatFooter.displayName = "VirtualAgentControlChatFooter";

// ../internal/src/components/header/virtual-agent-control-chat-header.tsx
import React30, { useCallback as useCallback3, useEffect as useEffect3, useState as useState2 } from "react";
import { EGDSButtonSize as EGDSButtonSize5 } from "@egds/react-core/button";
import { EGDSLayoutFlex as EGDSLayoutFlex8 } from "@egds/react-core/layout-flex";
import { EGDSLayoutPosition as EGDSLayoutPosition4 } from "@egds/react-core/layout-position";
import { EGDSSpacing as EGDSSpacing5 } from "@egds/react-core/spacing";
import { EGDSHeading as EGDSHeading4 } from "@egds/react-core/text";
import { gql as gql17 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/hooks/use-participant-state.tsx
import React28 from "react";
import { useProvidedState as useProvidedState9 } from "@shared-ui/provided-state-context";
function useParticipantState() {
  const [state] = useProvidedState9();
  const [chatInstance] = useChatInstanceState();
  const participantState = state == null ? void 0 : state.participantState;
  const activeConversationId = chatInstance == null ? void 0 : chatInstance.activeConversationId;
  const conversationParticipantState = React28.useMemo(() => {
    var _a;
    const filteredParticipants = (participantState == null ? void 0 : participantState.participants) ? Object.keys(participantState.participants).reduce((acc, participantId) => {
      if (participantState.participants[participantId].conversationId === activeConversationId) {
        acc[participantId] = participantState.participants[participantId];
      }
      return acc;
    }, {}) : {};
    const filteredActivityIndicators = ((_a = participantState == null ? void 0 : participantState.activityIndicatorParticipantList) == null ? void 0 : _a.filter(participant => participant.conversationId === activeConversationId)) || [];
    return [filteredParticipants, filteredActivityIndicators];
  }, [participantState, activeConversationId]);
  return conversationParticipantState;
}

// ../internal/src/components/header/virtual-agent-control-header-close-button.tsx
import React29 from "react";
import { EGDSButtonSize as EGDSButtonSize4 } from "@egds/react-core/button";
import { EGDSLayoutPosition as EGDSLayoutPosition3 } from "@egds/react-core/layout-position";
import { gql as gql16 } from "@shared-ui/apollo-client-wrapper";
var fragment15 = gql16`
  fragment VirtualAgentControlHeaderCloseButtonFragment on VirtualAgentControlEGDSButton {
    analytics {
      ... on VirtualAgentControlInteractionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlOutcomeAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlPageViewAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlImpressionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
    }
    button {
      ...EGDSButtonFragment
    }
  }
`;
var VirtualAgentControlHeaderCloseButton = ({
  onClick,
  data
}) => {
  const handleClose = React29.useCallback(() => {
    onClick == null ? void 0 : onClick();
  }, [onClick]);
  if (!(data == null ? void 0 : data.button)) {
    return null;
  }
  return /* @__PURE__ */React29.createElement(EGDSLayoutPosition3, {
    type: "absolute",
    position: {
      top: "four",
      left: "one"
    }
  }, /* @__PURE__ */React29.createElement(VirtualAgentEGDSButton, {
    data: data.button,
    onClick: handleClose,
    size: EGDSButtonSize4.LARGE
  }));
};
VirtualAgentControlHeaderCloseButton.fragment = fragment15;
VirtualAgentControlHeaderCloseButton.displayName = "VirtualAgentControlHeaderCloseButton";

// ../internal/src/components/header/virtual-agent-control-chat-header.tsx
var fragment16 = gql17`
  fragment VirtualAgentControlChatHeaderFragment on VirtualAgentControlChatHeader {
    avatar {
      ...AvatarGroupFragment
    }
    close {
      ...VirtualAgentControlHeaderCloseButtonFragment
    }
    settingsMenuTrigger {
      analytics {
        ... on VirtualAgentControlInteractionAnalyticEvent {
          eventName
          eventVersion
          payload
        }
        ... on VirtualAgentControlOutcomeAnalyticEvent {
          eventName
          eventVersion
          payload
        }
        ... on VirtualAgentControlPageViewAnalyticEvent {
          eventName
          eventVersion
          payload
        }
        ... on VirtualAgentControlImpressionAnalyticEvent {
          eventName
          eventVersion
          payload
        }
      }
      button {
        ...EGDSButtonFragment
      }
    }
    text
    visuallyHiddenHeaderAccessibilityLabel
    landmarkHeaderAccessibilityLabel
  }
  ${VirtualAgentControlHeaderCloseButton.fragment}
  ${AvatarGroup.fragment}
  ${VirtualAgentEGDSButton.fragment}
`;
var getChatHeaderAvatar = participantDisplayName => {
  const participantInitials = participantDisplayName[0].toUpperCase();
  return {
    __typename: "AvatarGroup",
    items: [{
      text: participantInitials,
      __typename: "Avatar",
      image: null,
      icon: null
    }]
  };
};
function getSettingsMenuAnalytics(settingsMenuAnalytics) {
  let menuItemPresentedImpression;
  let menuClick;
  for (let ind = 0; ind < settingsMenuAnalytics.length; ind += 1) {
    const analytic = settingsMenuAnalytics[ind];
    const analyticsPayload = getEventPayloadAnalytics(analytic.payload, analytic.eventName, analytic.eventVersion);
    switch (analytic.eventName) {
      case "virtual_agent_settings_menu.selected" /* SETTINGS_MENU_SELECTED */:
        {
          menuClick = analyticsPayload;
          break;
        }
      case "virtual_agent_settings_menu_item.presented" /* SETTINGS_MENU_ITEM_PRESENTED */:
        {
          menuItemPresentedImpression = analyticsPayload;
          break;
        }
    }
  }
  return {
    menuItemPresentedImpression,
    menuClick
  };
}
var VirtualAgentControlChatHeader = ({
  onMinimize,
  embedded,
  onOpenSettings,
  data
}) => {
  const {
    avatar,
    text,
    close,
    settingsMenuTrigger
  } = data;
  const [chatHeader, setChatHeader] = useState2(text);
  const [chatHeaderAvatar, setChatHeaderAvatar] = useState2(avatar);
  const {
    typeHumanAgent
  } = use_prefetch_localization_content_default(LocalizationContentKeys.PARTICIPANT) || {};
  const [conversation] = useConversationState();
  const {
    analytics: settingsMenuAnalytics
  } = settingsMenuTrigger != null ? settingsMenuTrigger : {};
  const {
    menuItemPresentedImpression = {},
    menuClick = {}
  } = (settingsMenuAnalytics == null ? void 0 : settingsMenuAnalytics.length) ? getSettingsMenuAnalytics(settingsMenuAnalytics) : {};
  const {
    trackEvent
  } = useAnalytics();
  const [participantState] = useParticipantState();
  const getParticipantHeader = useCallback3(() => {
    const participant = Object.keys(participantState).reduce((acc, participantId) => {
      const currentParticipant = participantState[participantId];
      if (currentParticipant.participantType === "HumanAgent" && currentParticipant.participantState === "JOINED" /* JOINED */) {
        return participantState[participantId];
      }
      return acc;
    }, {});
    if (!Object.keys(participant).length) {
      setChatHeader(text);
      setChatHeaderAvatar(avatar);
      return;
    }
    const participantDisplayName = getPartcipantDisplayName(participant);
    setChatHeader(participantDisplayName || typeHumanAgent);
    setChatHeaderAvatar(getChatHeaderAvatar(participantDisplayName || typeHumanAgent));
  }, [participantState]);
  useEffect3(() => {
    getParticipantHeader();
  }, [getParticipantHeader]);
  const handleMinimize = React30.useCallback(() => {
    trackEvent(buildClickstreamEventPaylaod("VIRTUAL_AGENT_CLOSED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type));
    onMinimize == null ? void 0 : onMinimize();
  }, [onMinimize]);
  const handleOpenSettings = React30.useCallback(() => {
    trackEvent(buildClickstreamAdditionalContextPayload("VIRTUAL_AGENT_SETTINGS_MENU_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, menuClick == null ? void 0 : menuClick.additionalContext));
    trackEvent(buildClickstreamAdditionalContextPayload("VIRTUAL_AGENT_SETTINGS_MENU_ITEM_PRESENTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, menuItemPresentedImpression == null ? void 0 : menuItemPresentedImpression.additionalContext));
    onOpenSettings == null ? void 0 : onOpenSettings();
  }, [onOpenSettings]);
  return /* @__PURE__ */React30.createElement(EGDSSpacing5, {
    padding: {
      blockstart: "six",
      blockend: "two"
    }
  }, /* @__PURE__ */React30.createElement("div", null, !embedded && close && /* @__PURE__ */React30.createElement(VirtualAgentControlHeaderCloseButton, {
    data: close,
    onClick: handleMinimize
  }), /* @__PURE__ */React30.createElement(EGDSLayoutFlex8, {
    tag: "header",
    direction: "column",
    alignItems: "center",
    space: "one"
  }, /* @__PURE__ */React30.createElement("div", {
    "aria-hidden": "true"
  }, /* @__PURE__ */React30.createElement(AvatarGroup, {
    data: chatHeaderAvatar
  })), /* @__PURE__ */React30.createElement(EGDSHeading4, {
    tag: "h2",
    size: 7,
    overflow: "truncate"
  }, chatHeader)), (settingsMenuTrigger == null ? void 0 : settingsMenuTrigger.button) && /* @__PURE__ */React30.createElement(EGDSLayoutPosition4, {
    type: "absolute",
    position: {
      top: "four",
      right: "one"
    }
  }, /* @__PURE__ */React30.createElement(VirtualAgentEGDSButton, {
    data: settingsMenuTrigger.button,
    onClick: handleOpenSettings,
    size: EGDSButtonSize5.LARGE
  }))));
};
VirtualAgentControlChatHeader.fragment = fragment16;
VirtualAgentControlChatHeader.displayName = "VirtualAgentControlChatHeader";

// ../internal/src/components/header/virtual-agent-control-toolbar.tsx
import React31 from "react";
import { EGDSButtonSize as EGDSButtonSize6 } from "@egds/react-core/button";
import { EGDSToolbar as EGDSToolbar2 } from "@egds/react-core/toolbar";
import { gql as gql18 } from "@shared-ui/apollo-client-wrapper";
var fragment17 = gql18`
  fragment VirtualAgentControlToolbarFragment on VirtualAgentControlToolbar {
    closeButton {
      ...EGDSButtonFragment
    }
  }
  ${VirtualAgentEGDSButton.fragment}
`;
var VirtualAgentControlToolbar = ({
  onClose,
  data
}) => {
  const {
    closeButton
  } = data;
  const handleClose = React31.useCallback(() => {
    onClose == null ? void 0 : onClose();
  }, [onClose]);
  if (!closeButton) {
    return null;
  }
  return /* @__PURE__ */React31.createElement(EGDSToolbar2, null, /* @__PURE__ */React31.createElement(VirtualAgentEGDSButton, {
    data: closeButton,
    onClick: handleClose,
    size: EGDSButtonSize6.LARGE
  }));
};
VirtualAgentControlToolbar.fragment = fragment17;
VirtualAgentControlToolbar.displayName = "VirtualAgentControlToolbar";

// ../internal/src/components/loading/loading-dialog.tsx
import React33 from "react";
import { EGDSDialog as EGDSDialog2, EGDSDialogContent as EGDSDialogContent2 } from "@egds/react-core/dialog";
import { EGDSLayoutFlex as EGDSLayoutFlex9, EGDSLayoutFlexItem as EGDSLayoutFlexItem5 } from "@egds/react-core/layout-flex";
import { EGDSLoader } from "@egds/react-core/loader";
import { EGDSText as EGDSText8 } from "@egds/react-core/text";
import { useDialog as useDialog3 } from "@shared-ui/dialog-context";

// ../internal/src/hooks/loading/use-loading-state.tsx
import React32 from "react";
import { useProvidedState as useProvidedState10 } from "@shared-ui/provided-state-context";
function useChatInstanceLoading() {
  var _a;
  const [state, dispatch] = useProvidedState10();
  const vacId = useVacId();
  const chatInstanceLoading = (_a = state.loadingState) == null ? void 0 : _a[vacId];
  let loadingDialog;
  if (chatInstanceLoading == null ? void 0 : chatInstanceLoading.active) {
    loadingDialog = chatInstanceLoading.loadingComponent.loadingDialog;
  }
  const chatInstanceLoadingDispatch = React32.useCallback(({
    type,
    payload: payload2
  }) => {
    dispatch({
      type,
      payload: {
        ...payload2,
        source: vacId
      }
    });
  }, [dispatch, vacId]);
  return [loadingDialog, chatInstanceLoadingDispatch];
}

// ../internal/src/components/loading/loading-dialog.tsx
var VirtualAgentControlLoadingDialog = () => {
  const [loadingDialog] = useChatInstanceLoading();
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog3("vac-loading-dialog");
  const {
    text = "Loading..."
  } = loadingDialog || {};
  React33.useEffect(() => {
    if (loadingDialog && !isDialogOpen) {
      dialogActions.openDialog();
    } else if (!loadingDialog && isDialogOpen) {
      dialogActions.closeDialog();
    }
  }, [loadingDialog, isDialogOpen, dialogActions]);
  if (!loadingDialog) {
    return null;
  }
  return /* @__PURE__ */React33.createElement(DialogComponent, null, /* @__PURE__ */React33.createElement(EGDSDialog2, {
    dialogShow: isDialogOpen
  }, /* @__PURE__ */React33.createElement(EGDSDialogContent2, null, /* @__PURE__ */React33.createElement(EGDSLayoutFlex9, {
    direction: "column",
    space: "two"
  }, /* @__PURE__ */React33.createElement(EGDSLayoutFlexItem5, {
    alignSelf: "center"
  }, /* @__PURE__ */React33.createElement(EGDSLoader, null)), /* @__PURE__ */React33.createElement(EGDSLayoutFlexItem5, {
    alignSelf: "center"
  }, /* @__PURE__ */React33.createElement(EGDSText8, {
    spacing: "two"
  }, text))))));
};

// ../internal/src/components/menuItem/virtual-agent-control-menu-settings.tsx
import React41, { useEffect as useEffect4 } from "react";
import { useProvidedState as useProvidedState12 } from "@shared-ui/provided-state-context";

// ../internal/src/store/chat-instances-state/reducers.ts
import isEqual from "lodash.isequal";

// ../internal/src/components/menuItem/virtual-agent-control-menu-bottomsheet-view.tsx
import React39 from "react";
import { EGDSSheet, EGDSSheetContent, EGDSSheetTransition } from "@egds/react-core/sheet";
import { gql as gql23 } from "@shared-ui/apollo-client-wrapper";
import { useDialog as useDialog4 } from "@shared-ui/dialog-context";

// ../internal/src/components/menuItem/virtual-agent-control-menu-list.tsx
import React37 from "react";
import { EGDSActionList } from "@egds/react-core/action-list-item";
import { useProvidedState as useProvidedState11 } from "@shared-ui/provided-state-context";

// ../internal/src/components/menuItem/virtual-agent-control-menu-list-item.tsx
import React36 from "react";
import { gql as gql21 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/menuItem/virtual-agent-control-button-menu-list-item.tsx
import React34 from "react";
import { EGDSActionListItem } from "@egds/react-core/action-list-item";
import { EGDSIcon as EGDSIcon5 } from "@egds/react-core/icons";
import { EGDSLayoutFlex as EGDSLayoutFlex10, EGDSLayoutFlexItem as EGDSLayoutFlexItem6 } from "@egds/react-core/layout-flex";
import { EGDSText as EGDSText9 } from "@egds/react-core/text";
import { gql as gql19 } from "@shared-ui/apollo-client-wrapper";
function getSettingsMenuInteractionAnalytics(analytics) {
  if (analytics == null ? void 0 : analytics.length) {
    const menuItemClickedAnalytic = analytics.find(analytic => analytic.eventName === "virtual_agent_settings_menu_item.selected");
    if (menuItemClickedAnalytic) {
      return getEventPayloadAnalytics(menuItemClickedAnalytic.payload, menuItemClickedAnalytic.eventName, menuItemClickedAnalytic.eventVersion);
    }
  }
  return null;
}
var fragment18 = gql19`
  fragment VirtualAgentControlButtonMenuListItemFragment on VirtualAgentControlButtonMenuListItem {
    id
    icon {
      ...IconFragment
    }
    label
    action {
      eventType
    }
    analytics {
      eventName
      eventVersion
      payload
    }
    accessibilityLabel
  }
  ${Icon.fragment}
`;
var VirtualAgentControlButtonMenuListItem = ({
  data,
  onCloseSettings
}) => {
  var _a;
  const {
    icon,
    label,
    action,
    analytics
  } = data;
  const downloadTranscript = useOperation("DOWNLOAD_TRANSCRIPT" /* DOWNLOAD_TRANSCRIPT */);
  const interactionAnalytics = getSettingsMenuInteractionAnalytics(analytics);
  const {
    trackEvent
  } = useAnalytics();
  const [conversation] = useConversationState();
  const handleOnItemClick = () => {
    if (onCloseSettings) {
      onCloseSettings();
    }
    if ((action == null ? void 0 : action.eventType) === "DOWNLOAD_TRANSCRIPT" /* DOWNLOAD_TRANSCRIPT */ && downloadTranscript) {
      downloadTranscript();
    }
    trackEvent(buildClickstreamAdditionalContextPayload("VIRTUAL_AGENT_SETTINGS_MENU_ITEM_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, interactionAnalytics == null ? void 0 : interactionAnalytics.additionalContext));
  };
  return /* @__PURE__ */React34.createElement(EGDSActionListItem, {
    key: `menu-item-${data.id}`
  }, /* @__PURE__ */React34.createElement(EGDSLayoutFlex10, {
    space: "three",
    onClick: handleOnItemClick
  }, /* @__PURE__ */React34.createElement(EGDSLayoutFlexItem6, {
    shrink: 0,
    alignSelf: "center"
  }, /* @__PURE__ */React34.createElement("div", {
    "aria-hidden": "true"
  }, /* @__PURE__ */React34.createElement(EGDSIcon5, {
    name: icon.id,
    id: icon.id,
    description: (_a = icon.description) != null ? _a : void 0
  }))), /* @__PURE__ */React34.createElement(EGDSLayoutFlexItem6, {
    grow: 1
  }, /* @__PURE__ */React34.createElement("section", null, /* @__PURE__ */React34.createElement(EGDSText9, {
    size: 300,
    theme: "emphasis"
  }, label)))));
};
VirtualAgentControlButtonMenuListItem.fragment = fragment18;
VirtualAgentControlButtonMenuListItem.displayName = "VirtualAgentControlButtonMenuListItem";

// ../internal/src/components/menuItem/virtual-agent-control-toggle-menu-list-item.tsx
import React35 from "react";
import { EGDSActionListItem as EGDSActionListItem2 } from "@egds/react-core/action-list-item";
import { EGDSIcon as EGDSIcon6 } from "@egds/react-core/icons";
import { EGDSLayoutFlex as EGDSLayoutFlex11, EGDSLayoutFlexItem as EGDSLayoutFlexItem7 } from "@egds/react-core/layout-flex";
import { EGDSText as EGDSText10 } from "@egds/react-core/text";
import { gql as gql20 } from "@shared-ui/apollo-client-wrapper";
function getSettingsMenuInteractionAnalytics2(checkedAnalytics) {
  if (checkedAnalytics == null ? void 0 : checkedAnalytics.length) {
    const menuItemClickedAnalytic = checkedAnalytics.find(analytics => analytics.eventName === "virtual_agent_settings_menu_item.selected");
    if (menuItemClickedAnalytic) {
      return getEventPayloadAnalytics(menuItemClickedAnalytic.payload, menuItemClickedAnalytic.eventName, menuItemClickedAnalytic.eventVersion);
    }
  }
  return null;
}
var fragment19 = gql20`
  fragment VirtualAgentControlToggleMenuListItemFragment on VirtualAgentControlToggleMenuListItem {
    checked
    checkedAccessibilityLabel
    checkedDescription
    checkedIcon {
      ...IconFragment
    }
    checkedLabel
    checkedToast {
      text
    }
    uncheckedToast {
      text
    }
    checkedAnalytics {
      ... on VirtualAgentControlImpressionAnalyticEvent {
        eventName
        eventVersion
        payload
      }

      ... on VirtualAgentControlInteractionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlOutcomeAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlPageViewAnalyticEvent {
        eventName
        eventVersion
        payload
      }
    }
    id
    uncheckedAccessibilityLabel
    uncheckedDescription
    uncheckedIcon {
      ...IconFragment
    }
    uncheckedLabel
  }
  ${Icon.fragment}
`;
var VirtualAgentControlToggleMenuListItem = ({
  onCloseSettings,
  onToggle,
  isChecked,
  data
}) => {
  var _a, _b, _c, _d;
  const {
    id,
    checkedAnalytics,
    checkedIcon,
    checkedLabel,
    uncheckedIcon,
    uncheckedLabel,
    checkedToast,
    uncheckedToast
  } = data;
  const interactionAnalytics = getSettingsMenuInteractionAnalytics2(checkedAnalytics);
  const {
    trackEvent
  } = useAnalytics();
  const [conversation] = useConversationState();
  const showToast = useOperation("SHOW_TOAST" /* SHOW_TOAST */);
  const handleCheckboxChange = () => {
    trackEvent(buildClickstreamAdditionalContextPayload("VIRTUAL_AGENT_SETTINGS_MENU_ITEM_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, interactionAnalytics == null ? void 0 : interactionAnalytics.additionalContext));
    if (onCloseSettings) {
      onCloseSettings();
    }
    if (onToggle) {
      onToggle(id);
    }
    if (showToast) {
      showToast(isChecked ? checkedToast == null ? void 0 : checkedToast.text : uncheckedToast == null ? void 0 : uncheckedToast.text);
    }
  };
  return /* @__PURE__ */React35.createElement(EGDSActionListItem2, {
    key: `menu-item-${id}`
  }, /* @__PURE__ */React35.createElement(EGDSLayoutFlex11, {
    alignContent: "center",
    dataTestId: "uitk-flex-toggle",
    space: "three",
    alignItems: "center",
    onClick: handleCheckboxChange
  }, /* @__PURE__ */React35.createElement(EGDSLayoutFlexItem7, {
    shrink: 0,
    alignSelf: "center"
  }, /* @__PURE__ */React35.createElement("div", {
    "aria-hidden": "true"
  }, isChecked ? /* @__PURE__ */React35.createElement(EGDSIcon6, {
    name: checkedIcon.id,
    id: checkedIcon.id,
    description: (_a = checkedIcon.description) != null ? _a : void 0,
    title: (_b = checkedIcon.title) != null ? _b : void 0
  }) : /* @__PURE__ */React35.createElement(EGDSIcon6, {
    name: uncheckedIcon.id,
    id: uncheckedIcon.id,
    description: (_c = uncheckedIcon.description) != null ? _c : void 0,
    title: (_d = checkedIcon.title) != null ? _d : void 0
  }))), /* @__PURE__ */React35.createElement(EGDSLayoutFlexItem7, {
    grow: 1
  }, /* @__PURE__ */React35.createElement("section", null, /* @__PURE__ */React35.createElement(EGDSText10, {
    size: 300,
    theme: "emphasis"
  }, isChecked ? checkedLabel : uncheckedLabel)))));
};
VirtualAgentControlToggleMenuListItem.fragment = fragment19;
VirtualAgentControlToggleMenuListItem.displayName = "VirtualAgentControlToggleMenuListItem";

// ../internal/src/components/menuItem/virtual-agent-control-menu-list-item.tsx
var fragment20 = gql21`
  fragment VirtualAgentControlMenuListItemFragment on VirtualAgentControlMenuListItem {
    ...VirtualAgentControlButtonMenuListItemFragment
    ...VirtualAgentControlToggleMenuListItemFragment
  }
  ${VirtualAgentControlButtonMenuListItem.fragment}
  ${VirtualAgentControlToggleMenuListItem.fragment}
`;
var VirtualAgentControlMenuListItem = ({
  onCloseSettings,
  onToggle,
  isChecked,
  data
}) => {
  switch (data.__typename) {
    case "VirtualAgentControlButtonMenuListItem":
      return /* @__PURE__ */React36.createElement(VirtualAgentControlButtonMenuListItem, {
        data,
        onCloseSettings
      });
    case "VirtualAgentControlToggleMenuListItem":
      return /* @__PURE__ */React36.createElement(VirtualAgentControlToggleMenuListItem, {
        data,
        onCloseSettings,
        onToggle,
        isChecked
      });
    default:
      return null;
  }
};
VirtualAgentControlMenuListItem.fragment = fragment20;
VirtualAgentControlMenuListItem.displayName = "VirtualAgentControlMenuListItem";

// ../internal/src/components/menuItem/virtual-agent-control-menu-list.tsx
var VirtualAgentControlMenuList = ({
  elements,
  onCloseSettings
}) => {
  var _a;
  const vacId = useVacId();
  const [state, dispatch] = useProvidedState11();
  const {
    settingMenuItemState
  } = ((_a = state.chatInstancesState) == null ? void 0 : _a[vacId]) || {};
  function handleItemToggle(id) {
    dispatch({
      type: "SET_SETTINGS_MENU_STATE" /* SET_SETTINGS_MENU_STATE */,
      payload: {
        vacId,
        menuItemId: id,
        menuItemState: !settingMenuItemState[id]
      }
    });
  }
  if (!(elements == null ? void 0 : elements.length)) {
    return null;
  }
  return /* @__PURE__ */React37.createElement(EGDSActionList, null, elements.map((item, index) => /* @__PURE__ */React37.createElement(VirtualAgentControlMenuListItem, {
    data: item,
    key: `menu-item-${item.id}`,
    onCloseSettings,
    onToggle: () => handleItemToggle(item.id),
    isChecked: settingMenuItemState[item.id]
  })));
};

// ../internal/src/components/menuItem/virtual-agent-control-menu-toolbar.tsx
import React38 from "react";
import { EGDSToolbar as EGDSToolbar3 } from "@egds/react-core/toolbar";
import { gql as gql22 } from "@shared-ui/apollo-client-wrapper";
var fragment21 = gql22`
  fragment VirtualAgentControlMenuToolbarFragment on VirtualAgentControlMenuToolbar {
    title
    navigationButton {
      ...EGDSButtonFragment
    }
  }
  ${VirtualAgentEGDSButton.fragment}
`;
var VirtualAgentControlMenuToolbar = ({
  onCloseSettings,
  data
}) => {
  var _a;
  const {
    title,
    navigationButton
  } = data;
  const handleNavigationClick = () => {
    if (onCloseSettings) {
      onCloseSettings();
    }
  };
  return /* @__PURE__ */React38.createElement(EGDSToolbar3, {
    id: "menu-toolbar",
    navigationContent: {
      onClick: handleNavigationClick,
      navType: "back",
      navIconLabel: (_a = navigationButton == null ? void 0 : navigationButton.accessibility) != null ? _a : ""
    },
    toolbarTitle: title || ""
  });
};
VirtualAgentControlMenuToolbar.fragment = fragment21;
VirtualAgentControlMenuToolbar.displayName = "VirtualAgentControlMenuToolbar";

// ../internal/src/components/menuItem/virtual-agent-control-menu-bottomsheet-view.tsx
var fragment22 = gql23`
  fragment VirtualAgentControlMenuBottomSheetViewFragment on VirtualAgentControlMenuBottomSheetView {
    element {
      ...VirtualAgentControlMenuListItemFragment
    }
    toolbar {
      ...VirtualAgentControlMenuToolbarFragment
    }
  }
  ${VirtualAgentControlMenuListItem.fragment}
  ${VirtualAgentControlMenuToolbar.fragment}
`;
var VirtualAgentControlMenuBottomSheetView = ({
  open,
  onCloseSettings,
  data
}) => {
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog4("va-settings-menu");
  const {
    element,
    toolbar
  } = data;
  React39.useEffect(() => {
    if (open && !isDialogOpen) {
      dialogActions.openDialog();
    } else if (!open && isDialogOpen) {
      dialogActions.closeDialog();
    }
  }, [open]);
  if (!(element == null ? void 0 : element.length) || !(toolbar == null ? void 0 : toolbar.title)) {
    return null;
  }
  const handleDismiss = () => {
    onCloseSettings == null ? void 0 : onCloseSettings();
    dialogActions.closeDialog();
  };
  return /* @__PURE__ */React39.createElement(EGDSSheetTransition, {
    isVisible: isDialogOpen
  }, /* @__PURE__ */React39.createElement(DialogComponent, null, /* @__PURE__ */React39.createElement(EGDSSheet, {
    type: "bottom",
    isVisible: isDialogOpen,
    triggerRef: null,
    onDismiss: handleDismiss,
    ariaLabel: toolbar.title
  }, /* @__PURE__ */React39.createElement(EGDSSheetContent, {
    padded: false
  }, toolbar && /* @__PURE__ */React39.createElement(VirtualAgentControlMenuToolbar, {
    data: toolbar,
    onCloseSettings
  }), /* @__PURE__ */React39.createElement(VirtualAgentControlMenuList, {
    onCloseSettings,
    elements: [...element]
  })))));
};
VirtualAgentControlMenuBottomSheetView.fragment = fragment22;
VirtualAgentControlMenuBottomSheetView.displayName = "VirtualAgentControlMenuBottomSheetView";

// ../internal/src/components/menuItem/virtual-agent-control-menu-popover-view.tsx
import React40 from "react";
import { EGDSMenu, EGDSMenuContainer, PositionType } from "@egds/react-core/menu";
import { gql as gql24 } from "@shared-ui/apollo-client-wrapper";
var fragment23 = gql24`
  fragment VirtualAgentControlMenuPopoverViewFragment on VirtualAgentControlMenuPopoverView {
    element {
      ...VirtualAgentControlMenuListItemFragment
    }
  }
  ${VirtualAgentControlMenuListItem.fragment}
`;
var VirtualAgentControlMenuPopoverView = ({
  open,
  onCloseSettings,
  data
}) => {
  const {
    element
  } = data;
  if (!(element == null ? void 0 : element.length)) {
    return null;
  }
  return /* @__PURE__ */React40.createElement(EGDSMenu, {
    id: "uitk-menu-item",
    isOpen: open
  }, /* @__PURE__ */React40.createElement(EGDSMenuContainer, {
    position: PositionType.RIGHT,
    showOverTrigger: true,
    width: 170
  }, /* @__PURE__ */React40.createElement(VirtualAgentControlMenuList, {
    onCloseSettings,
    elements: [...element]
  })));
};
VirtualAgentControlMenuPopoverView.fragment = fragment23;
VirtualAgentControlMenuPopoverView.displayName = "VirtualAgentControlMenuPopoverView";

// ../internal/src/components/menuItem/virtual-agent-control-menu-settings.tsx
var VirtualAgentControlMenuSettings = ({
  settingsMenu,
  handleMenuCloseSettings,
  isSettingsVisible
}) => {
  const vacId = useVacId();
  const [, dispatch] = useProvidedState12();
  function setToggleMenuItemState() {
    const {
      element
    } = settingsMenu;
    element.forEach(item => {
      if (item.__typename === "VirtualAgentControlToggleMenuListItem") {
        dispatch({
          type: "SET_SETTINGS_MENU_STATE" /* SET_SETTINGS_MENU_STATE */,
          payload: {
            vacId,
            menuItemId: item.id,
            menuItemState: item.checked
          }
        });
      }
    });
  }
  useEffect4(() => {
    if (settingsMenu) {
      setToggleMenuItemState();
    }
  }, [settingsMenu]);
  if ((settingsMenu == null ? void 0 : settingsMenu.__typename) === "VirtualAgentControlMenuPopoverView") {
    return /* @__PURE__ */React41.createElement(VirtualAgentControlMenuPopoverView, {
      data: settingsMenu,
      onCloseSettings: handleMenuCloseSettings,
      open: isSettingsVisible
    });
  }
  return settingsMenu && /* @__PURE__ */React41.createElement(VirtualAgentControlMenuBottomSheetView, {
    data: settingsMenu,
    onCloseSettings: handleMenuCloseSettings,
    open: isSettingsVisible
  });
};

// ../internal/src/components/quick-replies/virtual-agent-control-quick-replies.tsx
import React52, { useEffect as useEffect6, useState as useState4 } from "react";
import omit from "lodash.omit";
import { EGDSLayoutFlex as EGDSLayoutFlex15 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing8 } from "@egds/react-core/spacing";
import { useSignalSubscriber } from "@shared-ui/core-signal-manager/context";
import { useDialog as useDialog7 } from "@shared-ui/dialog-context";
import { VacParticipantEventsSignal } from "@shared-ui/retail-signals-virtual-agent";

// ../internal/src/components/quick-replies/virtual-agent-control-end-chat-dialog.tsx
import React50 from "react";
import { EGDSDialog as EGDSDialog4, EGDSDialogContent as EGDSDialogContent4 } from "@egds/react-core/dialog";
import { EGDSSpacingHr as EGDSSpacingHr2 } from "@egds/react-core/spacing";
import { gql as gql31 } from "@shared-ui/apollo-client-wrapper";
import { useDialog as useDialog6 } from "@shared-ui/dialog-context";

// ../internal/src/components/declarative-cards/components/dynamic-card-action.tsx
import React44 from "react";
import { v4 as uuidv43 } from "uuid";
import { EGDSButtonSize as EGDSButtonSize7, EGDSPrimaryButton as EGDSPrimaryButton2, EGDSSecondaryButton as EGDSSecondaryButton3, EGDSTertiaryButton as EGDSTertiaryButton4 } from "@egds/react-core/button";
import { EGDSDialog as EGDSDialog3, EGDSDialogContent as EGDSDialogContent3 } from "@egds/react-core/dialog";
import { EGDSLayoutFlex as EGDSLayoutFlex12 } from "@egds/react-core/layout-flex";
import { EGDSLink as EGDSLink4 } from "@egds/react-core/link";
import { EGDSSpacingHr } from "@egds/react-core/spacing";
import { gql as gql26 } from "@shared-ui/apollo-client-wrapper";
import { useSignalPublisher as useSignalPublisher2 } from "@shared-ui/core-signal-manager/context";
import { useDialog as useDialog5 } from "@shared-ui/dialog-context";
import { VacDynamicCardSignal } from "@shared-ui/retail-signals-virtual-agent";

// ../internal/src/components/declarative-cards/providers/dynamic-card-context-provider.tsx
import React42, { createContext, useContext } from "react";
var DefaultWebview = ({
  webview,
  isVisible
}) => {
  var _a;
  const webviewContent = ((_a = webview == null ? void 0 : webview.url) == null ? void 0 : _a.value) || (webview == null ? void 0 : webview.srcDoc) || "webview";
  return isVisible && /* @__PURE__ */React42.createElement("div", null, "Please override the default webview component in the DynamicCardContextProvider to render ", webviewContent, ".");
};
function useDefaultWebview(webview) {
  const [isWebviewVisible, setWebviewVisible] = React42.useState(false);
  return {
    WebviewComponent: /* @__PURE__ */React42.createElement(DefaultWebview, {
      webview,
      isVisible: isWebviewVisible
    }),
    openWebview: () => {
      setWebviewVisible(true);
    }
  };
}
function useDefaultActionDisabled() {
  return false;
}
var initialContext = {
  useWebview: useDefaultWebview,
  useActionDisabled: useDefaultActionDisabled
};
var DynamicCardContext = createContext(initialContext);
var useDynamicCardContext = () => {
  return useContext(DynamicCardContext);
};
var DynamicCardContextProvider = ({
  children,
  value
}) => {
  const context = React42.useMemo(() => ({
    useWebview: value.useWebview || useDefaultWebview,
    useActionDisabled: value.useActionDisabled || useDefaultActionDisabled
  }), [value]);
  return /* @__PURE__ */React42.createElement(DynamicCardContext.Provider, {
    value: context
  }, children);
};

// ../internal/src/components/declarative-cards/components/dynamic-card-text.tsx
import React43 from "react";
import { EGDSText as EGDSText11 } from "@egds/react-core/text";
import { EGDSUnformattedHtml } from "@egds/react-core/unformatted-html";
import { gql as gql25 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/declarative-cards/helpers/apply-text-styles.ts
var applyTextStyles = (text, bold, italic, strikethrough) => {
  let result = text;
  const applyStyle = (str, substrings, tag) => {
    if ((substrings == null ? void 0 : substrings.length) === 0) {
      return `<${tag}>${str}</${tag}>`;
    }
    let output = str;
    const openTag = `<${tag}>`;
    const closeTag = `</${tag}>`;
    [...substrings].reverse().forEach(substr => {
      let pos = output.lastIndexOf(substr);
      while (pos !== -1) {
        output = output.slice(0, pos) + openTag + substr + closeTag + output.slice(pos + substr.length);
        pos = output.lastIndexOf(substr, pos - 1);
      }
    });
    return output;
  };
  if ((strikethrough == null ? void 0 : strikethrough.length) || (strikethrough == null ? void 0 : strikethrough.length) === 0) {
    result = applyStyle(result, strikethrough, "s");
  }
  if ((italic == null ? void 0 : italic.length) || (italic == null ? void 0 : italic.length) === 0) {
    result = applyStyle(result, italic, "i");
  }
  if ((bold == null ? void 0 : bold.length) || (bold == null ? void 0 : bold.length) === 0) {
    result = applyStyle(result, bold, "b");
  }
  return result;
};

// ../internal/src/components/declarative-cards/components/dynamic-card-text.tsx
var fragment24 = gql25`
  fragment DynamicCardTextFragment on DynamicCardText {
    value
    bold
    size
    theme
    overflow
    subscript
    superscript
    italic
    strikethrough
  }
`;
var DynamicCardText = ({
  data
}) => {
  var _a, _b;
  const {
    value,
    size,
    bold,
    theme,
    overflow,
    subscript,
    superscript,
    italic,
    strikethrough
  } = data;
  const useBoldFontWeight = (bold == null ? void 0 : bold.length) === 0 || (bold == null ? void 0 : bold.length) === 1 && bold[0] === value;
  const props = {
    size: size != null ? size : 300,
    weight: useBoldFontWeight ? "bold" : "regular",
    theme: (_a = theme == null ? void 0 : theme.toLowerCase()) != null ? _a : "default"
  };
  if (overflow) {
    props.overflow = (_b = overflow == null ? void 0 : overflow.toLowerCase()) != null ? _b : "truncate";
  }
  let textContent = value;
  if ((bold == null ? void 0 : bold.length) && !useBoldFontWeight || italic || strikethrough) {
    const result = applyTextStyles(textContent, bold, italic, strikethrough);
    textContent = /* @__PURE__ */React43.createElement(EGDSUnformattedHtml, {
      htmlToParse: result
    });
  }
  if (subscript || superscript) {
    const adjustedProps = {
      ...props,
      size: 100
    };
    return /* @__PURE__ */React43.createElement(React43.Fragment, null, /* @__PURE__ */React43.createElement(EGDSText11, {
      ...props,
      inline: true
    }, textContent), subscript && /* @__PURE__ */React43.createElement(EGDSText11, {
      ...adjustedProps,
      inline: "sub"
    }, subscript), superscript && /* @__PURE__ */React43.createElement(EGDSText11, {
      ...adjustedProps,
      inline: "sup"
    }, superscript));
  }
  return /* @__PURE__ */React43.createElement(EGDSText11, {
    ...props
  }, textContent);
};
DynamicCardText.fragment = fragment24;
DynamicCardText.displayName = "DynamicCardText";

// ../internal/src/components/declarative-cards/components/dynamic-card-action.tsx
var fragment25 = gql26`
  fragment DynamicCardActionFragment on DynamicCardAction {
    link {
      ... on HttpURI {
        value
      }
    }
    event {
      eventId
      payload
    }
    dialog {
      close {
        ...EGDSButtonFragment
      }
      text {
        ...DynamicCardTextFragment
      }
    }
    properties {
      accessibility
      buttonType
      target
    }
    webview {
      close {
        ...EGDSButtonFragment
      }
      srcDoc
      url {
        relativePath
        value
      }
    }
  }
  ${VirtualAgentEGDSButton.fragment}
  ${DynamicCardText.fragment}
`;
var DynamicCardAction = ({
  text,
  icon,
  data
}) => {
  var _a;
  const {
    link,
    event,
    dialog,
    properties,
    webview
  } = data;
  const {
    publish
  } = useSignalPublisher2();
  const dialogTriggerRef = React44.useRef(null);
  const uniqueDialogId = React44.useMemo(() => `vac-declarative-cards-dialog-${uuidv43()}`, []);
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog5(uniqueDialogId);
  const {
    useWebview,
    useActionDisabled
  } = useDynamicCardContext();
  const {
    WebviewComponent,
    openWebview
  } = useWebview(webview);
  const actionIsDisabled = useActionDisabled();
  const {
    buttonType,
    accessibility
  } = properties != null ? properties : {};
  const href = (link == null ? void 0 : link.__typename) === "HttpURI" && link.value.length > 0 ? link.value : void 0;
  const target = UILinkTargetMap[(_a = properties == null ? void 0 : properties.target) != null ? _a : "INTERNAL" /* INTERNAL */];
  const getContent = () => {
    if (text && icon) {
      return /* @__PURE__ */React44.createElement(EGDSLayoutFlex12, {
        space: "one",
        alignItems: "center"
      }, icon && /* @__PURE__ */React44.createElement(Icon, {
        data: icon
      }), text == null ? void 0 : text.value);
    }
    if (text) {
      return text == null ? void 0 : text.value;
    }
    if (icon) {
      return /* @__PURE__ */React44.createElement(Icon, {
        data: icon
      });
    }
  };
  const getDialog = React44.useCallback(() => {
    var _a2;
    if (!(dialog == null ? void 0 : dialog.close) && !(dialog == null ? void 0 : dialog.text)) {
      return null;
    }
    return /* @__PURE__ */React44.createElement(DialogComponent, null, /* @__PURE__ */React44.createElement(EGDSDialog3, {
      dialogShow: isDialogOpen,
      triggerRef: dialogTriggerRef
    }, /* @__PURE__ */React44.createElement(EGDSDialogContent3, null, (_a2 = dialog == null ? void 0 : dialog.text) == null ? void 0 : _a2.map((dialogText, index) => /* @__PURE__ */React44.createElement(DynamicCardText, {
      key: index,
      data: dialogText
    })), /* @__PURE__ */React44.createElement(EGDSSpacingHr, null), (dialog == null ? void 0 : dialog.close) && /* @__PURE__ */React44.createElement(VirtualAgentEGDSButton, {
      data: dialog.close,
      onClick: () => dialogActions.closeDialog(),
      fullWidth: true
    }))));
  }, [isDialogOpen, dialog, dialogActions, DialogComponent]);
  const onClick = React44.useCallback(() => {
    var _a2, _b;
    if (event) {
      publish(new VacDynamicCardSignal("VAC_DYNAMIC_CARD_EVENT" /* Vac_DynamicCardEvent */, {
        eventId: (_a2 = event == null ? void 0 : event.eventId) != null ? _a2 : "",
        payload: (_b = event == null ? void 0 : event.payload) != null ? _b : "",
        message: text == null ? void 0 : text.value
      }));
    }
    if (dialog) {
      dialogActions.openDialog();
    }
    if (webview) {
      openWebview();
    }
  }, [event, dialog, webview, publish, text == null ? void 0 : text.value, dialogActions, openWebview]);
  if (!data || !(text || icon)) {
    return null;
  }
  if (buttonType) {
    const isDialogOrWebview = dialog || webview;
    const props = {
      href,
      ...(href && {
        target
      }),
      domRef: dialogTriggerRef,
      isFullWidth: true,
      "aria-label": accessibility != null ? accessibility : void 0,
      onClick: event || isDialogOrWebview ? onClick : void 0,
      size: EGDSButtonSize7.SMALL,
      // ElementProperties can be used to control button size if necessary
      ...(actionIsDisabled && {
        disabled: true
      })
    };
    const buttonMap = {
      ["PRIMARY" /* PRIMARY */]: EGDSPrimaryButton2,
      ["SECONDARY" /* SECONDARY */]: EGDSSecondaryButton3,
      ["TERTIARY" /* TERTIARY */]: EGDSTertiaryButton4
    };
    return /* @__PURE__ */React44.createElement(React44.Fragment, null, React44.createElement(buttonMap[buttonType], {
      ...props
    }, [getContent()]), getDialog(), WebviewComponent);
  }
  if (href) {
    return /* @__PURE__ */React44.createElement(EGDSLink4, null, /* @__PURE__ */React44.createElement("a", {
      href,
      target,
      ref: dialogTriggerRef
    }, getContent()));
  }
  if (dialog || webview || event) {
    return /* @__PURE__ */React44.createElement(React44.Fragment, null, /* @__PURE__ */React44.createElement("a", {
      href: "#",
      onClick,
      ref: dialogTriggerRef
    }, /* @__PURE__ */React44.createElement(EGDSLayoutFlex12, {
      space: "one",
      alignItems: "center"
    }, text && /* @__PURE__ */React44.createElement(DynamicCardText, {
      data: text
    }), icon && /* @__PURE__ */React44.createElement(Icon, {
      data: icon
    }))), getDialog(), WebviewComponent);
  }
  return null;
};
DynamicCardAction.displayName = "DynamicCardAction";
DynamicCardAction.fragment = fragment25;

// ../internal/src/components/declarative-cards/components/dynamic-card-cover-image.tsx
import React47 from "react";
import { EGDSFigure, EGDSFigureAspectRatioType, EGDSImage } from "@egds/react-core/images";
import { EGDSLayoutFlex as EGDSLayoutFlex13 } from "@egds/react-core/layout-flex";
import { EGDSLayoutPosition as EGDSLayoutPosition5 } from "@egds/react-core/layout-position";
import { gql as gql29 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/declarative-cards/components/dynamic-card-overlay-text.tsx
import React45 from "react";
import { EGDSText as EGDSText12 } from "@egds/react-core/text";
import { gql as gql27 } from "@shared-ui/apollo-client-wrapper";
var fragment26 = gql27`
  fragment DynamicCardOverlayTextFragment on DynamicCardOverlayText {
    position
    title
  }
`;
var DynamicCardOverlayText = ({
  data
}) => {
  const {
    title
  } = data;
  if (title.length === 0) {
    return null;
  }
  const props = {
    theme: "inverse",
    size: 600,
    weight: "bold"
  };
  return /* @__PURE__ */React45.createElement(EGDSText12, {
    ...props
  }, title);
};
DynamicCardOverlayText.fragment = fragment26;
DynamicCardOverlayText.displayName = "DynamicCardOverlayText";

// ../internal/src/components/declarative-cards/components/dynamic-card-overlay-toggle.tsx
import React46 from "react";
import { EGDSFavoriteSwitch } from "@egds/react-core/switch";
import { gql as gql28 } from "@shared-ui/apollo-client-wrapper";
import { useSignalPublisher as useSignalPublisher3 } from "@shared-ui/core-signal-manager/context";
import { VacDynamicCardSignal as VacDynamicCardSignal2 } from "@shared-ui/retail-signals-virtual-agent";
var fragment27 = gql28`
  fragment DynamicCardOverlayToggleFragment on DynamicCardOverlayToggle {
    position
    selected
    action {
      event {
        eventId
        payload
      }
      properties {
        accessibility
      }
    }
  }
`;
var DynamicCardOverlayToggle = ({
  data
}) => {
  var _a, _b;
  const {
    action
  } = data;
  const [selected, setSelected] = React46.useState((_a = data.selected) != null ? _a : false);
  const {
    publish
  } = useSignalPublisher3();
  const {
    event
  } = action != null ? action : {};
  const fireEvent = React46.useCallback(() => {
    if (event) {
      const {
        eventId,
        payload: payload2
      } = event;
      publish(new VacDynamicCardSignal2("VAC_DYNAMIC_CARD_EVENT" /* Vac_DynamicCardEvent */, {
        eventId,
        payload: payload2
      }));
    }
  }, [event, publish]);
  const onClick = () => {
    setSelected(current => !current);
    fireEvent();
  };
  const {
    properties
  } = action != null ? action : {};
  return /* @__PURE__ */React46.createElement(EGDSFavoriteSwitch, {
    checked: selected,
    onChange: onClick,
    label: (_b = properties == null ? void 0 : properties.accessibility) != null ? _b : void 0
  });
};
DynamicCardOverlayToggle.fragment = fragment27;
DynamicCardOverlayToggle.displayName = "DynamicCardOverlayToggle";

// ../internal/src/components/declarative-cards/components/dynamic-card-cover-image.tsx
var fragment28 = gql29`
  fragment DynamicCardCoverImageFragment on Image {
    url
    description
    aspectRatio
  }
`;
var supportedOverlays = {
  DynamicCardOverlayText,
  DynamicCardOverlayToggle
};
var DynamicCardCoverImage = ({
  data,
  roundedAll,
  overlays
}) => {
  const {
    url,
    description,
    aspectRatio
  } = data;
  const overlayContent = React47.useMemo(() => {
    if (!(overlays == null ? void 0 : overlays.length)) {
      return null;
    }
    const topRight = overlays.filter(({
      position
    }) => position === "TOP_RIGHT" /* TOP_RIGHT */);
    const bottomLeft = overlays.filter(({
      position
    }) => position === "BOTTOM_LEFT" /* BOTTOM_LEFT */);
    return /* @__PURE__ */React47.createElement(React47.Fragment, null, topRight.length ? /* @__PURE__ */React47.createElement(EGDSLayoutPosition5, {
      type: "absolute",
      position: {
        top: "one",
        right: "one",
        zindex: "layer1"
      }
    }, /* @__PURE__ */React47.createElement(EGDSLayoutFlex13, {
      alignItems: "center"
    }, topRight.map((element, i) => {
      const Component = supportedOverlays[element.__typename];
      return Component ? React47.createElement(Component, {
        key: i,
        data: element
      }) : null;
    }))) : null, bottomLeft.length ? /* @__PURE__ */React47.createElement(EGDSLayoutPosition5, {
      type: "absolute",
      position: {
        bottom: "three",
        left: "four",
        zindex: "layer1"
      }
    }, /* @__PURE__ */React47.createElement(EGDSLayoutFlex13, {
      alignItems: "center"
    }, bottomLeft.map((element, i) => {
      const Component = supportedOverlays[element.__typename];
      return Component ? React47.createElement(Component, {
        key: i,
        data: element
      }) : null;
    }))) : null);
  }, [overlays]);
  if (!(url == null ? void 0 : url.length)) {
    return null;
  }
  const aspectRatioDictionary = {
    ["ONE_ONE" /* ONE_ONE */]: EGDSFigureAspectRatioType.R1_1,
    ["FOUR_THREE" /* FOUR_THREE */]: EGDSFigureAspectRatioType.R4_3,
    ["THREE_TWO" /* THREE_TWO */]: EGDSFigureAspectRatioType.R3_2,
    ["SIXTEEN_NINE" /* SIXTEEN_NINE */]: EGDSFigureAspectRatioType.R16_9,
    ["TWENTY_ONE_NINE" /* TWENTY_ONE_NINE */]: EGDSFigureAspectRatioType.R21_9,
    ["THREE_FOUR" /* THREE_FOUR */]: EGDSFigureAspectRatioType.R3_4,
    ["FOUR_ONE" /* FOUR_ONE */]: EGDSFigureAspectRatioType.R4_1
  };
  const mappedAspectRatio = aspectRatio && aspectRatioDictionary[aspectRatio] ? aspectRatioDictionary[aspectRatio] : EGDSFigureAspectRatioType.R21_9;
  return /* @__PURE__ */React47.createElement(EGDSFigure, {
    ratio: mappedAspectRatio,
    className: roundedAll ? "uitk-card-roundcorner-all" : "",
    imageFit: "cover"
  }, overlayContent, /* @__PURE__ */React47.createElement(EGDSImage, {
    src: url,
    placeholderImage: true,
    alt: description
  }));
};
DynamicCardCoverImage.displayName = "DynamicCardCoverImage";
DynamicCardCoverImage.fragment = fragment28;

// ../internal/src/components/declarative-cards/components/dynamic-card-heading.tsx
import React48 from "react";
import { EGDSHeading as EGDSHeading5 } from "@egds/react-core/text";
import { gql as gql30 } from "@shared-ui/apollo-client-wrapper";
var fragment29 = gql30`
  fragment DynamicCardHeadingFragment on DynamicCardHeading {
    text
    level
    style
    align
  }
`;
var supportedHeadingStyles = Object.freeze({
  ["DISPLAY_SMALL" /* DISPLAY_SMALL */]: "display-small",
  ["HEADLINE_MEDIUM" /* HEADLINE_MEDIUM */]: "headline-medium",
  ["HEADLINE_SMALL" /* HEADLINE_SMALL */]: "headline-small",
  ["HEADLINE_OVERLINE" /* HEADLINE_OVERLINE */]: "headline-overline"
});
var DynamicCardHeading = ({
  data
}) => {
  const {
    text,
    level,
    style,
    align
  } = data;
  const tag = (level == null ? void 0 : level.toLowerCase()) || "h3";
  const customStyle = style && supportedHeadingStyles[style] || "headline-small";
  const alignment = align == null ? void 0 : align.toLowerCase();
  return /* @__PURE__ */React48.createElement(EGDSHeading5, {
    typeStyle: {
      customStyle
    },
    overflow: "truncate-3-lines",
    tag,
    align: alignment
  }, text);
};
DynamicCardHeading.fragment = fragment29;
DynamicCardHeading.displayName = "DynamicCardHeading";

// ../internal/src/components/declarative-cards/components/dynamic-card-webview.tsx
import React49 from "react";
import { EGDSSheet as EGDSSheet2, EGDSSheetContent as EGDSSheetContent2, EGDSSheetTransition as EGDSSheetTransition2 } from "@egds/react-core/sheet";
import { EGDSSpacing as EGDSSpacing6 } from "@egds/react-core/spacing";
import { EGDSToolbar as EGDSToolbar4 } from "@egds/react-core/toolbar";

// ../internal/src/components/quick-replies/virtual-agent-control-end-chat-dialog.tsx
var fragment30 = gql31`
  fragment VirtualAgentControlDialogFragment on VirtualAgentControlDialog {
    close {
      ...EGDSButtonFragment
    }
    open {
      button {
        ...EGDSButtonFragment
      }
      sendMessage {
        attributes {
          incomingEndChatMessageId
          travelerEndChat
        }
        flags {
          actionType
          disableUserInput
          sourceMessageId
        }
        intent
        messageType
        payload
        text
        umid
      }
    }
    text {
      ...DynamicCardTextFragment
    }
  }
  ${DynamicCardText.fragment}
`;
var VirtualAgentControlEndChatDialog = props => {
  const {
    data,
    handleEndChatSend
  } = props;
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog6("va-end-chat-dialog");
  if (!data) {
    return null;
  }
  const {
    open,
    close,
    text
  } = data;
  return /* @__PURE__ */React50.createElement(DialogComponent, null, /* @__PURE__ */React50.createElement(EGDSDialog4, {
    dialogShow: isDialogOpen
  }, /* @__PURE__ */React50.createElement(EGDSDialogContent4, null, text == null ? void 0 : text.map((dialogText, index) => /* @__PURE__ */React50.createElement(DynamicCardText, {
    key: index,
    data: dialogText
  })), /* @__PURE__ */React50.createElement(EGDSSpacingHr2, null), /* @__PURE__ */React50.createElement(VirtualAgentEGDSButton, {
    data: open.button,
    onClick: handleEndChatSend,
    fullWidth: true
  }), /* @__PURE__ */React50.createElement(EGDSSpacingHr2, null), /* @__PURE__ */React50.createElement(VirtualAgentEGDSButton, {
    data: close,
    onClick: () => {
      dialogActions.closeDialog();
    },
    fullWidth: true
  }))));
};
VirtualAgentControlEndChatDialog.fragment = fragment30;
VirtualAgentControlEndChatDialog.displayName = "VirtualAgentControlEndChatDialog";

// ../internal/src/components/quick-replies/virtual-agent-control-quick-reply-item.tsx
import React51 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex14, EGDSLayoutFlexItem as EGDSLayoutFlexItem8 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing7 } from "@egds/react-core/spacing";
import { gql as gql32 } from "@shared-ui/apollo-client-wrapper";
var fragment31 = gql32`
  fragment VirtualAgentControlQuickReplyItemFragment on VirtualAgentControlQuickReplyItem {
    analytics {
      eventName
      eventVersion
      payload
    }
    button {
      ...EGDSButtonFragment
    }
    payload
    outboundMessage
    isEndChat
    isSticky
    dialog {
      ...VirtualAgentControlDialogFragment
    }
  }
  ${VirtualAgentControlEndChatDialog.fragment}
  ${VirtualAgentEGDSButton.fragment}
`;
function getVirtualAgentQuickReplyAnalytic(qrAnalytics) {
  if (qrAnalytics == null ? void 0 : qrAnalytics.length) {
    const quickReplyAnalytic = qrAnalytics.find(analytics => analytics.eventName === "virtual_agent_quick_reply.selected");
    if (quickReplyAnalytic) {
      return getEventPayloadAnalytics(quickReplyAnalytic.payload, quickReplyAnalytic.eventName, quickReplyAnalytic.eventVersion);
    }
  }
  return null;
}
var VirtualAgentControlQuickReplyItem = ({
  data,
  handleEndChatQRClick
}) => {
  const {
    button,
    payload: payload2,
    outboundMessage,
    isEndChat,
    analytics
  } = data;
  const {
    trackEvent
  } = useAnalytics();
  const [conversation] = useConversationState();
  const [, conversationMessages] = useMessagesState();
  const handleSend = useOperation("SEND_MESSAGE" /* SEND_MESSAGE */);
  const handleCreateConversation = useOperation("CREATE_CONVERSATION" /* CREATE_CONVERSATION */);
  const selectionAnalytics = getVirtualAgentQuickReplyAnalytic(analytics);
  function handleOnclick() {
    const message = outboundMessage != null ? outboundMessage : "";
    const messageData = {
      message,
      payload: payload2,
      messageType: "POSTBACK" /* POSTBACK */
    };
    if (selectionAnalytics) {
      trackEvent(buildClickstreamAdditionalContextPayload("VIRTUAL_AGENT_QUICK_REPLY_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, selectionAnalytics == null ? void 0 : selectionAnalytics.additionalContext, conversationMessages == null ? void 0 : conversationMessages.lastQuickMessageId));
    }
    if (isEndChat) {
      return handleEndChatQRClick == null ? void 0 : handleEndChatQRClick();
    }
    if (isFakeIntent(payload2) && conversation.state === "CLOSED" /* CLOSED */) {
      return handleCreateConversation == null ? void 0 : handleCreateConversation();
    }
    handleSend == null ? void 0 : handleSend(messageData);
  }
  return /* @__PURE__ */React51.createElement(EGDSLayoutFlex14, {
    direction: "column"
  }, /* @__PURE__ */React51.createElement(EGDSLayoutFlexItem8, {
    alignSelf: "start",
    maxWidth: "seventy_two"
  }, /* @__PURE__ */React51.createElement(EGDSSpacing7, {
    margin: {
      blockstart: "half"
    }
  }, /* @__PURE__ */React51.createElement(VirtualAgentEGDSButton, {
    data: button,
    onClick: () => handleOnclick()
  }))));
};
VirtualAgentControlQuickReplyItem.fragment = fragment31;
VirtualAgentControlQuickReplyItem.displayName = "VirtualAgentControlQuickReplyItem";

// ../internal/src/components/quick-replies/virtual-agent-control-quick-replies.tsx
function parseFlagsAndAttributes(sendMessagePayload) {
  const flags = omit(sendMessagePayload.flags, "__typename");
  const attributes = omit(sendMessagePayload.attributes, "__typename");
  return {
    ...sendMessagePayload,
    message: sendMessagePayload.text,
    attributes,
    flags
  };
}
var VirtualAgentControlQuickReplies = () => {
  const [, conversationMessages] = useMessagesState();
  const [endChatQuickReply, setEndChatQuickReply] = useState4();
  const [, dialogActions] = useDialog7("va-end-chat-dialog");
  const handleSend = useOperation("SEND_MESSAGE" /* SEND_MESSAGE */);
  useSignalSubscriber("VAC_PARTICIPANT_EVENTS" /* VAC_Participant_Event */, VacParticipantEventsSignal, {
    onSuccess: (/* istanbul ignore next */
    ({
      payload: payload2
    }) => {
      const {
        eventName,
        participantType
      } = payload2;
      if (eventName === "Participant.ParticipantLeft" /* ParticipantLeft */ && participantType === "HumanAgent" /* HumanAgent */) {
        setEndChatQuickReply(null);
      }
    })
  });
  const handleEndChatSend = () => {
    var _a, _b;
    if (!((_b = (_a = endChatQuickReply == null ? void 0 : endChatQuickReply.dialog) == null ? void 0 : _a.open) == null ? void 0 : _b.sendMessage)) {
      return;
    }
    const actionWithFallback = parseFlagsAndAttributes(endChatQuickReply.dialog.open.sendMessage);
    handleSend == null ? void 0 : handleSend(actionWithFallback);
    dialogActions.closeDialog();
    setEndChatQuickReply(null);
  };
  const handleEndChatQRClick = () => {
    dialogActions.openDialog();
  };
  useEffect6(() => {
    var _a;
    if ((_a = conversationMessages == null ? void 0 : conversationMessages.quickReplies) == null ? void 0 : _a.length) {
      for (let index = 0; index < conversationMessages.quickReplies.length; index += 1) {
        const quickReply = conversationMessages.quickReplies[index];
        if (quickReply.isEndChat) {
          setEndChatQuickReply(quickReply);
          break;
        }
      }
    }
  }, [conversationMessages == null ? void 0 : conversationMessages.quickReplies]);
  const quickReplies = conversationMessages == null ? void 0 : conversationMessages.quickReplies.filter(quckRply => !quckRply.isSticky);
  if (endChatQuickReply) {
    quickReplies.unshift(endChatQuickReply);
  }
  if (!(quickReplies == null ? void 0 : quickReplies.length)) {
    return null;
  }
  const toQuickReply = (quickReply, index) => /* @__PURE__ */React52.createElement(VirtualAgentControlQuickReplyItem, {
    data: quickReply,
    key: `quick_reply_${index}`,
    ...(quickReply.isEndChat && {
      handleEndChatQRClick
    })
  });
  return /* @__PURE__ */React52.createElement(React52.Fragment, null, /* @__PURE__ */React52.createElement(EGDSSpacing8, {
    padding: {
      blockend: "three",
      inline: "two"
    }
  }, /* @__PURE__ */React52.createElement(EGDSLayoutFlex15, {
    direction: "row",
    space: "two",
    wrap: "wrap"
  }, quickReplies.map(toQuickReply))), (endChatQuickReply == null ? void 0 : endChatQuickReply.dialog) && /* @__PURE__ */React52.createElement(VirtualAgentControlEndChatDialog, {
    data: endChatQuickReply.dialog,
    handleEndChatSend
  }));
};

// ../internal/src/components/trigger/virtual-agent-control-trigger.tsx
import React54, { useEffect as useEffect7 } from "react";
import { gql as gql34 } from "@shared-ui/apollo-client-wrapper";
import { useInteractionRumTracker as useInteractionRumTracker3 } from "@shared-ui/core-real-user-monitoring/context";
import { useSignalPublisher as useSignalPublisher4 } from "@shared-ui/core-signal-manager/context";
import { SystemEventLevel as SystemEventLevel7 } from "@shared-ui/logger-context";
import { VacInitializationSignal } from "@shared-ui/retail-signals-virtual-agent";

// ../internal/src/components/trigger/__private/__fragments/floating-action-button.tsx
import React53 from "react";
import { EGDSBadgeNotification } from "@egds/react-core/badge";
import { EGDSSecondaryButton as EGDSSecondaryButton4 } from "@egds/react-core/button";
import { EGDSIcon as EGDSIcon7, EGDSIconSize as EGDSIconSize4 } from "@egds/react-core/icons";
import { EGDSLayoutPosition as EGDSLayoutPosition6 } from "@egds/react-core/layout-position";
import { EGDSText as EGDSText13 } from "@egds/react-core/text";
import { gql as gql33 } from "@shared-ui/apollo-client-wrapper";
import { useProvidedState as useProvidedState13 } from "@shared-ui/provided-state-context";
var fragment32 = gql33`
  fragment FloatingActionButtonFragment on UIFloatingActionButton {
    disabled
    primary
    icon {
      description
      id
      title
    }
  }
`;
var FloatingActionButton = ({
  onClick,
  unreadMessageCount,
  data
}) => {
  var _a, _b;
  const {
    disabled,
    icon,
    primary
  } = data;
  const [state] = useProvidedState13();
  const count = unreadMessageCount ? unreadMessageCount.toString() : "0";
  if (!icon && !primary) {
    return null;
  }
  return /* @__PURE__ */React53.createElement(EGDSLayoutPosition6, {
    type: "fixed",
    position: {
      bottom: "four",
      right: "four",
      zindex: "layer3"
    },
    onClick
  }, /* @__PURE__ */React53.createElement(EGDSLayoutPosition6, {
    type: "absolute",
    position: {
      top: "minus-three",
      left: "minus-one"
    }
  }, /* @__PURE__ */React53.createElement(EGDSBadgeNotification, {
    count,
    theme: "action",
    visible: !!unreadMessageCount
  })), /* @__PURE__ */React53.createElement(EGDSSecondaryButton4, {
    type: "button",
    disabled
  }, icon && /* @__PURE__ */React53.createElement(EGDSIcon7, {
    name: icon.id,
    id: icon.id,
    title: (_a = icon.title) != null ? _a : void 0,
    description: (_b = icon.description) != null ? _b : void 0,
    size: EGDSIconSize4.MEDIUM
  }), primary && /* @__PURE__ */React53.createElement(EGDSText13, {
    whiteSpace: "nowrap"
  }, primary)));
};
FloatingActionButton.fragment = fragment32;
FloatingActionButton.displayName = "VirtualAgentControlTrigger";

// ../internal/src/components/trigger/virtual-agent-control-trigger.tsx
var fragment33 = gql34`
  fragment VirtualAgentControlTriggerFragment on VirtualAgentControlTrigger {
    analytics {
      ... on VirtualAgentControlImpressionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlInteractionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlOutcomeAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlPageViewAnalyticEvent {
        eventName
        eventVersion
        payload
      }
    }
    button {
      ...EGDSButtonFragment
      ...FloatingActionButtonFragment
    }
  }
  ${VirtualAgentEGDSButton.fragment}
  ${FloatingActionButton.fragment}
`;
var VirtualAgentControlTrigger = ({
  onClick,
  data
}) => {
  const {
    button
  } = data;
  const [conversation] = useConversationState();
  const loggerV2 = useLogging(EVENTS);
  const [, conversationMessages] = useMessagesState();
  const {
    trackEvent
  } = useAnalytics();
  const {
    trackEvent: trackRumEvent
  } = useInteractionRumTracker3();
  const {
    publish
  } = useSignalPublisher4();
  useEffect7(() => {
    publish(new VacInitializationSignal("VAC_INITIALIZATION_SIGNAL", null));
    trackEvent(buildClickstreamEventPaylaod("VIRTUAL_AGENT_ICON_VIEWED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type));
  }, [conversation == null ? void 0 : conversation.conversationId]);
  if (!button) {
    return null;
  }
  const handleClick = () => {
    trackEvent(buildClickstreamEventPaylaod("VIRTUAL_AGENT_ICON_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type));
    onClick == null ? void 0 : onClick();
    loggerV2("PERFORMANCE", {
      eventName: "vac.performance" /* PERFORMANCE */,
      eventlevel: SystemEventLevel7.INFO,
      operationName: "VAC_PERF_FabClicked" /* VAC_PERF_FAB_CLICKED */,
      logSource: PREFIX,
      performanceEventTime: (/* @__PURE__ */new Date()).getTime()
    });
    trackRumEvent("action", {
      action: "VAC_PERF_FabClicked" /* VAC_PERF_FAB_CLICKED */,
      attributes: {
        performanceEventTime: (/* @__PURE__ */new Date()).getTime()
      }
    });
  };
  switch (button.__typename) {
    case "UIPrimaryFloatingActionButton":
    case "UISecondaryFloatingActionButton":
      return /* @__PURE__ */React54.createElement(FloatingActionButton, {
        data: button,
        onClick: handleClick,
        unreadMessageCount: conversationMessages == null ? void 0 : conversationMessages.unreadMessageCount
      });
    case "UIPrimaryButton":
    case "UISecondaryButton":
    case "UITertiaryButton":
      return /* @__PURE__ */React54.createElement(VirtualAgentEGDSButton, {
        data: button,
        onClick: handleClick
      });
    default:
      return null;
  }
};
VirtualAgentControlTrigger.fragment = fragment33;
VirtualAgentControlTrigger.displayName = "VirtualAgentControlTrigger";

// ../internal/src/components/typing-indicator/typing-indicator.tsx
import React55 from "react";
import { v4 as uuidv44 } from "uuid";
import { EGDSCard as EGDSCard2 } from "@egds/react-core/cards";
import { EGDSLayoutFlex as EGDSLayoutFlex16, EGDSLayoutFlexItem as EGDSLayoutFlexItem9 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing9 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText14 } from "@egds/react-core/text";
import { Level as Level2 } from "@egds/react-core/utils";
import { LoadingDots } from "@egds/react-virtual-agent-control";
var VirtualAgentControlTypingIndicator = () => {
  const [participantState, activityIndicators] = useParticipantState();
  const containerRef = React55.useRef(null);
  const liveAnnouncementId = React55.useMemo(() => `activityIndicatorAnnounce-${uuidv44()}`, []);
  const activityIndicatorLocalization = use_prefetch_localization_content_default(LocalizationContentKeys.ACTIVITY_INDICATOR);
  React55.useEffect(() => {
    let timerId;
    if (activityIndicators.length) {
      const handleLiveAnnouncement = () => {
        const activityIndicatorAuthorId = activityIndicators[0].messageAuthor;
        const activityIndicatorAuthor = participantState[activityIndicatorAuthorId];
        const localizedAnnouncementString = localizeTemplateLiteral(activityIndicatorLocalization == null ? void 0 : activityIndicatorLocalization.typingIndicatorAccessibilityAnnouncementTemplate, "participantDisplayName", getPartcipantDisplayName(activityIndicatorAuthor));
        ariaAnnounceLive(localizedAnnouncementString, Level2.POLITE, containerRef, liveAnnouncementId);
      };
      timerId = setTimeout(() => {
        handleLiveAnnouncement();
      }, 1e4);
    }
    return () => {
      clearTimeout(timerId);
    };
  }, [activityIndicatorLocalization == null ? void 0 : activityIndicatorLocalization.typingIndicatorAccessibilityAnnouncementTemplate, activityIndicators, activityIndicators.length, liveAnnouncementId, participantState]);
  if (!activityIndicators.length) {
    return null;
  }
  let activityIndicatorMessage = null;
  if (activityIndicators.length > 0 && activityIndicators[0].activeIndicatorMessage) {
    activityIndicatorMessage = activityIndicators[0].activeIndicatorMessage;
  }
  let activityIndicatorEyebrow = null;
  if (activityIndicators.length > 0 && activityIndicators[0].eyebrow) {
    activityIndicatorEyebrow = activityIndicators[0].eyebrow;
  }
  return (
    // TO DO: change author once participant call is added
    /* @__PURE__ */
    React55.createElement(EGDSLayoutFlex16, {
      domRef: containerRef,
      direction: "column"
    }, /* @__PURE__ */React55.createElement(EGDSLayoutFlexItem9, {
      alignSelf: "start"
    }, /* @__PURE__ */React55.createElement(EGDSText14, {
      size: 200,
      spacing: "half",
      theme: "standard"
    }, activityIndicatorEyebrow)), /* @__PURE__ */React55.createElement(EGDSLayoutFlex16, {
      "data-test-id": "text-dots-align",
      role: "status",
      "aria-live": "polite"
    }, /* @__PURE__ */React55.createElement(EGDSCard2, {
      padded: true,
      backgroundTheme: "secondary"
    }, /* @__PURE__ */React55.createElement(EGDSLayoutFlex16, {
      direction: "row",
      space: "two",
      alignItems: "center"
    }, activityIndicatorMessage && /* @__PURE__ */React55.createElement(EGDSLayoutFlexItem9, null, /* @__PURE__ */React55.createElement(EGDSText14, {
      size: 300,
      weight: "regular",
      "data-test-id": "display-message",
      className: "activity-indicator-text"
    }, activityIndicatorMessage)), /* @__PURE__ */React55.createElement(EGDSLayoutFlexItem9, {
      alignSelf: "start",
      maxWidth: "seventy_two"
    }, /* @__PURE__ */React55.createElement(EGDSSpacing9, {
      margin: {
        blockstart: "half"
      }
    }, /* @__PURE__ */React55.createElement("span", null, /* @__PURE__ */React55.createElement(LoadingDots, null))))))))
  );
};

// ../internal/src/hooks/participant-event-markers/use-participant-event-markers.tsx
import { useSignalSubscriber as useSignalSubscriber2 } from "@shared-ui/core-signal-manager/context";
import { useProvidedState as useProvidedState14 } from "@shared-ui/provided-state-context";
import { VacParticipantEventsSignal as VacParticipantEventsSignal2 } from "@shared-ui/retail-signals-virtual-agent";
var useParticipantEventMarkers = () => {
  const [, dispatch] = useProvidedState14();
  const [chatInstance] = useChatInstanceState();
  const {
    participantTypesForEventMarkers,
    showParticipantEventMarker
  } = (chatInstance == null ? void 0 : chatInstance.chatConfig) || {};
  useSignalSubscriber2("VAC_PARTICIPANT_EVENTS" /* VAC_Participant_Event */, VacParticipantEventsSignal2, {
    onSuccess: ({
      payload: payload2
    }) => {
      const {
        participantType,
        conversationId,
        markerString,
        timeStamp,
        participantId
      } = payload2;
      if (participantTypesForEventMarkers.includes(participantType) && showParticipantEventMarker) {
        dispatch({
          type: "ADD_SEPERATOR_MESSAGE" /* ADD_SEPERATOR_MESSAGE */,
          payload: {
            primary: markerString,
            conversationId,
            timeStamp,
            participantId
          }
        });
      }
    }
  });
};

// ../internal/src/hooks/use-heartbeat.tsx
import React56 from "react";
import { useSignalPublisher as useSignalPublisher5 } from "@shared-ui/core-signal-manager/context";
import { VacStartHeartbeatSignal } from "@shared-ui/retail-signals-virtual-agent";
function useHeartbeat() {
  var _a, _b;
  const [chatInstanceState] = useChatInstanceState();
  const [conversation,, conversationContext] = useConversationState();
  const {
    publish
  } = useSignalPublisher5();
  const presenceConfig = (_a = chatInstanceState == null ? void 0 : chatInstanceState.chatConfig) == null ? void 0 : _a.presenceConfig;
  React56.useEffect(() => {
    var _a2;
    if ((presenceConfig == null ? void 0 : presenceConfig.mode) === "HEARTBEAT" /* HEARTBEAT */ && (conversation == null ? void 0 : conversation.state) === "ACTIVE" /* ACTIVE */) {
      const heartbeatPayload = {
        conversationContext,
        interval: presenceConfig.heartbeatIntervalInMillis,
        channelOriginId: (_a2 = chatInstanceState == null ? void 0 : chatInstanceState.chatConfig) == null ? void 0 : _a2.channelOriginId
      };
      publish(new VacStartHeartbeatSignal("VAC_START_HEARTBEAT" /* VAC_StartHeartbeat */, heartbeatPayload));
    }
  }, [conversation == null ? void 0 : conversation.state, conversationContext, presenceConfig, publish, (_b = chatInstanceState == null ? void 0 : chatInstanceState.chatConfig) == null ? void 0 : _b.channelOriginId]);
  return [];
}

// ../internal/src/hooks/use-notification-sound.tsx
import { useEffect as useEffect8, useRef } from "react";
import { useSignalSubscriber as useSignalSubscriber3 } from "@shared-ui/core-signal-manager/context";
import { SystemEventLevel as SystemEventLevel8 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState15 } from "@shared-ui/provided-state-context";
import { VacNotificationSoundSignal } from "@shared-ui/retail-signals-virtual-agent";
var useAudioNotification = vacId => {
  var _a, _b, _c, _d;
  const [state] = useProvidedState15();
  const ref = useRef();
  const loggerV2 = useLogging(EVENTS);
  const playSoundRef = useRef(true);
  const playSound = async audioUrl => {
    try {
      const audio = new Audio(audioUrl);
      await audio.play();
    } catch (error) {
      loggerV2("RUNTIME", {
        eventName: "vac.runtime" /* RUNTIME */,
        eventlevel: SystemEventLevel8.ERROR,
        operationName: "VAC_NotificationAudioError" /* VAC_NOTIFICATION_AUDIO_ERROR */,
        logSource: PREFIX
      });
    }
  };
  useEffect8(() => {
    var _a2, _b2, _c2, _d2;
    const chatConfig = (_b2 = (_a2 = state.chatInstancesState) == null ? void 0 : _a2[vacId]) == null ? void 0 : _b2.chatConfig;
    const settingMenuItemState = (_d2 = (_c2 = state.chatInstancesState) == null ? void 0 : _c2[vacId]) == null ? void 0 : _d2.settingMenuItemState;
    ref.current = {
      chatConfig,
      settingMenuItemState
    };
  }, [(_b = (_a = state.chatInstancesState) == null ? void 0 : _a[vacId]) == null ? void 0 : _b.chatConfig, (_d = (_c = state.chatInstancesState) == null ? void 0 : _c[vacId]) == null ? void 0 : _d.settingMenuItemState]);
  const handleNewMessage = (audioUrl, enableSound, isSoundMuted) => {
    if (!enableSound || isSoundMuted) {
      return;
    }
    if (audioUrl && playSoundRef.current) {
      playSound(audioUrl);
      playSoundRef.current = false;
      setTimeout(() => {
        playSoundRef.current = true;
      }, 1e3);
    }
  };
  useSignalSubscriber3("VAC_NOTIFICATION_SOUND" /* VAC_NotificationSound */, VacNotificationSoundSignal, {
    onSuccess: () => {
      var _a2, _b2, _c2, _d2;
      const {
        audioUrl,
        enableSound
      } = ((_b2 = (_a2 = ref.current) == null ? void 0 : _a2.chatConfig) == null ? void 0 : _b2.notificationConfig) || {};
      const isSoundMuted = (_d2 = (_c2 = ref.current) == null ? void 0 : _c2.settingMenuItemState) == null ? void 0 : _d2.toggleSound;
      handleNewMessage(audioUrl, enableSound, isSoundMuted);
    }
  });
  return {
    ref,
    handleNewMessage
  };
};
var use_notification_sound_default = useAudioNotification;

// ../internal/src/providers/dialog-provider/dialog-provider-config.tsx
import React57 from "react";
import * as ReactDOM from "react-dom";
var CustomLayer = props => {
  const {
    id,
    isOpen,
    children,
    rootRef
  } = props;
  if (!isOpen) {
    return null;
  }
  if (!rootRef.current) {
    return null;
  }
  const layer = /* @__PURE__ */React57.createElement("div", {
    role: "dialog",
    className: "uitk-dialog-layer uitk-dialog-layer-responsive layer-overlay-active",
    id
  }, children || null);
  return ReactDOM.createPortal(layer, rootRef.current, `layer-${id}`);
};
function getDialogProviderConfig(rootRef) {
  return {
    useDialogControl: (id, dialogQueryKey, isOpen, setIsOpen) => {
      const actions = {
        openDialog: () => setIsOpen(true),
        closeDialog: () => setIsOpen(false),
        toggleDialog: () => setIsOpen(!isOpen)
      };
      return {
        ComponentFactory: props => {
          const {
            children
          } = props;
          return /* @__PURE__ */React57.createElement(CustomLayer, {
            id,
            isOpen,
            rootRef
          }, children);
        },
        actions
      };
    }
  };
}

// ../internal/src/store/orchestrator-state/reducers.ts
import isEqual2 from "lodash.isequal";

// ../internal/src/store/orchestrator-state/orchestrator-state.ts
var initOrchestratorState = {
  vacConfig: {
    authToken: "",
    websocket: null
  },
  initializationStatus: "RUNNING" /* RUNNING */,
  lastWebsocketDisconnectTime: null
};

// src/components/chat/chat.tsx
import { TIMERS as TIMERS7, useTimers as useTimers7 } from "@shared-ui/retail-virtual-agent-providers";

// src/utils/signals/subscriber.tsx
import { useSignalSubscriber as useSignalSubscriber4 } from "@shared-ui/core-signal-manager/context";
import { VacDynamicCardSignal as VacDynamicCardSignal3, VacSendMessageSignal } from "@shared-ui/retail-signals-virtual-agent";

// src/hooks/use-send-message.tsx
import React58 from "react";
import { v4 as uuidv45 } from "uuid";
import { useMutation } from "@shared-ui/apollo-client-wrapper";
import { useBexApi as useBexApi4 } from "@shared-ui/bex-api-context";
import { useInteractionRumTracker as useInteractionRumTracker4 } from "@shared-ui/core-real-user-monitoring/context";
import { useSignalPublisher as useSignalPublisher6 } from "@shared-ui/core-signal-manager/context";
import { SystemEventLevel as SystemEventLevel10 } from "@shared-ui/logger-context";
import { VacChatMessageSendFailedSignal, VacChatMessageSentSignal } from "@shared-ui/retail-signals-virtual-agent";
import { TIMERS as TIMERS2, useTimers as useTimers2 } from "@shared-ui/retail-virtual-agent-providers";

// src/events.ts
import { SystemEvent as SystemEvent3, SystemEventLevel as SystemEventLevel9 } from "@shared-ui/logger-context";
var PREFIX2 = "VacLogging";
var COMPONENT_NAME2 = "GENERIC_CHAT";
var ACTION = "action";
var EVENTS2 = {
  INITIALIZATION_OVERALL: new SystemEvent3(SystemEventLevel9.INFO, `${PREFIX2}.${COMPONENT_NAME2}.initialization.overall`),
  INITIALIZATION_TRIGGER: new SystemEvent3(SystemEventLevel9.INFO, `${PREFIX2}.${COMPONENT_NAME2}.initialization.trigger`),
  INITIALIZATION_CHAT: new SystemEvent3(SystemEventLevel9.INFO, `${PREFIX2}.${COMPONENT_NAME2}.initialization.chat`),
  INITIALIZATION_CONNECTED: new SystemEvent3(SystemEventLevel9.INFO, `${PREFIX2}.${COMPONENT_NAME2}.initialization.connected`),
  INITIALIZATION_MESSAGE: new SystemEvent3(SystemEventLevel9.INFO, `${PREFIX2}.${COMPONENT_NAME2}.initialization.message`),
  API_SUCCESS: new SystemEvent3(SystemEventLevel9.INFO, `${PREFIX2}.${COMPONENT_NAME2}.api.success`),
  API_ERROR: new SystemEvent3(SystemEventLevel9.ERROR, `${PREFIX2}.${COMPONENT_NAME2}.api.error`),
  PERFORMANCE: new SystemEvent3(SystemEventLevel9.INFO, `${PREFIX2}.${COMPONENT_NAME2}.performance`),
  RUNTIME: new SystemEvent3(SystemEventLevel9.ERROR, `${PREFIX2}.${COMPONENT_NAME2}.runtime`)
};

// src/service/send-message/send-message.tsx
import { gql as gql35 } from "@shared-ui/apollo-client-wrapper";
var __SendMessageMutation = gql35`
  mutation VirtualAgentSendMessageMutation(
    $authenticationConfig: AuthenticationConfigInput
    $context: ContextInput!
    $message: VirtualAgentControlMessageInput!
    $conversationContext: ConversationContextInput!
  ) {
    sendMessage(authenticationConfig: $authenticationConfig, context: $context, messageInput: $message, conversationContext: $conversationContext) {
      deliveryStatus
      umid
      messageId
    }
  }
`;

// src/hooks/use-send-message.tsx
function dispatchEventByMessageType(messageType, umid, updatedMessage, dispatch) {
  switch (messageType) {
    case "FILE" /* FILE */:
      dispatch({
        type: "UPDATE_OUTBOUND_MESSAGE_ELEMENT" /* UPDATE_OUTBOUND_MESSAGE_ELEMENT */,
        payload: {
          umid,
          outboundMessageData: updatedMessage
        }
      });
      break;
    default:
      dispatch({
        type: "UPDATE_OUTBOUND_MESSAGE" /* UPDATE_OUTBOUND_MESSAGE */,
        payload: {
          umid,
          outboundMessageData: updatedMessage
        }
      });
  }
}
function doSendMessage(sendMessageMutation, mutationParams, dispatch, publishSendMessage, messageType, messageContent, isRetry = false) {
  sendMessageMutation({
    variables: mutationParams
  }).then(result => {
    const {
      sendMessage
    } = result.data;
    if (!sendMessage) {
      return;
    }
    const {
      deliveryStatus,
      umid,
      messageId
    } = sendMessage;
    const updatedMessage = {
      messageId,
      status: deliveryStatus,
      deliveryFailed: isRetry,
      messageContent
    };
    dispatchEventByMessageType(messageType, umid, updatedMessage, dispatch);
    publishSendMessage(messageId, isRetry);
  }, /* istanbul ignore next -- @preserve */
  error => {
    const {
      umid
    } = mutationParams.message;
    const updatedMessage = {
      status: messageContent == null ? void 0 : messageContent.statusNotDelivered,
      messageContent
    };
    dispatchEventByMessageType(messageType, umid, updatedMessage, dispatch);
    publishSendMessage();
  });
}
function useSendMessage() {
  const {
    context
  } = useBexApi4();
  const handleCreateConversation = useOperation("CREATE_CONVERSATION" /* CREATE_CONVERSATION */);
  const [conversation, conversationDispatch, conversationContext] = useConversationState();
  const [,, _messagesDispatch] = useMessagesState();
  const currentActiveConversationId = React58.useRef();
  const currentActiveParticipantId = React58.useRef();
  const [authenticationConfig] = useAuthenticationConfig();
  const {
    publish
  } = useSignalPublisher6();
  const [sendMessage] = useMutation(__SendMessageMutation);
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  const {
    outboundMessageContextAccessibilityLabel
  } = use_prefetch_localization_content_default(LocalizationContentKeys.PARTICIPANT) || {};
  const {
    startTimer,
    getTime
  } = useTimers2();
  const {
    trackEvent
  } = useInteractionRumTracker4();
  const loggerV2 = useLogging(EVENTS2);
  const messagesDispatch = ({
    type,
    payload: payload2
  }) => {
    if (currentActiveConversationId.current) {
      return _messagesDispatch({
        type,
        payload: {
          ...payload2,
          conversationId: currentActiveConversationId.current
        }
      });
    }
    return _messagesDispatch({
      type,
      payload: payload2
    });
  };
  const sendCreateConversationRequest = async () => {
    try {
      conversationDispatch({
        type: "SET_FOOTER_STATE" /* SET_FOOTER_STATE */,
        payload: {
          chatFooterState: {
            chatInputStatus: "DISABLE" /* DISABLE */
          }
        }
      });
      const result = await (handleCreateConversation == null ? void 0 : handleCreateConversation());
      return result;
    } finally {
      conversationDispatch({
        type: "SET_FOOTER_STATE" /* SET_FOOTER_STATE */,
        payload: {
          chatFooterState: {
            chatInputStatus: "ENABLE" /* ENABLE */
          }
        }
      });
    }
  };
  const publishSendMessage = React58.useCallback((messageId, isRetry = false) => {
    let event;
    if (messageId) {
      event = new VacChatMessageSentSignal("VAC_CHAT_MESSAGE_SENT" /* VAC_ChatMsgSent */, {
        messageId,
        isRetry
      });
      const timestamp = getTime(TIMERS2.SEND_MESSAGE, true);
      loggerV2("API_SUCCESS", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel10.INFO,
        operationName: "VirtualAgentSendMessageMutation" /* VAC_SEND_MESSAGE_MUTATION */,
        logSource: PREFIX2,
        loadingDuration: timestamp
      });
      trackEvent(ACTION, {
        action: `${"VirtualAgentSendMessageMutation" /* VAC_SEND_MESSAGE_MUTATION */}-success`,
        attributes: {
          loadingDuration: timestamp
        }
      });
    } else {
      event = new VacChatMessageSendFailedSignal("VAC_CHAT_MESSAGE_SEND_FAILED" /* VAC_ChatMsgSendFailed */, null);
      const timestamp = getTime(TIMERS2.SEND_MESSAGE, true);
      loggerV2("API_ERROR", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel10.ERROR,
        operationName: "VirtualAgentSendMessageMutation" /* VAC_SEND_MESSAGE_MUTATION */,
        logSource: PREFIX2,
        loadingDuration: timestamp
      });
      trackEvent(ACTION, {
        action: `${"VirtualAgentSendMessageMutation" /* VAC_SEND_MESSAGE_MUTATION */}-failure`,
        attributes: {
          loadingDuration: timestamp
        }
      });
    }
    publish(event);
  }, [publish, loggerV2]);
  const handleSend = React58.useCallback(async messageData => {
    var _a, _b;
    let isCreateConversationCalled = false;
    startTimer(TIMERS2.SEND_MESSAGE);
    if ((conversation == null ? void 0 : conversation.state) === "CLOSED" /* CLOSED */) {
      const results = await (sendCreateConversationRequest == null ? void 0 : sendCreateConversationRequest());
      if ((_b = (_a = results == null ? void 0 : results.data) == null ? void 0 : _a.virtualAgentControlCreateConversation) == null ? void 0 : _b.identifiers) {
        const {
          conversationId,
          participantId
        } = results.data.virtualAgentControlCreateConversation.identifiers;
        currentActiveConversationId.current = conversationId;
        currentActiveParticipantId.current = participantId;
        isCreateConversationCalled = true;
      }
    }
    const umid =
    // eslint-disable-next-line no-nested-ternary
    (messageData == null ? void 0 : messageData.messageType) === "FILE" /* FILE */ ? messageData.umid : messageData.operation === "retry" ? messageData.umid : uuidv45();
    const currentConversationContext = isCreateConversationCalled ? {
      ...conversationContext,
      participantId: currentActiveParticipantId.current,
      conversationId: currentActiveConversationId.current
    } : conversationContext;
    const mutationParams = {
      ...((authenticationConfig == null ? void 0 : authenticationConfig.channelOriginId) && {
        authenticationConfig
      }),
      context,
      message: {
        text: messageData == null ? void 0 : messageData.message,
        ...((messageData == null ? void 0 : messageData.attachment) && {
          attachments: messageData.attachment
        }),
        ...((messageData == null ? void 0 : messageData.payload) && {
          payload: messageData.payload
        }),
        ...((messageData == null ? void 0 : messageData.intent) && {
          intent: messageData.intent
        }),
        ...((messageData == null ? void 0 : messageData.messageType) && {
          messageType: messageData.messageType
        }),
        ...((messageData == null ? void 0 : messageData.messageTriggerLocation) && {
          messageTriggerLocation: messageData.messageTriggerLocation
        }),
        ...((messageData == null ? void 0 : messageData.flags) && {
          flags: messageData.flags
        }),
        ...((messageData == null ? void 0 : messageData.attributes) && {
          attributes: messageData.attributes
        }),
        umid
      },
      conversationContext: currentConversationContext
    };
    if (messageData.messageType === "FILE" /* FILE */) {
      if (messageData.operation === "retry") {
        dispatchEventByMessageType("FILE" /* FILE */, umid, {
          messageId: umid,
          status: messageContent == null ? void 0 : messageContent.statusSending,
          deliveryFailed: true,
          messageContent
        }, messagesDispatch);
        messagesDispatch({
          type: "SET_QUICK_REPLIES" /* SET_QUICK_REPLIES */,
          payload: {
            message: [],
            messageId: null,
            analytics: null
          }
        });
        doSendMessage(sendMessage, mutationParams, messagesDispatch, publishSendMessage, "FILE" /* FILE */, messageContent, true);
      } else {
        doSendMessage(sendMessage, mutationParams, messagesDispatch, publishSendMessage, "FILE" /* FILE */, messageContent);
      }
    } else if (mutationParams.message.text) {
      if (messageData.operation === "retry") {
        dispatchEventByMessageType("TEXT" /* TEXT */, umid, {
          messageId: umid,
          status: messageContent == null ? void 0 : messageContent.statusSending,
          deliveryFailed: true,
          messageContent
        }, messagesDispatch);
        messagesDispatch({
          type: "SET_QUICK_REPLIES" /* SET_QUICK_REPLIES */,
          payload: {
            message: [],
            messageId: null,
            analytics: null
          }
        });
        doSendMessage(sendMessage, mutationParams, messagesDispatch, publishSendMessage, "", messageContent, true);
      } else {
        const outboundMessage = createOutboundTextMessage(messageData == null ? void 0 : messageData.message, messageContent == null ? void 0 : messageContent.statusSending, umid, outboundMessageContextAccessibilityLabel);
        const outboundMessageGroup = createOutboundMessageElementGroup([outboundMessage]);
        messagesDispatch({
          type: "ADD_MESSAGE" /* ADD_MESSAGE */,
          payload: {
            message: outboundMessageGroup,
            messageContent
          }
        });
        messagesDispatch({
          type: "SET_QUICK_REPLIES" /* SET_QUICK_REPLIES */,
          payload: {
            message: [],
            messageId: null,
            analytics: null
          }
        });
        doSendMessage(sendMessage, mutationParams, messagesDispatch, publishSendMessage, "", messageContent);
      }
    }
  }, [authenticationConfig, context, conversationContext, sendMessage, messagesDispatch, publishSendMessage, conversation == null ? void 0 : conversation.state]);
  return [handleSend];
}

// src/utils/signals/subscriber.tsx
var VacSignalSubscriber = () => {
  const [handleSend] = useSendMessage();
  useSignalSubscriber4("VAC_SEND_MESSAGE", VacSendMessageSignal, {
    onSuccess: signal => {
      const {
        messageData
      } = signal.payload;
      handleSend == null ? void 0 : handleSend(messageData);
    }
  });
  useSignalSubscriber4("VAC_DYNAMIC_CARD_EVENT", VacDynamicCardSignal3, {
    onSuccess: signal => {
      const {
        eventId,
        payload: payload2,
        message
      } = signal.payload;
      if (eventId === "VAC_DCF_POSTBACK") {
        const messageData = {
          message,
          payload: payload2,
          messageType: "POSTBACK"
        };
        handleSend == null ? void 0 : handleSend(messageData);
      }
    }
  });
  return null;
};

// src/hooks/use-chat.tsx
import { useQuery } from "@shared-ui/apollo-client-wrapper";
import { useBexApi as useBexApi5 } from "@shared-ui/bex-api-context";

// src/service/chat/chat.tsx
import { gql as gql72 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/messages/inbound-message-group/inbound-message-group.tsx
import React92, { useEffect as useEffect9 } from "react";
import { v4 as uuidv410 } from "uuid";
import { EGDSLayoutFlex as EGDSLayoutFlex33, EGDSLayoutFlexItem as EGDSLayoutFlexItem21 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing21 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText27 } from "@egds/react-core/text";
import { Level as Level3 } from "@egds/react-core/utils";
import { gql as gql65 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/messages/inbound-message/inbound-message.tsx
import React91 from "react";
import { gql as gql64 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/messages/inbound-message/components/inbound-card-message.tsx
import React59 from "react";
import { EGDSCard as EGDSCard3 } from "@egds/react-core/cards";
import { EGDSLayoutFlex as EGDSLayoutFlex17, EGDSLayoutFlexItem as EGDSLayoutFlexItem10 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing10 } from "@egds/react-core/spacing";
import { EGDSHeading as EGDSHeading6, EGDSText as EGDSText15 } from "@egds/react-core/text";
import { gql as gql36 } from "@shared-ui/apollo-client-wrapper";
var fragment34 = gql36`
  fragment VirtualAgentControlInboundCardMessageFragment on VirtualAgentControlInboundCardMessage {
    eyebrow
    metadata {
      ... on VirtualAgentControlMessageMetadata {
        timeStamp
        messageId
        messageContextAccessibilityLabel
        newMessageAccessibilityAnnouncement
      }
    }
    stateUpdates {
      chatInput {
        interactionMode
        chatInputStatus
      }
    }
    header
    primary
  }
`;
var VirtualAgentControlInboundCardMessage = ({
  data
}) => {
  const {
    primary,
    header
  } = data;
  return /* @__PURE__ */React59.createElement(EGDSLayoutFlex17, {
    direction: "column"
  }, /* @__PURE__ */React59.createElement(EGDSLayoutFlexItem10, {
    alignSelf: "start"
  }, /* @__PURE__ */React59.createElement(EGDSSpacing10, {
    margin: {
      blockstart: "half"
    }
  }, /* @__PURE__ */React59.createElement(EGDSCard3, {
    padded: true,
    border: true
  }, header && /* @__PURE__ */React59.createElement(EGDSHeading6, {
    size: 6,
    tag: "h2"
  }, header), /* @__PURE__ */React59.createElement(EGDSText15, {
    size: 300,
    spacing: "two"
  }, primary)))));
};
VirtualAgentControlInboundCardMessage.fragment = fragment34;
VirtualAgentControlInboundCardMessage.displayName = "VirtualAgentControlInboundCardMessage";

// ../internal/src/components/messages/inbound-message/components/inbound-dynamic-card-message.tsx
import React86 from "react";
import { EGDSCard as EGDSCard6 } from "@egds/react-core/cards";
import { EGDSCarousel as EGDSCarousel2 } from "@egds/react-core/carousel";
import { EGDSLayoutFlex as EGDSLayoutFlex29, EGDSLayoutFlexItem as EGDSLayoutFlexItem17 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing18 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText24 } from "@egds/react-core/text";
import { gql as gql60 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/providers/dynamic-card/vac-dynamic-card-context.tsx
import React61 from "react";
import { v4 as uuidv46 } from "uuid";
import { useDialog as useDialog8 } from "@shared-ui/dialog-context";

// ../internal/src/components/webview/webview.tsx
import React60 from "react";
import { EGDSSheet as EGDSSheet3, EGDSSheetContent as EGDSSheetContent3, EGDSSheetTransition as EGDSSheetTransition3 } from "@egds/react-core/sheet";
import { EGDSSpacing as EGDSSpacing11 } from "@egds/react-core/spacing";
import { EGDSToolbar as EGDSToolbar5 } from "@egds/react-core/toolbar";
var Webview = props => {
  const {
    isOpen,
    onCloseCallback,
    action,
    embedded,
    DialogComponent
  } = props;
  const handleSend = useOperation("SEND_MESSAGE" /* SEND_MESSAGE */);
  const attachmentStringMap = use_prefetch_localization_content_default(LocalizationContentKeys.DIALOG);
  const {
    uri,
    toolbar: {
      primary
    },
    srcDoc
  } = action;
  const [title, setTitle] = React60.useState(primary);
  const handleMessage = React60.useCallback(event => {
    const {
      data
    } = event;
    if (data.source === "webview" || data.source === "ChatRichResponse") {
      if (data.title) {
        setTitle(data.title);
      }
      if (data.close) {
        onCloseCallback(false);
      }
      if (data.payload) {
        const message = {
          messageType: data.messageType,
          payload: data.payload
        };
        handleSend == null ? void 0 : handleSend(message);
      }
    }
  }, [onCloseCallback, handleSend, setTitle]);
  React60.useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);
  const renderWebviewChildren = React60.useCallback(() => {
    var _a;
    return /* @__PURE__ */React60.createElement(React60.Fragment, null, /* @__PURE__ */React60.createElement(EGDSToolbar5, {
      navigationContent: {
        onClick: () => {
          onCloseCallback(false);
        },
        navIconLabel: (_a = attachmentStringMap == null ? void 0 : attachmentStringMap.closeWebviewWindow) != null ? _a : "Close webview, Return to chat window"
      },
      toolbarTitle: title
    }), /* @__PURE__ */React60.createElement(EGDSSpacing11, {
      padding: "four"
    }, /* @__PURE__ */React60.createElement(EGDSSheetContent3, {
      padded: false
    }, /* @__PURE__ */React60.createElement("iframe", {
      title: primary,
      style: {
        height: "100%",
        width: "100%",
        border: 0
      },
      src: uri,
      srcDoc
    }))));
  }, [attachmentStringMap == null ? void 0 : attachmentStringMap.closeWebviewWindow, title, primary, uri, srcDoc, onCloseCallback]);
  const sheetProps = embedded ? {
    type: "centered",
    height: 1200
  } : {
    type: "side"
  };
  return /* @__PURE__ */React60.createElement(EGDSSheetTransition3, {
    isVisible: isOpen
  }, /* @__PURE__ */React60.createElement(DialogComponent, null, /* @__PURE__ */React60.createElement(EGDSSheet3, {
    ...sheetProps,
    isVisible: isOpen,
    triggerRef: null
  }, renderWebviewChildren())));
};

// ../internal/src/providers/dynamic-card/vac-dynamic-card-context.tsx
var WebviewDialog = ({
  webview,
  webviewUriAppender,
  dialogId
}) => {
  var _a, _b;
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog8(dialogId);
  const [chatInstanceState] = useChatInstanceState();
  const embedded = (chatInstanceState == null ? void 0 : chatInstanceState.embedded) || false;
  if (!webview || !isDialogOpen) {
    return null;
  }
  const rawUrl = (_a = webview.url) == null ? void 0 : _a.value;
  const finalUrl = rawUrl ? `${rawUrl}${webviewUriAppender != null ? webviewUriAppender : ""}` : void 0;
  const hasWebviewContent = finalUrl || webview.srcDoc;
  if (!hasWebviewContent) {
    return null;
  }
  const action = {
    toolbar: {
      primary: "Webview"
    },
    uri: finalUrl,
    srcDoc: (_b = webview == null ? void 0 : webview.srcDoc) != null ? _b : void 0
  };
  return /* @__PURE__ */React61.createElement(Webview, {
    isOpen: isDialogOpen,
    onCloseCallback: () => {
      dialogActions.closeDialog();
    },
    action,
    DialogComponent: React61.memo(DialogComponent),
    embedded
  });
};
var createVacDynamicCardContext = ({
  webviewUriAppender,
  source = null
} = {}) => ({
  useWebview: webview => {
    const dialogId = React61.useMemo(() => `vac-declarative-cards-webview-${uuidv46()}`, []);
    const [, dialogActions] = useDialog8(dialogId);
    const webviewContext = React61.useMemo(() => ({
      WebviewComponent: /* @__PURE__ */React61.createElement(WebviewDialog, {
        webview,
        webviewUriAppender,
        dialogId
      }),
      openWebview: () => {
        dialogActions.openDialog();
      }
    }), [dialogActions, dialogId, webview]);
    return webviewContext;
  },
  useActionDisabled: () => source === "HISTORY" /* HISTORY */
});

// ../internal/src/components/declarative-cards/dynamic-card.tsx
import React80 from "react";
import { EGDSCard as EGDSCard4, EGDSCardContentSection, EGDSCardLink } from "@egds/react-core/cards";
import { EGDSLayoutFlex as EGDSLayoutFlex27, EGDSLayoutFlexItem as EGDSLayoutFlexItem15 } from "@egds/react-core/layout-flex";
import { EGDSLayoutGrid, EGDSLayoutGridItem } from "@egds/react-core/layout-grid";
import { gql as gql54 } from "@shared-ui/apollo-client-wrapper";
import { useSignalPublisher as useSignalPublisher7 } from "@shared-ui/core-signal-manager/context";
import { VacDynamicCardSignal as VacDynamicCardSignal4 } from "@shared-ui/retail-signals-virtual-agent";

// ../internal/src/components/declarative-cards/dynamic-card-composition.tsx
import React77 from "react";
import { EGDSSpacing as EGDSSpacing14 } from "@egds/react-core/spacing";
import { gql as gql51 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-rating.tsx
import React65 from "react";
import { EGDSRating } from "@egds/react-core/rating";
import { YelpRating } from "@egds/react-virtual-agent-control";
import { gql as gql39 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/egds-badge/egds-badge.tsx
import React63 from "react";
import { gql as gql38 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/egds-badge/egds-standard-badge.tsx
import React62 from "react";
import { EGDSBadgeSize, EGDSBadgeStandard, EGDSBadgeStandardThemes } from "@egds/react-core/badge";
import { gql as gql37 } from "@shared-ui/apollo-client-wrapper";
var __EGDSStandardBadgeFragment = gql37`
  fragment EGDSStandardBadgeFragment on EGDSStandardBadge {
    accessibility
    graphic {
      ...UIGraphicFragment
    }
    text
    size
    theme
  }

  ${UIGraphic.fragment}
`;
var EGDSStandardBadge = ({
  data
}) => {
  const {
    graphic,
    text,
    theme,
    accessibility,
    size
  } = data;
  if (!text) {
    return null;
  }
  return /* @__PURE__ */React62.createElement(EGDSBadgeStandard, {
    a11yText: accessibility != null ? accessibility : void 0,
    text,
    size: size ? EGDSBadgeSize[size.toUpperCase()] : EGDSBadgeSize.LARGE,
    theme: theme ? EGDSBadgeStandardThemes[theme.toUpperCase()] : "positive"
  }, graphic && /* @__PURE__ */React62.createElement(UIGraphic, {
    data: graphic
  }));
};
EGDSStandardBadge.fragment = __EGDSStandardBadgeFragment;
EGDSStandardBadge.displayName = "EGDSStandardBadge";

// ../internal/src/components/common/egds-badge/egds-badge.tsx
var __EGDSBadgeFragment = gql38`
  fragment EGDSBadgeFragment on EGDSBadge {
    ...EGDSStandardBadgeFragment
  }

  ${EGDSStandardBadge.fragment}
`;
var VirtualAgentEGDSBadge = ({
  data
}) => {
  switch (data.__typename) {
    case "EGDSStandardBadge":
      return /* @__PURE__ */React63.createElement(EGDSStandardBadge, {
        data
      });
    default:
      return null;
  }
};
VirtualAgentEGDSBadge.fragment = __EGDSBadgeFragment;
VirtualAgentEGDSBadge.displayName = "VirtualAgentEGDSBadge";

// ../internal/src/components/declarative-cards/compositions/badge-rating/badge-rating.tsx
import React64 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex18 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing12 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText16 } from "@egds/react-core/text";
var BadgeRating = props => {
  const {
    badge,
    primary,
    secondary
  } = props;
  return /* @__PURE__ */React64.createElement(EGDSLayoutFlex18, {
    space: "two"
  }, /* @__PURE__ */React64.createElement(EGDSSpacing12, {
    padding: {
      blockstart: "one"
    }
  }, /* @__PURE__ */React64.createElement("div", null, /* @__PURE__ */React64.createElement(VirtualAgentEGDSBadge, {
    data: badge
  }))), (primary || secondary) && /* @__PURE__ */React64.createElement(EGDSLayoutFlex18, {
    direction: "column"
  }, /* @__PURE__ */React64.createElement(EGDSText16, {
    size: 200,
    weight: "bold"
  }, primary), /* @__PURE__ */React64.createElement(EGDSText16, {
    size: 200
  }, secondary)));
};

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-rating.tsx
var fragment35 = gql39`
  fragment VirtualAgentControlRatingFragment on VirtualAgentControlRating {
    badge {
      ...EGDSBadgeFragment
    }
    icon
    primary
    rating
    secondary
  }
  ${VirtualAgentEGDSBadge.fragment}
`;
var VirtualAgentControlRating = ({
  data
}) => {
  const {
    badge,
    primary,
    rating,
    secondary,
    icon
  } = data;
  if (badge) {
    return /* @__PURE__ */React65.createElement(BadgeRating, {
      badge,
      primary: primary != null ? primary : "",
      secondary: secondary != null ? secondary : ""
    });
  }
  if (icon && rating) {
    return /* @__PURE__ */React65.createElement(YelpRating, {
      rating
    });
  }
  return /* @__PURE__ */React65.createElement(EGDSRating, {
    rating: rating.toPrecision()
  });
};
VirtualAgentControlRating.fragment = fragment35;
VirtualAgentControlRating.displayName = "VirtualAgentControlRating";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-step-indicator.tsx
import React66 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex19 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing13 } from "@egds/react-core/spacing";
import { StepIndicator } from "@egds/react-core/step-indicator";
import { EGDSText as EGDSText17 } from "@egds/react-core/text";
import { gql as gql40 } from "@shared-ui/apollo-client-wrapper";
var fragment36 = gql40`
  fragment VirtualAgentControlStepIndicatorFragment on VirtualAgentControlStepIndicator {
    accessibilityLabel
    activeStep
    steps {
      icon {
        id
      }
      primary
      secondary
    }
  }
`;
var VirtualAgentControlStepIndicator = ({
  data
}) => {
  const fontSize = 200;
  const mappedSteps = data.steps.map(step => {
    var _a;
    const modedContent = /* @__PURE__ */React66.createElement(EGDSText17, {
      size: fontSize
    }, step.secondary);
    return {
      label: step.primary || "",
      content: modedContent || "",
      icon: ((_a = step.icon) == null ? void 0 : _a.id) || ""
    };
  });
  return /* @__PURE__ */React66.createElement(EGDSLayoutFlex19, {
    space: "two"
  }, /* @__PURE__ */React66.createElement(EGDSSpacing13, {
    padding: {
      blockstart: "one"
    }
  }, /* @__PURE__ */React66.createElement("div", null, /* @__PURE__ */React66.createElement(StepIndicator, {
    steps: [...(mappedSteps != null ? mappedSteps : [])],
    activeStep: data.activeStep,
    stepLength: "natural",
    direction: "vertical",
    stepIndicatorSize: "small"
  }))));
};
VirtualAgentControlStepIndicator.fragment = fragment36;
VirtualAgentControlStepIndicator.displayName = "VirtualAgentControlStepIndicator";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-progress-bar.tsx
import React67 from "react";
import { EGDSProgressBar } from "@egds/react-core/progress-bar";
import { gql as gql41 } from "@shared-ui/apollo-client-wrapper";
var fragment37 = gql41`
  fragment VirtualAgentControlProgressBarFragment on VirtualAgentControlProgressBar {
    progressBar {
      ... on EGDSProgressBar {
        accessibilityLabel
        percent
        progressDescription
        title
      }
    }
    theme
  }
`;
var VirtualAgentControlProgressBarThemeMapper = {
  ["MUTED" /* MUTED */]: "muted",
  ["POSITIVE" /* POSITIVE */]: "positive",
  ["STANDARD" /* STANDARD */]: "standard"
};
var VirtualAgentControlProgressBar = ({
  data
}) => {
  const {
    progressBar,
    theme
  } = data;
  const {
    percent,
    accessibilityLabel,
    title,
    progressDescription
  } = progressBar;
  return /* @__PURE__ */React67.createElement(EGDSProgressBar, {
    title: title || "",
    progressDescription: progressDescription || "",
    percent,
    accessibilityLabel,
    theme: VirtualAgentControlProgressBarThemeMapper[theme != null ? theme : "STANDARD" /* STANDARD */]
  });
};
VirtualAgentControlProgressBar.fragment = fragment37;
VirtualAgentControlProgressBar.displayName = "VirtualAgentControlProgressBar";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-more-info-trigger.tsx
import React69 from "react";
import { v4 as uuidv47 } from "uuid";
import { EGDSIconSize as EGDSIconSize5 } from "@egds/react-core/icons";
import { EGDSLayoutFlex as EGDSLayoutFlex20 } from "@egds/react-core/layout-flex";
import { EGDSMoreInfoTrigger } from "@egds/react-core/more-info-trigger";
import { EGDSText as EGDSText18 } from "@egds/react-core/text";
import { gql as gql43 } from "@shared-ui/apollo-client-wrapper";
import { useDialog as useDialog9 } from "@shared-ui/dialog-context";

// ../internal/src/components/declarative-cards/compositions/bottom-sheet/virtual-agent-control-bottom-sheet.tsx
import React68 from "react";
import { EGDSSheet as EGDSSheet4, EGDSSheetContent as EGDSSheetContent4, EGDSSheetTransition as EGDSSheetTransition4 } from "@egds/react-core/sheet";
import { EGDSToolbar as EGDSToolbar6 } from "@egds/react-core/toolbar";
import { gql as gql42 } from "@shared-ui/apollo-client-wrapper";
var fragment38 = gql42`
  fragment VirtualAgentControlBottomSheetFragment on VirtualAgentControlBottomSheet {
    close {
      ...EGDSButtonFragment
    }
    text {
      ...DynamicCardTextFragment
    }
  }
  ${VirtualAgentEGDSButton.fragment}
  ${DynamicCardText.fragment}
`;
var VirtualAgentControlBottomSheet = ({
  closeDialog,
  DialogComponent,
  visible,
  triggerRef,
  data
}) => {
  const {
    text,
    close: {
      primary
    }
  } = data;
  return /* @__PURE__ */React68.createElement("div", null, /* @__PURE__ */React68.createElement(EGDSSheetTransition4, {
    isVisible: visible
  }, /* @__PURE__ */React68.createElement(DialogComponent, null, /* @__PURE__ */React68.createElement(EGDSSheet4, {
    type: "bottom",
    isVisible: visible,
    triggerRef,
    autoFocus: true
  }, /* @__PURE__ */React68.createElement(EGDSToolbar6, {
    navigationContent: {
      navIconLabel: primary != null ? primary : "Close",
      onClick: () => closeDialog()
    }
  }), /* @__PURE__ */React68.createElement(EGDSSheetContent4, null, text == null ? void 0 : text.map((value, index) => /* @__PURE__ */React68.createElement(DynamicCardText, {
    key: index,
    data: value
  })))))));
};
VirtualAgentControlBottomSheet.fragment = fragment38;
VirtualAgentControlBottomSheet.displayName = "VirtualAgentControlBottomSheet";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-more-info-trigger.tsx
var fragment39 = gql43`
  fragment VirtualAgentControlMoreInfoTriggerFragment on VirtualAgentControlMoreInfoTrigger {
    accessibility
    action {
      ...VirtualAgentControlBottomSheetFragment
    }
    analytics {
      ... on VirtualAgentControlInteractionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
    }
    icon {
      ...IconFragment
    }
    text
  }
  ${VirtualAgentControlBottomSheet.fragment}
  ${Icon.fragment}
`;
var VirtualAgentControlMoreInfoTrigger = ({
  data,
  overflow,
  textSize
}) => {
  const {
    text,
    icon,
    action,
    accessibility
  } = data;
  const triggerRef = React69.useRef(null);
  const uniqueDialogId = React69.useMemo(() => `vac-more-info-trigger-dialog-${uuidv47()}`, []);
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog9(uniqueDialogId);
  const hasAccessibility = !!(accessibility && accessibility.length > 0);
  const defaultIconId = "icon__info_outline";
  const defaultIcon = {
    ...icon,
    id: defaultIconId
  };
  const openDialog = () => {
    dialogActions.openDialog();
  };
  const closeDialog = () => {
    dialogActions.closeDialog();
  };
  const handleTriggerClick = () => {
    if (!isDialogOpen) {
      openDialog();
    }
  };
  return /* @__PURE__ */React69.createElement(React69.Fragment, null, /* @__PURE__ */React69.createElement(EGDSMoreInfoTrigger, {
    size: EGDSIconSize5.SMALL
  }, /* @__PURE__ */React69.createElement("button", {
    type: "button",
    onClick: handleTriggerClick
  }, /* @__PURE__ */React69.createElement(EGDSLayoutFlex20, {
    alignItems: "center",
    space: "one"
  }, hasAccessibility && /* @__PURE__ */React69.createElement(EGDSText18, {
    className: "is-visually-hidden"
  }, accessibility), /* @__PURE__ */React69.createElement(EGDSText18, {
    overflow,
    size: textSize,
    "aria-hidden": hasAccessibility
  }, text), /* @__PURE__ */React69.createElement("div", {
    "aria-hidden": "true"
  }, /* @__PURE__ */React69.createElement(Icon, {
    data: defaultIcon
  }))))), isDialogOpen && /* @__PURE__ */React69.createElement(VirtualAgentControlBottomSheet, {
    data: action,
    triggerRef,
    visible: isDialogOpen,
    closeDialog,
    DialogComponent
  }));
};
VirtualAgentControlMoreInfoTrigger.fragment = fragment39;
VirtualAgentControlMoreInfoTrigger.displayName = "VirtualAgentControlMoreInfoTrigger";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-stacked-text.tsx
import React70 from "react";
import { EGDSText as EGDSText19 } from "@egds/react-core/text";
import { gql as gql44 } from "@shared-ui/apollo-client-wrapper";
var fragment40 = gql44`
  fragment VirtualAgentControlStackedTextFragment on VirtualAgentControlStackedText {
    textBlocks
    textStyle
  }
`;
var VirtualAgentControlStackedText = ({
  data
}) => {
  const {
    textBlocks,
    textStyle
  } = data;
  const TEXT_STYLES = {
    small: {
      customTheme: "standard",
      customStyle: "text-small"
    },
    medium: {
      customTheme: "standard",
      customStyle: "text-medium"
    }
  };
  const textStyleName = textStyle === "TEXT_MEDIUM" /* TEXT_MEDIUM */ ? "medium" : "small";
  const textSize = textStyle === "TEXT_MEDIUM" /* TEXT_MEDIUM */ ? 300 : 200;
  return /* @__PURE__ */React70.createElement("div", {
    "data-stid": "stacked-text"
  }, textBlocks.map((text, index) => /* @__PURE__ */React70.createElement(EGDSText19, {
    key: `text-${index}`,
    size: textSize,
    typeStyle: TEXT_STYLES[textStyleName],
    spacing: index > 0 ? "one" : void 0,
    overflow: "truncate-4-lines"
  }, text)));
};
VirtualAgentControlStackedText.fragment = fragment40;
VirtualAgentControlStackedText.displayName = "VirtualAgentControlStackedText";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-star-rating-and-details.tsx
import React71 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex21, EGDSLayoutFlexItem as EGDSLayoutFlexItem11 } from "@egds/react-core/layout-flex";
import { EGDSRating as EGDSRating2 } from "@egds/react-core/rating";
import { EGDSText as EGDSText20 } from "@egds/react-core/text";
import { gql as gql45 } from "@shared-ui/apollo-client-wrapper";
var fragment41 = gql45`
  fragment VirtualAgentControlStarRatingAndDetailsFragment on VirtualAgentControlStarRatingAndDetails {
    fractionRating
    numberReviews
    rating
    secondLine
  }
`;
var VirtualAgentControlStarRatingAndDetails = ({
  data
}) => {
  const {
    fractionRating,
    numberReviews,
    rating,
    secondLine
  } = data;
  const typeStyle = {
    customTheme: "standard",
    customStyle: "text-small"
  };
  return /* @__PURE__ */React71.createElement(EGDSLayoutFlex21, {
    "data-stid": "star-rating-and-details",
    direction: "column",
    space: "xsmall"
  }, /* @__PURE__ */React71.createElement(EGDSLayoutFlexItem11, null, /* @__PURE__ */React71.createElement(EGDSLayoutFlex21, {
    space: "xsmall",
    wrap: "wrap"
  }, fractionRating && /* @__PURE__ */React71.createElement(EGDSLayoutFlexItem11, {
    alignSelf: "center"
  }, /* @__PURE__ */React71.createElement(EGDSText20, {
    typeStyle
  }, fractionRating)), rating > 0 && /* @__PURE__ */React71.createElement(EGDSLayoutFlexItem11, {
    alignSelf: "center"
  }, /* @__PURE__ */React71.createElement(EGDSRating2, {
    rating: rating.toPrecision()
  })), /* @__PURE__ */React71.createElement(EGDSLayoutFlexItem11, {
    alignSelf: "center"
  }, /* @__PURE__ */React71.createElement(EGDSText20, {
    typeStyle
  }, numberReviews)))), secondLine && /* @__PURE__ */React71.createElement(EGDSLayoutFlexItem11, null, /* @__PURE__ */React71.createElement(EGDSText20, {
    overflow: "truncate-2-lines",
    typeStyle
  }, secondLine)));
};
VirtualAgentControlStarRatingAndDetails.fragment = fragment41;
VirtualAgentControlStarRatingAndDetails.displayName = "VirtualAgentControlStarRatingAndDetails";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-title.tsx
import React72 from "react";
import { EGDSHeading as EGDSHeading7, EGDSSubheading } from "@egds/react-core/text";
import { gql as gql46 } from "@shared-ui/apollo-client-wrapper";
var fragment42 = gql46`
  fragment VirtualAgentControlTitleFragment on VirtualAgentControlTitle {
    heading
    subHeading
  }
`;
var VirtualAgentControlTitle = ({
  data
}) => {
  const {
    heading,
    subHeading
  } = data;
  const Heading = /* @__PURE__ */React72.createElement(EGDSHeading7, {
    typeStyle: {
      customStyle: "headline-medium"
    },
    overflow: "truncate-3-lines",
    tag: "h3"
  }, heading);
  if (!subHeading) {
    return Heading;
  }
  return /* @__PURE__ */React72.createElement(React72.Fragment, null, Heading, /* @__PURE__ */React72.createElement(EGDSSubheading, {
    overflow: "truncate-3-lines"
  }, subHeading));
};
VirtualAgentControlTitle.fragment = fragment42;
VirtualAgentControlTitle.displayName = "VirtualAgentControlTitle";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-list.tsx
import React73 from "react";
import { EGDSList, EGDSListItem } from "@egds/react-core/list";
import { EGDSText as EGDSText21 } from "@egds/react-core/text";
import { gql as gql47 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/__private/list-map.ts
var bulletMap = {
  ["BULLET" /* BULLET */]: "default",
  ["ICON" /* ICON */]: "icon-alternate",
  ["NO_BULLET" /* NO_BULLET */]: "no-bullet",
  ["NUMBER" /* NUMBER */]: "default"
};
var textSizeMap = {
  ["LIST_1" /* LIST_1 */]: 2,
  ["LIST_2" /* LIST_2 */]: 2,
  ["LIST_3" /* LIST_3 */]: 3
};
var typeMap = {
  ["BULLET" /* BULLET */]: "unordered",
  ["ICON" /* ICON */]: "unordered",
  ["NO_BULLET" /* NO_BULLET */]: "unordered",
  ["NUMBER" /* NUMBER */]: "ordered"
};

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-list.tsx
var fragment43 = gql47`
  fragment VirtualAgentControlListFragment on VirtualAgentControlList {
    listItems {
      ...VirtualAgentControlListItemFragment
    }
    size
    type
  }

  fragment VirtualAgentControlListItemFragment on VirtualAgentControlListItem {
    icon {
      ...IconFragment
    }
    nestedList {
      ...VirtualAgentControlLevelOneListFragment
    }
    text
  }

  fragment VirtualAgentControlLevelOneListFragment on VirtualAgentControlLevelOneList {
    listItems {
      ...VirtualAgentControlLevelOneListItemFragment
    }
    type
  }

  fragment VirtualAgentControlLevelOneListItemFragment on VirtualAgentControlLevelOneListItem {
    icon {
      ...IconFragment
    }
    text
  }
  ${Icon.fragment}
`;
var VirtualAgentControlList = ({
  data
}) => {
  const {
    type,
    listItems,
    size
  } = data;
  const computedTextSize = size ? textSizeMap[size] : 2;
  const bulletType = type ? bulletMap[type] : "default";
  const computedType = type ? typeMap[type] : "unordered";
  if (!listItems || listItems.length === 0) {
    return null;
  }
  return /* @__PURE__ */React73.createElement(EGDSList, {
    "data-stid": "virtual-agent-control-list",
    bullet: bulletType,
    size: computedTextSize,
    type: computedType
  }, listItems.map(({
    icon,
    nestedList,
    text
  }, index) => {
    var _a;
    return /* @__PURE__ */React73.createElement(EGDSListItem, {
      key: `${text}-${(icon == null ? void 0 : icon.id) || ""}-${index}`
    }, icon && /* @__PURE__ */React73.createElement(Icon, {
      data: icon
    }), /* @__PURE__ */React73.createElement(EGDSText21, {
      overflow: "truncate-3-lines"
    }, text), nestedList && /* @__PURE__ */React73.createElement(EGDSList, {
      "data-stid": "virtual-agent-control-level-one-list",
      bullet: (nestedList == null ? void 0 : nestedList.type) ? bulletMap[nestedList.type] : "default",
      size: computedTextSize,
      type: (nestedList == null ? void 0 : nestedList.type) ? typeMap[nestedList.type] : "unordered"
    }, (_a = nestedList == null ? void 0 : nestedList.listItems) == null ? void 0 : _a.map((nestedItem, nestedIndex) => {
      var _a2;
      return /* @__PURE__ */React73.createElement(EGDSListItem, {
        key: `${nestedItem.text}-${((_a2 = nestedItem == null ? void 0 : nestedItem.icon) == null ? void 0 : _a2.id) || ""}-${nestedIndex}`
      }, (nestedItem == null ? void 0 : nestedItem.icon) && /* @__PURE__ */React73.createElement(Icon, {
        data: nestedItem.icon
      }), /* @__PURE__ */React73.createElement(EGDSText21, {
        overflow: "truncate-3-lines"
      }, nestedItem.text));
    })));
  }));
};
VirtualAgentControlList.fragment = fragment43;
VirtualAgentControlList.displayName = "VirtualAgentControlList";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-callout-subtext.tsx
import React74 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex22, EGDSLayoutFlexItem as EGDSLayoutFlexItem12 } from "@egds/react-core/layout-flex";
import { EGDSHeading as EGDSHeading8, EGDSText as EGDSText22 } from "@egds/react-core/text";
import { gql as gql48 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/__private/callout-subtext-map.ts
var directionMap = {
  ["ROW" /* ROW */]: "row",
  ["STACK" /* STACK */]: "column"
};
var leadingMap = {
  ["CALLOUT" /* CALLOUT */]: "callout",
  ["SUBTEXT" /* SUBTEXT */]: "subtext"
};
var alignSelfMap = {
  ["ROW" /* ROW */]: "end",
  ["STACK" /* STACK */]: "start"
};

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-callout-subtext.tsx
var fragment44 = gql48`
  fragment VirtualAgentControlCalloutSubtextFragment on VirtualAgentControlCalloutSubtext {
    callout
    leading
    position
    subtext
  }
`;
var VirtualAgentControlCalloutSubtext = ({
  data
}) => {
  const {
    callout,
    leading,
    position,
    subtext
  } = data;
  const computedDirection = position ? directionMap[position] : "row";
  const computedLeading = leading ? leadingMap[leading] : "callout";
  const computedAlignSelf = position ? alignSelfMap[position] : "end";
  const Callout = /* @__PURE__ */React74.createElement(EGDSHeading8, {
    typeStyle: {
      customTheme: "standard",
      customStyle: "headline-small"
    },
    overflow: "truncate-3-lines"
  }, callout);
  const Subtext = /* @__PURE__ */React74.createElement(EGDSText22, {
    size: 200,
    overflow: "truncate-4-lines"
  }, subtext);
  const items = computedLeading === "callout" ? [Callout, Subtext] : [Subtext, Callout];
  return /* @__PURE__ */React74.createElement(EGDSLayoutFlex22, {
    "data-stid": "virtual-agent-control-callout-subtext",
    space: "xsmall",
    wrap: "wrap",
    direction: computedDirection
  }, items.map((Component, idx) => /* @__PURE__ */React74.createElement(EGDSLayoutFlexItem12, {
    alignSelf: computedAlignSelf,
    key: `callout-subtext-${idx}`
  }, Component)));
};
VirtualAgentControlCalloutSubtext.fragment = fragment44;
VirtualAgentControlCalloutSubtext.displayName = "VirtualAgentControlCalloutSubtext";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-callout-more-info-trigger.tsx
import React75 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex23, EGDSLayoutFlexItem as EGDSLayoutFlexItem13 } from "@egds/react-core/layout-flex";
import { EGDSHeading as EGDSHeading9 } from "@egds/react-core/text";
import { gql as gql49 } from "@shared-ui/apollo-client-wrapper";
var fragment45 = gql49`
  fragment VirtualAgentControlCalloutMoreInfoTriggerFragment on VirtualAgentControlCalloutMoreInfoTrigger {
    accessibility
    callout
    moreInfoTrigger {
      ...VirtualAgentControlMoreInfoTriggerFragment
    }
  }
  ${VirtualAgentControlMoreInfoTrigger.fragment}
`;
var VirtualAgentControlCalloutMoreInfoTrigger = ({
  data
}) => {
  const {
    callout,
    accessibility,
    moreInfoTrigger
  } = data;
  const hasAccessibility = !!(accessibility && accessibility.length > 0);
  const updatedMoreInfoTrigger = {
    ...moreInfoTrigger,
    accessibility: hasAccessibility ? accessibility : moreInfoTrigger.accessibility,
    icon: {
      ...moreInfoTrigger.icon,
      // overwrite the icon size for this composition
      size: "EXTRA_SMALL" /* EXTRA_SMALL */
    }
  };
  return /* @__PURE__ */React75.createElement(EGDSLayoutFlex23, {
    "data-stid": "virtual-agent-control-callout-more-info-trigger",
    space: "xsmall",
    wrap: "wrap",
    alignItems: "end"
  }, /* @__PURE__ */React75.createElement(EGDSLayoutFlexItem13, null, /* @__PURE__ */React75.createElement(EGDSHeading9, {
    typeStyle: {
      customTheme: "standard",
      customStyle: "headline-small"
    },
    overflow: "truncate",
    "aria-hidden": hasAccessibility
  }, callout)), /* @__PURE__ */React75.createElement(EGDSLayoutFlexItem13, null, /* @__PURE__ */React75.createElement(VirtualAgentControlMoreInfoTrigger, {
    data: updatedMoreInfoTrigger,
    textSize: 200,
    overflow: "truncate"
  })));
};
VirtualAgentControlCalloutMoreInfoTrigger.fragment = fragment45;
VirtualAgentControlCalloutMoreInfoTrigger.displayName = "VirtualAgentControlCalloutMoreInfoTrigger";

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-graphic-text.tsx
import React76 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex24, EGDSLayoutFlexItem as EGDSLayoutFlexItem14 } from "@egds/react-core/layout-flex";
import { EGDSText as EGDSText23 } from "@egds/react-core/text";
import { gql as gql50 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/common/__private/graphic-text-map.ts
var gapMap = {
  ["SMALL" /* SMALL */]: "xsmall",
  ["MEDIUM" /* MEDIUM */]: "small",
  ["LARGE" /* LARGE */]: "small",
  ["XLARGE" /* XLARGE */]: "small"
};
var graphicPositionMap = {
  ["TOP" /* TOP */]: "start",
  ["CENTERED" /* CENTERED */]: "center"
};
var textSizeMap2 = {
  ["SMALL" /* SMALL */]: 200,
  ["MEDIUM" /* MEDIUM */]: 300,
  ["LARGE" /* LARGE */]: 400,
  ["XLARGE" /* XLARGE */]: 500
};

// ../internal/src/components/declarative-cards/compositions/virtual-agent-control-graphic-text.tsx
var fragment46 = gql50`
  fragment VirtualAgentControlGraphicTextFragment on VirtualAgentControlGraphicText {
    graphic {
      ...UIGraphicFragment
    }
    graphicPosition
    size
    text
  }
  ${UIGraphic.fragment}
`;
var VirtualAgentControlGraphicText = ({
  data
}) => {
  const {
    graphic,
    graphicPosition,
    size,
    text
  } = data;
  const computedGap = size ? gapMap[size] : "xsmall";
  const computedGraphicPosition = graphicPosition ? graphicPositionMap[graphicPosition] : "start";
  const computedTextSize = size ? textSizeMap2[size] : 200;
  const computedGraphicSize = graphicData => {
    switch (graphicData == null ? void 0 : graphicData.__typename) {
      case "Icon":
        return {
          ...graphicData,
          size
        };
      case "Mark":
        return {
          ...graphicData,
          markSize: size
        };
      default:
        return graphicData;
    }
  };
  return /* @__PURE__ */React76.createElement(EGDSLayoutFlex24, {
    "data-stid": "virtual-agent-control-graphic-text",
    space: computedGap
  }, /* @__PURE__ */React76.createElement(EGDSLayoutFlexItem14, {
    alignSelf: computedGraphicPosition
  }, /* @__PURE__ */React76.createElement("div", null, /* @__PURE__ */React76.createElement(UIGraphic, {
    data: computedGraphicSize(graphic)
  }))), /* @__PURE__ */React76.createElement(EGDSLayoutFlexItem14, null, /* @__PURE__ */React76.createElement(EGDSText23, {
    size: computedTextSize,
    overflow: "truncate-4-lines"
  }, text)));
};
VirtualAgentControlGraphicText.fragment = fragment46;
VirtualAgentControlGraphicText.displayName = "VirtualAgentControlGraphicText";

// ../internal/src/components/declarative-cards/dynamic-card-composition.tsx
var fragment47 = gql51`
  fragment DynamicCardCompositionFragment on DynamicCardComposition {
    progressBar {
      ...VirtualAgentControlProgressBarFragment
    }
    rating {
      ...VirtualAgentControlRatingFragment
    }
    stepIndicator {
      ...VirtualAgentControlStepIndicatorFragment
    }
    moreInfoTrigger {
      ...VirtualAgentControlMoreInfoTriggerFragment
    }
    stackedText {
      ...VirtualAgentControlStackedTextFragment
    }
    starRatingAndDetails {
      ...VirtualAgentControlStarRatingAndDetailsFragment
    }
    title {
      ...VirtualAgentControlTitleFragment
    }
    list {
      ...VirtualAgentControlListFragment
    }
    calloutSubtext {
      ...VirtualAgentControlCalloutSubtextFragment
    }
    calloutMoreInfoTrigger {
      ...VirtualAgentControlCalloutMoreInfoTriggerFragment
    }
    graphicText {
      ...VirtualAgentControlGraphicTextFragment
    }
  }
  ${VirtualAgentControlProgressBar.fragment}
  ${VirtualAgentControlRating.fragment}
  ${VirtualAgentControlStepIndicator.fragment}
  ${VirtualAgentControlMoreInfoTrigger.fragment}
  ${VirtualAgentControlStackedText.fragment}
  ${VirtualAgentControlStarRatingAndDetails.fragment}
  ${VirtualAgentControlTitle.fragment}
  ${VirtualAgentControlList.fragment}
  ${VirtualAgentControlCalloutSubtext.fragment}
  ${VirtualAgentControlCalloutMoreInfoTrigger.fragment}
  ${VirtualAgentControlGraphicText.fragment}
`;
var DynamicCardComposition = ({
  data,
  spacing,
  customComponents
}) => {
  const componentMap = {
    progressBar: (customComponents == null ? void 0 : customComponents.VirtualAgentControlProgressBar) || VirtualAgentControlProgressBar,
    rating: (customComponents == null ? void 0 : customComponents.VirtualAgentControlRating) || VirtualAgentControlRating,
    stepIndicator: (customComponents == null ? void 0 : customComponents.VirtualAgentControlStepIndicator) || VirtualAgentControlStepIndicator,
    moreInfoTrigger: (customComponents == null ? void 0 : customComponents.VirtualAgentControlMoreInfoTrigger) || VirtualAgentControlMoreInfoTrigger,
    stackedText: (customComponents == null ? void 0 : customComponents.VirtualAgentControlStackedText) || VirtualAgentControlStackedText,
    starRatingAndDetails: (customComponents == null ? void 0 : customComponents.VirtualAgentControlStarRatingAndDetails) || VirtualAgentControlStarRatingAndDetails,
    title: (customComponents == null ? void 0 : customComponents.VirtualAgentControlTitle) || VirtualAgentControlTitle,
    list: (customComponents == null ? void 0 : customComponents.VirtualAgentControlList) || VirtualAgentControlList,
    calloutSubtext: (customComponents == null ? void 0 : customComponents.VirtualAgentControlCalloutSubtext) || VirtualAgentControlCalloutSubtext,
    calloutMoreInfoTrigger: (customComponents == null ? void 0 : customComponents.VirtualAgentControlCalloutMoreInfoTrigger) || VirtualAgentControlCalloutMoreInfoTrigger,
    graphicText: (customComponents == null ? void 0 : customComponents.VirtualAgentControlGraphicText) || VirtualAgentControlGraphicText
  };
  const activeComponent = Object.entries(data || {}).find(([key2, value]) => {
    return value != null && componentMap[key2] != null;
  });
  if (!activeComponent) {
    return null;
  }
  const [key, componentData] = activeComponent;
  const Component = componentMap[key];
  if (!Component) {
    return null;
  }
  return /* @__PURE__ */React77.createElement(EGDSSpacing14, {
    ...spacing
  }, /* @__PURE__ */React77.createElement("div", null, /* @__PURE__ */React77.createElement(Component, {
    data: componentData
  })));
};
DynamicCardComposition.fragment = fragment47;
DynamicCardComposition.displayName = "DynamicCardComposition";

// ../internal/src/components/declarative-cards/dynamic-card-content.tsx
import React78 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex25 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing15 } from "@egds/react-core/spacing";
import { gql as gql52 } from "@shared-ui/apollo-client-wrapper";
var fragment48 = gql52`
  fragment DynamicCardContentFragment on DynamicCardContent {
    text {
      ...DynamicCardTextFragment
    }
    icon {
      ...IconFragment
    }
    badge {
      ...EGDSBadgeFragment
    }
    action {
      ...DynamicCardActionFragment
    }
    textElement {
      ... on DynamicCardHeading {
        ...DynamicCardHeadingFragment
      }
    }
  }
  ${DynamicCardText.fragment}
  ${Icon.fragment}
  ${VirtualAgentEGDSBadge.fragment}
  ${DynamicCardAction.fragment}
  ${DynamicCardHeading.fragment}
`;
function getTextElementComponent(textElement) {
  if (!textElement) {
    return DynamicCardText;
  }
  switch (textElement.__typename) {
    case "DynamicCardHeading":
      return DynamicCardHeading;
    default:
      return DynamicCardText;
  }
}
var DynamicCardContent = ({
  data,
  spacing,
  customComponents
}) => {
  const {
    action,
    text,
    icon,
    badge,
    textElement
  } = data;
  const textData = textElement || text;
  const BuiltInTextComponent = getTextElementComponent(textData);
  const TextComponent = (customComponents == null ? void 0 : customComponents.DynamicCardText) || BuiltInTextComponent;
  const IconComponent = (customComponents == null ? void 0 : customComponents.Icon) || Icon;
  const BadgeComponent = (customComponents == null ? void 0 : customComponents.Badge) || VirtualAgentEGDSBadge;
  const props = {
    ...spacing
  };
  if (badge) {
    return /* @__PURE__ */React78.createElement(EGDSSpacing15, {
      ...props
    }, /* @__PURE__ */React78.createElement("div", null, /* @__PURE__ */React78.createElement(BadgeComponent, {
      data: badge
    })));
  }
  if (action && (text || icon)) {
    return /* @__PURE__ */React78.createElement(EGDSSpacing15, {
      ...props
    }, /* @__PURE__ */React78.createElement("div", null, /* @__PURE__ */React78.createElement(DynamicCardAction, {
      data: action,
      text,
      icon
    })));
  }
  if (textData && icon) {
    return /* @__PURE__ */React78.createElement(EGDSSpacing15, {
      ...props
    }, /* @__PURE__ */React78.createElement("div", null, /* @__PURE__ */React78.createElement(EGDSLayoutFlex25, {
      alignItems: "start"
    }, /* @__PURE__ */React78.createElement(IconComponent, {
      data: icon
    }), /* @__PURE__ */React78.createElement(EGDSSpacing15, {
      padding: {
        blockstart: "half",
        inlinestart: "one"
      }
    }, /* @__PURE__ */React78.createElement("span", null, /* @__PURE__ */React78.createElement(TextComponent, {
      data: textData
    }))))));
  }
  if (textData) {
    return /* @__PURE__ */React78.createElement(EGDSSpacing15, {
      ...props
    }, /* @__PURE__ */React78.createElement("div", null, /* @__PURE__ */React78.createElement(TextComponent, {
      data: textData
    })));
  }
  if (icon) {
    return /* @__PURE__ */React78.createElement(EGDSSpacing15, {
      ...props
    }, /* @__PURE__ */React78.createElement("div", null, /* @__PURE__ */React78.createElement(IconComponent, {
      data: icon
    })));
  }
  return null;
};
DynamicCardContent.fragment = fragment48;
DynamicCardContent.displayName = "DynamicCardContent";

// ../internal/src/components/declarative-cards/dynamic-card-group.tsx
import React79 from "react";
import { EGDSLayoutFlex as EGDSLayoutFlex26 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing16, EGDSSpacingHr as EGDSSpacingHr3 } from "@egds/react-core/spacing";
import { gql as gql53 } from "@shared-ui/apollo-client-wrapper";
var fragment49 = gql53`
  fragment DynamicCardGroupFragment on DynamicCardGroup {
    separator
    inlineContent {
      ...DynamicCardContentFragment
    }
    inlineJustify
    forceWrap
    groupElement {
      ...DynamicCardContentFragment
      ...DynamicCardCompositionFragment
    }
  }
  ${DynamicCardContent.fragment}
  ${DynamicCardComposition.fragment}
`;
var groupComponents = {
  DynamicCardContent,
  DynamicCardComposition
};
var Justify = {
  SPACE: "space-between",
  START: "start",
  END: "end"
};
var DynamicCardGroup = ({
  data,
  spacing,
  customComponents
}) => {
  const {
    separator,
    inlineContent,
    inlineJustify,
    forceWrap,
    groupElement
  } = data;
  if (!(inlineContent == null ? void 0 : inlineContent.length) && !(groupElement == null ? void 0 : groupElement.length)) {
    return null;
  }
  const ContentComponent = (customComponents == null ? void 0 : customComponents.DynamicCardContent) || DynamicCardContent;
  const spacingProps = {
    ...spacing
  };
  const getAlignItems = () => {
    const hasCustomAlignment = forceWrap && inlineJustify && inlineJustify !== "SPACE" /* SPACE */;
    if (hasCustomAlignment) {
      return Justify[inlineJustify];
    }
    return "stretch";
  };
  const layoutProps = {
    justifyContent: Justify[inlineJustify != null ? inlineJustify : "START" /* START */],
    space: "small",
    alignItems: getAlignItems(),
    direction: forceWrap ? "column" : "row"
  };
  const contentSpacingProps = {
    margin: {
      block: "unset",
      // forceWrap should remove all inline spacing as each item is on its own line
      inlineend: !forceWrap && inlineJustify === "START" /* START */ ? "two" : "unset",
      inlinestart: !forceWrap && inlineJustify === "END" /* END */ ? "two" : "unset"
    }
  };
  const groupContent = React79.useMemo(() => {
    return (groupElement == null ? void 0 : groupElement.length) ? /* @__PURE__ */React79.createElement(React79.Fragment, null, groupElement.map((content, i) => React79.createElement(groupComponents[content.__typename], {
      key: `${content.__typename}-${i}`,
      // Prefer stable keys
      data: content,
      customComponents,
      spacing: contentSpacingProps
    }))) : /* @__PURE__ */React79.createElement(React79.Fragment, null, inlineContent == null ? void 0 : inlineContent.map((content, i) => React79.createElement(ContentComponent, {
      key: `${content.__typename}-${i}`,
      // Prefer stable keys
      data: content,
      customComponents,
      spacing: contentSpacingProps
    })));
  }, [groupElement, inlineContent]);
  return /* @__PURE__ */React79.createElement(EGDSSpacing16, {
    ...spacingProps
  }, /* @__PURE__ */React79.createElement("div", null, /* @__PURE__ */React79.createElement(EGDSLayoutFlex26, {
    ...layoutProps,
    "data-stid": "dynamic-card-group"
  }, groupContent), separator && /* @__PURE__ */React79.createElement(EGDSSpacingHr3, {
    blockMargin: "two"
  })));
};
DynamicCardGroup.fragment = fragment49;
DynamicCardGroup.displayName = "DynamicCardGroup";

// ../internal/src/components/declarative-cards/dynamic-card.tsx
var fragment50 = gql54`
  fragment DynamicCardFragment on DynamicCard {
    elements {
      ...DynamicCardContentFragment
      ...DynamicCardGroupFragment
      ...DynamicCardCompositionFragment
    }
    image {
      ...DynamicCardCoverImageFragment
    }
    imagePosition
    imageOverlays {
      ...DynamicCardOverlayTextFragment
      ...DynamicCardOverlayToggleFragment
    }
    action {
      event {
        eventId
        payload
      }
      link {
        ... on HttpURI {
          value
        }
      }
      properties {
        accessibility
        target
      }
    }
    footer {
      ...DynamicCardContentFragment
    }
    content {
      ...DynamicCardGroupFragment
    }
  }
  ${DynamicCardContent.fragment}
  ${DynamicCardGroup.fragment}
  ${DynamicCardComposition.fragment}
  ${DynamicCardCoverImage.fragment}
  ${DynamicCardOverlayText.fragment}
  ${DynamicCardOverlayToggle.fragment}
`;
var defaultComponents = {
  DynamicCardContent,
  DynamicCardGroup,
  DynamicCardComposition
};
var defaultContentComponents = {
  DynamicCardGroup
};
var defaultFooterComponents = {
  DynamicCardContent
};
var renderCardBody = ({
  elementContent,
  cardContent,
  cardFooter
}) => {
  const showElementContent = !cardContent && !cardFooter && elementContent;
  return /* @__PURE__ */React80.createElement(React80.Fragment, null, showElementContent && /* @__PURE__ */React80.createElement(EGDSLayoutFlexItem15, {
    grow: 1
  }, elementContent), cardContent && /* @__PURE__ */React80.createElement(EGDSLayoutFlexItem15, {
    grow: 1
  }, cardContent), cardFooter && /* @__PURE__ */React80.createElement(EGDSLayoutFlexItem15, null, cardFooter));
};
var DynamicCard = ({
  data,
  customComponents
}) => {
  const {
    elements,
    image,
    imagePosition,
    imageOverlays,
    action,
    footer,
    content
  } = data;
  const {
    link,
    event,
    properties
  } = action || {};
  const {
    useActionDisabled
  } = useDynamicCardContext();
  const actionIsDisabled = useActionDisabled();
  const components = {
    ...defaultComponents,
    ...customComponents
  };
  const contentComponents = {
    ...defaultContentComponents,
    ...customComponents
  };
  const footerComponents = {
    ...defaultFooterComponents,
    ...customComponents
  };
  const {
    publish
  } = useSignalPublisher7();
  const handleCardClick = React80.useCallback(() => {
    if (!event || link) {
      return null;
    }
    if (event && !actionIsDisabled) {
      const {
        eventId,
        payload: payload2
      } = event;
      publish(new VacDynamicCardSignal4("VAC_DYNAMIC_CARD_EVENT" /* Vac_DynamicCardEvent */, {
        eventId,
        payload: payload2
      }));
    }
  }, [event, link, publish]);
  const elementContent = React80.useMemo(() => {
    var _a, _b;
    if (!(elements == null ? void 0 : elements.length)) {
      return null;
    }
    const href = (link == null ? void 0 : link.__typename) === "HttpURI" ? link.value : void 0;
    const target = UILinkTargetMap[(_a = properties == null ? void 0 : properties.target) != null ? _a : "INTERNAL" /* INTERNAL */];
    return /* @__PURE__ */React80.createElement(EGDSCardContentSection, null, href ? /* @__PURE__ */React80.createElement(EGDSCardLink, null, /* @__PURE__ */React80.createElement("a", {
      href,
      target
    }, (_b = properties == null ? void 0 : properties.accessibility) != null ? _b : "")) : null, /* @__PURE__ */React80.createElement(React80.Fragment, null, elements == null ? void 0 : elements.map((element, i) => React80.createElement(components[element.__typename], {
      key: `${element.__typename}-${i}`,
      data: element,
      customComponents: components,
      spacing: {
        margin: {
          blockend: i === elements.length - 1 ? "unset" : "two"
        }
      }
    }))));
  }, [elements, link, properties]);
  const cardContent = React80.useMemo(() => {
    var _a, _b;
    if (!(content == null ? void 0 : content.length)) {
      return null;
    }
    const target = UILinkTargetMap[(_a = properties == null ? void 0 : properties.target) != null ? _a : "INTERNAL" /* INTERNAL */];
    const href = (link == null ? void 0 : link.__typename) === "HttpURI" ? link.value : void 0;
    return /* @__PURE__ */React80.createElement(EGDSCardContentSection, null, href ? /* @__PURE__ */React80.createElement(EGDSCardLink, null, /* @__PURE__ */React80.createElement("a", {
      href,
      target
    }, (_b = properties == null ? void 0 : properties.accessibility) != null ? _b : "")) : null, content.map((element, i) => React80.createElement(contentComponents[element.__typename], {
      key: `${element.__typename}-${i}`,
      data: element,
      customComponents: contentComponents,
      spacing: {
        margin: {
          blockend: i === content.length - 1 ? "unset" : "two"
        }
      }
    })));
  }, [content, link, properties]);
  const cardFooter = React80.useMemo(() => {
    if (!(footer == null ? void 0 : footer.length)) {
      return null;
    }
    return /* @__PURE__ */React80.createElement(EGDSCardContentSection, null, footer.map((element, i) => React80.createElement(footerComponents[element.__typename], {
      key: i,
      data: element,
      customComponents: footerComponents,
      spacing: {
        margin: {
          blockend: i === footer.length - 1 ? "unset" : "small"
        }
      }
    })));
  }, [footer]);
  if (!(elements == null ? void 0 : elements.length) && !(content == null ? void 0 : content.length) && !(image == null ? void 0 : image.url) && !(footer == null ? void 0 : footer.length)) {
    return null;
  }
  const cardProps = {
    onClick: handleCardClick,
    border: true,
    "data-test-id": "dynamic-card"
  };
  if (!(image == null ? void 0 : image.url)) {
    return /* @__PURE__ */React80.createElement(EGDSCard4, {
      ...cardProps
    }, /* @__PURE__ */React80.createElement(EGDSLayoutFlex27, {
      direction: "column"
    }, renderCardBody({
      elementContent,
      cardContent,
      cardFooter
    })));
  }
  const coverImageProps = {
    data: image,
    // overlays type should be DynamicCardFragment["imageOverlays"]
    // but due to the new type DynamicCardOverlayTripsSaveItem which we don't support yet
    // we need to use a union of the supported overlay types
    // once we support all overlay types we can change it to DynamicCardFragment["imageOverlays"]
    overlays: imageOverlays
  };
  if (!(elements == null ? void 0 : elements.length) && !(content == null ? void 0 : content.length) && !(footer == null ? void 0 : footer.length)) {
    return /* @__PURE__ */React80.createElement(EGDSCard4, {
      ...cardProps
    }, /* @__PURE__ */React80.createElement("div", null, /* @__PURE__ */React80.createElement(DynamicCardCoverImage, {
      ...coverImageProps,
      roundedAll: true
    })));
  }
  if (imagePosition === "LEFT" /* LEFT */) {
    return /* @__PURE__ */React80.createElement(EGDSCard4, {
      ...cardProps
    }, /* @__PURE__ */React80.createElement(EGDSLayoutGrid, {
      columns: 3
    }, /* @__PURE__ */React80.createElement(EGDSLayoutGridItem, {
      colSpan: 1
    }, /* @__PURE__ */React80.createElement(DynamicCardCoverImage, {
      ...coverImageProps
    })), /* @__PURE__ */React80.createElement(EGDSLayoutGridItem, {
      colSpan: 2
    }, /* @__PURE__ */React80.createElement(EGDSLayoutFlex27, {
      direction: "column"
    }, renderCardBody({
      elementContent,
      cardContent,
      cardFooter
    })))));
  }
  return /* @__PURE__ */React80.createElement(EGDSCard4, {
    ...cardProps
  }, /* @__PURE__ */React80.createElement(EGDSLayoutFlex27, {
    direction: "column"
  }, /* @__PURE__ */React80.createElement(EGDSLayoutFlexItem15, null, /* @__PURE__ */React80.createElement("div", null, /* @__PURE__ */React80.createElement(DynamicCardCoverImage, {
    ...coverImageProps
  }))), renderCardBody({
    elementContent,
    cardContent,
    cardFooter
  })));
};
DynamicCard.fragment = fragment50;
DynamicCard.displayName = "DynamicCard";

// ../internal/src/components/declarative-cards/carousel/dynamic-card-carousel.tsx
import React81 from "react";
import { v4 as uuidv48 } from "uuid";
import { EGDSCarousel } from "@egds/react-core/carousel";
import { EGDSSpacing as EGDSSpacing17 } from "@egds/react-core/spacing";
import { gql as gql55 } from "@shared-ui/apollo-client-wrapper";
var fragment51 = gql55`
  fragment DynamicCardCarouselFragment on DynamicCardCarousel {
    nextButton {
      ...EGDSButtonFragment
    }
    prevButton {
      ...EGDSButtonFragment
    }
    cards {
      ...DynamicCardCarouselItemFragment
    }
    carouselId
  }
  fragment DynamicCardCarouselItemFragment on DynamicCardCarouselItem {
    id
    cardPayload
    card {
      ...DynamicCardFragment
    }
  }

  ${DynamicCard.fragment}
`;
var DynamicCardCarousel = ({
  data,
  ariaLabel
}) => {
  var _a, _b;
  const {
    cards,
    nextButton,
    prevButton,
    carouselId
  } = data;
  const elementId = carouselId || `dcf-carousel-${uuidv48()}`;
  const carouselProps = {
    itemsVisible: {
      lg: 3,
      md: 2,
      sm: 1
    },
    pagingInset: true,
    navigationIconId: {
      next: `next-button-${elementId}`,
      prev: `prev-button-${elementId}`
    },
    buttonText: {
      nextButton: (_a = nextButton == null ? void 0 : nextButton.primary) != null ? _a : "",
      prevButton: (_b = prevButton == null ? void 0 : prevButton.primary) != null ? _b : ""
    },
    peek: true,
    itemsMaxHeight: true
  };
  const dynamicCardItems = cards == null ? void 0 : cards.map((dynamicCardItem, i) => {
    const {
      cardPayload,
      card,
      id: itemId
    } = dynamicCardItem;
    const key = itemId || `card-${i}`;
    if (cardPayload) {
      return /* @__PURE__ */React81.createElement(DynamicCard, {
        data: parseJson(cardPayload),
        key
      });
    }
    if (card) {
      return /* @__PURE__ */React81.createElement(DynamicCard, {
        data: card,
        key
      });
    }
    return null;
  });
  return /* @__PURE__ */React81.createElement(React81.Fragment, null, dynamicCardItems && (dynamicCardItems == null ? void 0 : dynamicCardItems.length) > 0 && /* @__PURE__ */React81.createElement(EGDSSpacing17, {
    margin: {
      blockstart: "half"
    }
  }, /* @__PURE__ */React81.createElement("div", {
    id: elementId,
    "aria-label": ariaLabel
  }, /* @__PURE__ */React81.createElement(EGDSCarousel, {
    ...carouselProps
  }, dynamicCardItems))));
};
DynamicCardCarousel.fragment = fragment51;
DynamicCardCarousel.displayName = "DynamicCardCarousel";

// ../internal/src/components/dynamic-card/dynamic-card.tsx
import React85 from "react";
import { EGDSCard as EGDSCard5, EGDSCardContentSection as EGDSCardContentSection2 } from "@egds/react-core/cards";
import { EGDSFigure as EGDSFigure2, EGDSFigureAspectRatioType as EGDSFigureAspectRatioType2, EGDSImage as EGDSImage2 } from "@egds/react-core/images";
import { EGDSLayoutFlex as EGDSLayoutFlex28, EGDSLayoutFlexItem as EGDSLayoutFlexItem16 } from "@egds/react-core/layout-flex";
import { gql as gql59 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/actionable/virtual-agent-control-actionable.tsx
import React83 from "react";
import { gql as gql57 } from "@shared-ui/apollo-client-wrapper";
import { useDialog as useDialog10 } from "@shared-ui/dialog-context";
import { useProvidedState as useProvidedState16 } from "@shared-ui/provided-state-context";

// ../internal/src/components/actionable/virtual-agent-control-actionable-element.tsx
import React82 from "react";
import { gql as gql56 } from "@shared-ui/apollo-client-wrapper";
var fragment52 = gql56`
  fragment VirtualAgentControlActionableElementFragment on VirtualAgentControlActionableElement {
    link {
      ...EGDSStandardLinkFragment
    }
    button {
      ...EGDSButtonFragment
    }
  }
  ${EGDSStandardLink.fragment}
  ${VirtualAgentEGDSButton.fragment}
`;
var VirtualAgentControlActionableElement = ({
  data,
  target,
  onClick,
  href
}) => {
  const {
    button,
    link
  } = data;
  const handleClick = React82.useCallback(() => {
    onClick == null ? void 0 : onClick();
  }, [onClick]);
  if (button) {
    return /* @__PURE__ */React82.createElement(VirtualAgentEGDSButton, {
      target,
      data: button,
      onClick: handleClick,
      href
    });
  }
  if (link) {
    return /* @__PURE__ */React82.createElement(EGDSStandardLink, {
      target,
      data: link,
      onClick: handleClick
    });
  }
  return null;
};
VirtualAgentControlActionableElement.fragment = fragment52;
VirtualAgentControlActionableElement.displayName = "VirtualAgentControlActionableElement";

// ../internal/src/components/actionable/virtual-agent-control-actionable.tsx
var fragment53 = gql57`
  fragment VirtualAgentControlActionableFragment on VirtualAgentControlActionable {
    element {
      ...VirtualAgentControlActionableElementFragment
    }
    action {
      ...VirtualAgentControlSendMessageActionFragment
      ... on VirtualAgentControlRedirectAction {
        url
        target
      }
      ... on VirtualAgentControlWebviewAction {
        uri
        toolbar {
          primary
        }
      }
      ... on VirtualAgentControlDialogAction {
        srcDoc
        toolbar {
          primary
        }
      }
    }
  }
  fragment VirtualAgentControlSendMessageActionFragment on VirtualAgentControlSendMessageAction {
    message
    payload
  }
  ${VirtualAgentControlActionableElement.fragment}
`;
var VirtualAgentControlActionable = ({
  data
}) => {
  var _a;
  const {
    element,
    action
  } = data;
  const [showWebview, setShowWebview] = React83.useState(false);
  const handleSend = useOperation("SEND_MESSAGE" /* SEND_MESSAGE */);
  const [state] = useProvidedState16();
  const vacId = useVacId();
  const embedded = ((_a = state == null ? void 0 : state.chatInstancesState) == null ? void 0 : _a[vacId].embedded) || false;
  const [, dialogActions, DialogComponent] = useDialog10("va-chat-container-dialog");
  const performAction = React83.useCallback(() => {
    var _a2, _b, _c, _d, _e;
    switch (action.__typename) {
      case "VirtualAgentControlSendMessageAction":
        {
          const elementText = (_d = (_c = (_a2 = element.button) == null ? void 0 : _a2.primary) != null ? _c : (_b = element.link) == null ? void 0 : _b.text) != null ? _d : "";
          const actionWithFallback = {
            ...action,
            message: ((_e = action.message) == null ? void 0 : _e.length) ? action.message : elementText
          };
          handleSend == null ? void 0 : handleSend(actionWithFallback);
          break;
        }
      case "VirtualAgentControlDialogAction":
      case "VirtualAgentControlWebviewAction":
        {
          dialogActions.openDialog();
          setShowWebview(true);
          break;
        }
      default:
        break;
    }
  }, [action, handleSend]);
  if (action.__typename === "VirtualAgentControlRedirectAction") {
    const {
      target
    } = action;
    const targetType = "INTERNAL" /* INTERNAL */ === target ? "_top" /* TOP */ : "_blank" /* BLANK */;
    return /* @__PURE__ */React83.createElement(VirtualAgentControlActionableElement, {
      data: element,
      href: action.url,
      target: targetType
    });
  }
  if (action.__typename === "VirtualAgentControlWebviewAction" || action.__typename === "VirtualAgentControlDialogAction") {
    return /* @__PURE__ */React83.createElement(React83.Fragment, null, showWebview && /* @__PURE__ */React83.createElement(Webview, {
      isOpen: showWebview,
      onCloseCallback: value => {
        setShowWebview(value);
        dialogActions.closeDialog();
      },
      action,
      DialogComponent: React83.memo(DialogComponent),
      embedded
    }), /* @__PURE__ */React83.createElement(VirtualAgentControlActionableElement, {
      data: element,
      onClick: performAction
    }));
  }
  return /* @__PURE__ */React83.createElement(VirtualAgentControlActionableElement, {
    data: element,
    onClick: performAction
  });
};
VirtualAgentControlActionable.fragment = fragment53;
VirtualAgentControlActionable.displayName = "VirtualAgentControlActionable";

// ../internal/src/components/dynamic-card/elements/horizontal-break.tsx
import React84 from "react";
import { EGDSSpacingHr as EGDSSpacingHr4 } from "@egds/react-core/spacing";
import { gql as gql58 } from "@shared-ui/apollo-client-wrapper";
var fragment54 = gql58`
  fragment VirtualAgentControlHorizontalBreakFragment on VirtualAgentControlHorizontalBreak {
    __typename
    label
  }
`;
var VirtualAgentControlHorizontalBreak = () => {
  return /* @__PURE__ */React84.createElement("span", null, /* @__PURE__ */React84.createElement(EGDSSpacingHr4, {
    blockMargin: "one"
  }));
};
VirtualAgentControlHorizontalBreak.fragment = fragment54;
VirtualAgentControlHorizontalBreak.displayName = "VirtualAgentControlHorizontalBreak";

// ../internal/src/components/dynamic-card/dynamic-card.tsx
var fragment55 = gql59`
  fragment VirtualAgentControlDynamicCardFragment on VirtualAgentControlDynamicCard {
    cover {
      url
      description
    }
    elements {
      ... on VirtualAgentControlActionable {
        ...VirtualAgentControlActionableFragment
      }
      ... on VirtualAgentControlHorizontalBreak {
        ...VirtualAgentControlHorizontalBreakFragment
      }
      ... on EGDSSpannableText {
        ...EGDSSpannableTextFragment
      }
      ... on EGDSStylizedText {
        ...EGDSStylizedTextFragment
      }
    }
  }
  ${VirtualAgentControlActionable.fragment}
  ${VirtualAgentControlHorizontalBreak.fragment}
  ${EGDSSpannableText.fragment}
  ${EGDSStylizedText.fragment}
`;
var registry3 = {
  VirtualAgentControlActionable,
  VirtualAgentControlHorizontalBreak,
  EGDSSpannableText,
  EGDSStylizedText
};
var adapt = elements => {
  const components = elements.reduce((accum, element, index) => {
    const {
      __typename
    } = element;
    const Component = registry3[__typename];
    if (Component) {
      const componentKey = `${element.__typename}-${index}`;
      accum.push(/* @__PURE__ */React85.createElement(EGDSLayoutFlexItem16, {
        key: componentKey
      }, /* @__PURE__ */React85.createElement(Component, {
        data: element
      })));
    }
    return accum;
  }, []);
  return components;
};
var VirtualAgentControlDynamicCard = ({
  data,
  maxWidth = "seventy_two"
}) => {
  const {
    elements,
    cover
  } = data;
  const components = adapt(elements);
  if (!components.length) {
    return null;
  }
  return /* @__PURE__ */React85.createElement(EGDSLayoutFlexItem16, {
    alignSelf: "start",
    maxWidth
  }, /* @__PURE__ */React85.createElement(EGDSCard5, {
    border: true
  }, /* @__PURE__ */React85.createElement(EGDSLayoutFlex28, {
    direction: "column"
  }, cover && /* @__PURE__ */React85.createElement(EGDSFigure2, {
    ratio: EGDSFigureAspectRatioType2.R16_9,
    imageFit: "cover"
  }, /* @__PURE__ */React85.createElement(EGDSImage2, {
    src: cover.url,
    alt: cover.description,
    placeholderImage: true
  })), /* @__PURE__ */React85.createElement(EGDSCardContentSection2, null, /* @__PURE__ */React85.createElement(EGDSLayoutFlex28, {
    direction: "column",
    space: "two"
  }, components)))));
};
VirtualAgentControlDynamicCard.fragment = fragment55;
VirtualAgentControlDynamicCard.displayName = "VirtualAgentControlDynamicCard";

// ../internal/src/components/messages/inbound-message/components/inbound-dynamic-card-message.tsx
var fragment56 = gql60`
  fragment VirtualAgentControlInboundDynamicCardMessageFragment on VirtualAgentControlInboundDynamicCardMessage {
    eyebrow
    webviewUriAppender
    accessibilityLabel
    metadata {
      ... on VirtualAgentControlMessageMetadata {
        timeStamp
        messageId
        messageContextAccessibilityLabel
        newMessageAccessibilityAnnouncement
        source
      }
    }
    stateUpdates {
      chatInput {
        interactionMode
        chatInputStatus
      }
    }
    analytics {
      ... on VirtualAgentControlImpressionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlInteractionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlOutcomeAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlPageViewAnalyticEvent {
        eventName
        eventVersion
        payload
      }
    }
    quickReplies {
      ...VirtualAgentControlQuickReplyItemFragment
    }
    dynamicCard {
      ...VirtualAgentControlDynamicCardFragment
    }
    carousel {
      ...EGDSCarouselFragment
    }
    cardPayload
    cardCarousel {
      ...DynamicCardCarouselFragment
    }
  }

  fragment EGDSCarouselFragment on EGDSCarousel {
    nextButton {
      ...EGDSButtonFragment
    }
    previousButton {
      ...EGDSButtonFragment
    }
    items {
      ...VirtualAgentControlDynamicCardItemFragment
    }
  }

  fragment VirtualAgentControlDynamicCardItemFragment on VirtualAgentControlDynamicCardItem {
    id
    card {
      ...VirtualAgentControlDynamicCardFragment
    }
  }

  fragment DynamicCardCarouselFragment on DynamicCardCarousel {
    nextButton {
      ...EGDSButtonFragment
    }
    prevButton {
      ...EGDSButtonFragment
    }
    cards {
      ...DynamicCardCarouselItemFragment
    }
    carouselId
  }

  fragment DynamicCardCarouselItemFragment on DynamicCardCarouselItem {
    id
    cardPayload
    card {
      ...DynamicCardFragment
    }
  }

  ${VirtualAgentControlDynamicCard.fragment}
  ${VirtualAgentControlQuickReplyItem.fragment}
  ${DynamicCard.fragment}
`;
var VirtualAgentControlInboundDynamicCardMessage = ({
  data
}) => {
  var _a, _b;
  const {
    dynamicCard,
    carousel,
    cardPayload,
    cardCarousel,
    accessibilityLabel,
    metadata: {
      messageId,
      source
    },
    webviewUriAppender
  } = data;
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  const {
    dcfUnavailableLine1,
    dcfUnavailableLine2
  } = messageContent || {};
  const shouldShowDcfUnavailableMessage = !!dcfUnavailableLine1 && !!dcfUnavailableLine2;
  const dynamicCardContextData = createVacDynamicCardContext({
    webviewUriAppender,
    source
  });
  if (carousel) {
    const {
      items,
      nextButton,
      previousButton
    } = carousel;
    const props = {
      buttonText: {
        nextButton: (_a = nextButton == null ? void 0 : nextButton.primary) != null ? _a : "",
        prevButton: (_b = previousButton == null ? void 0 : previousButton.primary) != null ? _b : ""
      }
    };
    const cards = items.map((cardItem, i) => {
      if (cardItem.__typename === "VirtualAgentControlDynamicCardItem") {
        const {
          card
        } = cardItem;
        return /* @__PURE__ */React86.createElement(VirtualAgentControlDynamicCard, {
          data: card,
          maxWidth: "full_width",
          key: `card-${i}`
        });
      }
      return null;
    });
    return /* @__PURE__ */React86.createElement(EGDSSpacing18, {
      margin: {
        blockstart: "half"
      }
    }, /* @__PURE__ */React86.createElement("div", {
      id: messageId,
      "aria-label": accessibilityLabel != null ? accessibilityLabel : ""
    }, /* @__PURE__ */React86.createElement(EGDSCarousel2, {
      buttonText: props.buttonText,
      itemsVisible: {
        lg: 3,
        md: 2,
        sm: 1
      },
      navigationIconId: {
        next: `next-button-${messageId}`,
        prev: `prev-button-${messageId}`
      },
      pagingInset: true,
      pageBy: 1,
      peek: true,
      itemsMaxHeight: true
    }, cards)));
  }
  if (dynamicCard) {
    return /* @__PURE__ */React86.createElement(EGDSSpacing18, {
      margin: {
        blockstart: "half"
      }
    }, /* @__PURE__ */React86.createElement("div", {
      style: {
        minWidth: "256px"
      },
      "aria-label": accessibilityLabel != null ? accessibilityLabel : "",
      id: messageId
    }, /* @__PURE__ */React86.createElement(VirtualAgentControlDynamicCard, {
      data: dynamicCard
    })));
  }
  if (cardPayload) {
    return /* @__PURE__ */React86.createElement(EGDSLayoutFlexItem17, {
      alignSelf: "start",
      maxWidth: "seventy_two",
      "aria-label": accessibilityLabel != null ? accessibilityLabel : "",
      id: messageId
    }, /* @__PURE__ */React86.createElement(EGDSSpacing18, {
      margin: {
        blockstart: "half"
      }
    }, /* @__PURE__ */React86.createElement("div", {
      style: {
        minWidth: "256px"
      }
    }, /* @__PURE__ */React86.createElement(DynamicCardContextProvider, {
      value: dynamicCardContextData
    }, /* @__PURE__ */React86.createElement(DynamicCard, {
      data: parseJson(cardPayload)
    })))));
  }
  if (cardCarousel) {
    const cardCarouselWithMessageId = {
      ...cardCarousel,
      carouselId: messageId
    };
    return /* @__PURE__ */React86.createElement(DynamicCardContextProvider, {
      value: dynamicCardContextData
    }, /* @__PURE__ */React86.createElement(DynamicCardCarousel, {
      data: cardCarouselWithMessageId,
      ariaLabel: accessibilityLabel != null ? accessibilityLabel : ""
    }));
  }
  return shouldShowDcfUnavailableMessage ? /* @__PURE__ */React86.createElement(EGDSLayoutFlex29, {
    space: "three",
    wrap: "wrap"
  }, /* @__PURE__ */React86.createElement(EGDSSpacing18, {
    margin: {
      blockstart: "three"
    },
    padding: "six"
  }, /* @__PURE__ */React86.createElement(EGDSCard6, {
    padded: true,
    border: true
  }, /* @__PURE__ */React86.createElement(EGDSText24, {
    inline: "strong"
  }, dcfUnavailableLine1), /* @__PURE__ */React86.createElement(EGDSText24, {
    size: 300,
    spacing: "two"
  }, dcfUnavailableLine2)))) : null;
};
VirtualAgentControlInboundDynamicCardMessage.fragment = fragment56;
VirtualAgentControlInboundDynamicCardMessage.displayName = "VirtualAgentControlInboundDynamicCardMessage";

// ../internal/src/components/messages/inbound-message/components/inbound-file-message.tsx
import React88 from "react";
import { v4 as uuidv49 } from "uuid";
import { EGDSLayoutFlex as EGDSLayoutFlex31, EGDSLayoutFlexItem as EGDSLayoutFlexItem19 } from "@egds/react-core/layout-flex";
import { gql as gql62 } from "@shared-ui/apollo-client-wrapper";
import { SystemEventLevel as SystemEventLevel11 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState18 } from "@shared-ui/provided-state-context";

// ../internal/src/components/attachment-card/attachment-card.tsx
import React87 from "react";
import { EGDSCard as EGDSCard7, EGDSCardFeaturedHeader } from "@egds/react-core/cards";
import { EGDSIcon as EGDSIcon8, EGDSIconSize as EGDSIconSize6 } from "@egds/react-core/icons";
import { EGDSLayoutFlex as EGDSLayoutFlex30, EGDSLayoutFlexItem as EGDSLayoutFlexItem18 } from "@egds/react-core/layout-flex";
import { EGDSLoader as EGDSLoader2 } from "@egds/react-core/loader";
import { EGDSSpacing as EGDSSpacing19 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText25 } from "@egds/react-core/text";
import { gql as gql61 } from "@shared-ui/apollo-client-wrapper";
import { useProvidedState as useProvidedState17 } from "@shared-ui/provided-state-context";
var fragment57 = gql61`
  fragment VirtualAgentControlAttachmentCardFragment on EGDSStandardLink {
    text
    action {
      resource {
        value
      }
      target
      accessibility
      useRelativePath
    }
    disabled
    size
  }
`;
var VirtualAgentControlAttachmentCard = ({
  data,
  cardType,
  onAttachmentClick,
  uniqueAttachmentId,
  size,
  mediaUrl
}) => {
  const [state] = useProvidedState17();
  const attachmentStringMap = use_prefetch_localization_content_default(LocalizationContentKeys.ATTACHMENT);
  const {
    messagesState: {
      attachmentStatusMap
    }
  } = state;
  const {
    text,
    action: {
      accessibility
    }
  } = data;
  if (!text && !uniqueAttachmentId) {
    return null;
  }
  if (!attachmentStatusMap[uniqueAttachmentId]) {
    return null;
  }
  const {
    loading,
    operationType,
    hasError,
    isSuccess
  } = attachmentStatusMap[uniqueAttachmentId];
  const handleClick = event => {
    onAttachmentClick == null ? void 0 : onAttachmentClick(event);
  };
  const renderIcon = () => {
    if (loading) {
      return /* @__PURE__ */React87.createElement(EGDSLoader2, {
        isSmall: true,
        isDarkBackground: cardType === "VirtualAgentControlOutboundFileMessage"
      });
    }
    if (hasError) {
      return /* @__PURE__ */React87.createElement(EGDSIcon8, {
        name: "error",
        theme: "negative",
        size: EGDSIconSize6.SMALL
      });
    }
    if (isSuccess) {
      return /* @__PURE__ */React87.createElement(EGDSIcon8, {
        name: "check_circle",
        theme: "positive",
        size: EGDSIconSize6.SMALL
      });
    }
    return /* @__PURE__ */React87.createElement(EGDSIcon8, {
      name: "download",
      theme: "primary",
      size: EGDSIconSize6.SMALL
    });
  };
  const renderStatusText = () => {
    if (operationType === "upload") {
      if (hasError) {
        const maxFileSize = "25MB";
        if (size && parseInt(size, 10) > maxFileSizeinInteger) {
          return localizeTemplateLiteral(attachmentStringMap.uploadFileSizeError, "maxFileSize", maxFileSize);
        }
        return attachmentStringMap.uploadError;
      }
      if (loading) {
        return attachmentStringMap.uploadProcessing;
      }
      return attachmentStringMap.uploadSuccess;
    }
    if (operationType === "download") {
      if (hasError) {
        return attachmentStringMap.downloadError;
      }
      if (loading) {
        return attachmentStringMap.downloadProcessing;
      }
      if (isSuccess) {
        return attachmentStringMap.downloadSuccess;
      }
      return "";
    }
  };
  const renderAttachmentCardContent = () => {
    return /* @__PURE__ */React87.createElement(EGDSLayoutFlex30, {
      direction: "row",
      "aria-label": accessibility != null ? accessibility : ""
    }, /* @__PURE__ */React87.createElement(EGDSLayoutFlexItem18, {
      alignSelf: "start",
      "aria-hidden": "true"
    }, /* @__PURE__ */React87.createElement(EGDSSpacing19, {
      margin: {
        inlineend: "one"
      }
    }, renderIcon())), /* @__PURE__ */React87.createElement(EGDSLayoutFlexItem18, null, /* @__PURE__ */React87.createElement(EGDSLayoutFlex30, {
      direction: "column"
    }, /* @__PURE__ */React87.createElement(EGDSLayoutFlexItem18, null, /* @__PURE__ */React87.createElement(EGDSStandardLink, {
      target: mediaUrl && "_blank",
      theme: cardType === "VirtualAgentControlOutboundFileMessage" ? "inverse" : "default",
      rel: mediaUrl ? " noopener" : "",
      onClick: handleClick,
      data: {
        __typename: "EGDSStandardLink",
        text: `${text}`,
        action: {
          __typename: "UILinkAction",
          resource: {
            __typename: "SelfReferencedURI",
            value: `${mediaUrl || "#"}`
          }
        },
        disabled: false,
        icon: null,
        iconPosition: null
      }
    })), /* @__PURE__ */React87.createElement(EGDSLayoutFlexItem18, null, /* @__PURE__ */React87.createElement(EGDSSpacing19, {
      margin: {
        blockstart: "one"
      }
    }, /* @__PURE__ */React87.createElement(EGDSText25, {
      size: 200,
      theme: cardType === "VirtualAgentControlOutboundFileMessage" ? "inverse" : "default",
      "data-stid": "attachment-status"
    }, renderStatusText()))))));
  };
  const renderAttachmentCard = () => {
    if (cardType === "VirtualAgentControlInboundFileMessage") {
      return /* @__PURE__ */React87.createElement(EGDSCard7, {
        padded: true,
        className: loading ? "is-disabled" : "",
        backgroundTheme: "secondary"
      }, renderAttachmentCardContent());
    }
    return /* @__PURE__ */React87.createElement(EGDSCard7, {
      padded: true,
      className: loading ? "is-disabled" : ""
    }, /* @__PURE__ */React87.createElement(EGDSCardFeaturedHeader, null, renderAttachmentCardContent()));
  };
  return /* @__PURE__ */React87.createElement(EGDSLayoutFlex30, {
    direction: "column"
  }, /* @__PURE__ */React87.createElement(EGDSLayoutFlexItem18, {
    alignSelf: cardType === "VirtualAgentControlInboundFileMessage" ? "start" : "end",
    maxWidth: "seventy_two"
  }, /* @__PURE__ */React87.createElement(EGDSSpacing19, {
    margin: {
      blockstart: "half"
    }
  }, renderAttachmentCard())));
};
VirtualAgentControlAttachmentCard.fragment = fragment57;
VirtualAgentControlAttachmentCard.displayName = "VirtualAgentControlAttachmentCard";

// ../internal/src/components/messages/inbound-message/components/inbound-file-message.tsx
var fragment58 = gql62`
  fragment VirtualAgentControlInboundFileMessageFragment on VirtualAgentControlInboundFileMessage {
    metadata {
      messageId
      timeStamp
      messageContextAccessibilityLabel
      newMessageAccessibilityAnnouncement
    }
    attachmentLink {
      ...VirtualAgentControlAttachmentCardFragment
    }
    stateUpdates {
      chatInput {
        interactionMode
        chatInputStatus
      }
    }
    attachmentMetadata {
      uniqueAttachmentId
      mediaType
    }
    accessibilityLabel
  }
  ${VirtualAgentControlAttachmentCard.fragment}
`;
var VirtualAgentControlInboundFileMessage = ({
  data
}) => {
  const {
    attachmentLink,
    attachmentMetadata,
    metadata: {
      messageId
    }
  } = data;
  const [uniqueAttachmentId, setUniqueAttachmentId] = React88.useState("");
  const [mediaUrl, setMediaUrl] = React88.useState("");
  const [conversation] = useConversationState();
  const {
    trackEvent
  } = useAnalytics();
  const [state, dispatch] = useProvidedState18();
  const [, downloadFile2] = useAttachment();
  const loggerV2 = useLogging(EVENTS);
  if (!attachmentLink) {
    return null;
  }
  const {
    messagesState: {
      attachmentStatusMap
    }
  } = state;
  const {
    action: {
      resource: {
        value
      },
      useRelativePath
    }
  } = attachmentLink;
  const getUniqueAttachmentIdFromResource = url => {
    const urlArrayFilePart = url.split("/files")[1];
    return urlArrayFilePart.split("/")[1];
  };
  const isDirectDownloadUrl = url => {
    if (url == null ? void 0 : url.match(/^blob:|^\/partners|^#vacservice|^#\/vacservice/)) {
      return void 0;
    }
    return url;
  };
  React88.useEffect(() => {
    if (attachmentMetadata == null ? void 0 : attachmentMetadata.uniqueAttachmentId) {
      setUniqueAttachmentId(attachmentMetadata == null ? void 0 : attachmentMetadata.uniqueAttachmentId);
      if (!attachmentStatusMap[attachmentMetadata.uniqueAttachmentId]) {
        dispatch({
          type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
          payload: {
            uniqueAttachmentId: attachmentMetadata.uniqueAttachmentId,
            attachmentStatus: {
              loading: false,
              hasError: false,
              isSuccess: false,
              operationType: AttachmentOperationTypes.DOWNLOAD
            }
          }
        });
      }
    } else {
      const id = !useRelativePath ? uuidv49() : getUniqueAttachmentIdFromResource(value);
      setUniqueAttachmentId(id);
      dispatch({
        type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
        payload: {
          uniqueAttachmentId: id,
          attachmentStatus: {
            loading: false,
            hasError: false,
            isSuccess: false,
            operationType: AttachmentOperationTypes.DOWNLOAD
          }
        }
      });
    }
  }, [value, attachmentMetadata == null ? void 0 : attachmentMetadata.uniqueAttachmentId]);
  const isViewableUrl = url => {
    return !!(url == null ? void 0 : url.match(/^blob:/));
  };
  const downloadSubmittedEvent = () => {
    trackEvent(buildClickstreamAdditionalContextPayload("ATTACHMENT_FILE_DOWNLOAD_SUBMITTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }));
  };
  const downloadSucessfulEvent = () => {
    trackEvent(buildClickstreamAdditionalContextPayload("ATTACHMENT_FILE_DOWNLOAD_SUCCEEDED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }));
  };
  const downloadFailureEvent = error => {
    trackEvent(buildClickstreamAdditionalContextErrorPayload("ATTACHMENT_FILE_DOWNLOAD_FAILED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }, {
      error_name: error != null ? error : "network_error"
    }));
    loggerV2("RUNTIME", {
      eventName: "vac.runtime" /* RUNTIME */,
      eventlevel: SystemEventLevel11.ERROR,
      operationName: "VAC_DownloadFailureRuntime" /* VAC_DOWNLOAD_FAILURE_RUNTIME */,
      logSource: PREFIX
    });
  };
  const previewFileEvent = () => {
    trackEvent(buildClickstreamAdditionalContextPayload("ATTACHMENT_FILE_PREVIEW_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }));
  };
  const handleDownload = async event => {
    if (isViewableUrl(mediaUrl)) {
      previewFileEvent();
      return Promise.resolve();
    }
    event.stopPropagation();
    event.preventDefault();
    downloadSubmittedEvent();
    dispatch({
      type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
      payload: {
        uniqueAttachmentId,
        attachmentStatus: {
          loading: true,
          hasError: false,
          isSuccess: false,
          operationType: AttachmentOperationTypes.DOWNLOAD
        }
      }
    });
    try {
      let trimmedMediaUrl = value;
      if (value.startsWith("#")) {
        trimmedMediaUrl = value.slice(1);
      }
      const response = await downloadFile2(trimmedMediaUrl);
      const {
        file_data: {
          bytes,
          file_metadata: {
            content_type
          }
        }
      } = response;
      downloadSucessfulEvent();
      const blobUrl = decodeFileBytes({
        bytes,
        mediaType: content_type
      });
      setMediaUrl(blobUrl);
      dispatch({
        type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
        payload: {
          uniqueAttachmentId,
          attachmentStatus: {
            loading: false,
            hasError: false,
            isSuccess: true,
            operationType: AttachmentOperationTypes.DOWNLOAD
          }
        }
      });
    } catch (err) {
      downloadFailureEvent(err == null ? void 0 : err.message);
      dispatch({
        type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
        payload: {
          uniqueAttachmentId,
          attachmentStatus: {
            loading: false,
            hasError: true,
            isSuccess: false,
            operationType: AttachmentOperationTypes.DOWNLOAD
          }
        }
      });
    }
  };
  const getOnAttachmentClickHandler = () => {
    if (!useRelativePath) {
      return () => {};
    }
    return event => handleDownload(event);
  };
  return /* @__PURE__ */React88.createElement(EGDSLayoutFlex31, {
    direction: "column",
    id: messageId
  }, /* @__PURE__ */React88.createElement(EGDSLayoutFlexItem19, {
    alignSelf: "end"
  }, attachmentLink && /* @__PURE__ */React88.createElement(VirtualAgentControlAttachmentCard, {
    data: attachmentLink,
    mediaUrl: isDirectDownloadUrl(value) || mediaUrl,
    uniqueAttachmentId,
    onAttachmentClick: getOnAttachmentClickHandler(),
    cardType: data.__typename
  })));
};
VirtualAgentControlInboundFileMessage.fragment = fragment58;
VirtualAgentControlInboundFileMessage.displayName = "VirtualAgentControlInboundFileMessage";

// ../internal/src/components/messages/inbound-message/components/inbound-text-message.tsx
import React90 from "react";
import Linkify from "react-linkify";
import { EGDSCard as EGDSCard8 } from "@egds/react-core/cards";
import { EGDSLayoutFlex as EGDSLayoutFlex32, EGDSLayoutFlexItem as EGDSLayoutFlexItem20 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing20 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText26 } from "@egds/react-core/text";
import { gql as gql63 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/helpers/linkify-helpers.tsx
import React89 from "react";
import { EGDSLink as EGDSLink5 } from "@egds/react-core/link";
var getLinkComponentDecorator = (href, text, key, theme) => {
  return /* @__PURE__ */React89.createElement(EGDSLink5, {
    inline: true,
    key,
    theme
  }, /* @__PURE__ */React89.createElement("a", {
    href,
    target: "_blank",
    rel: "noopener"
  }, text));
};

// ../internal/src/components/messages/inbound-message/components/inbound-text-message.tsx
var fragment59 = gql63`
  fragment VirtualAgentControlInboundTextMessageFragment on VirtualAgentControlInboundTextMessage {
    eyebrow
    metadata {
      ... on VirtualAgentControlMessageMetadata {
        timeStamp
        messageId
        isActivityIndicatorMessage
        messageAuthor
        messageContextAccessibilityLabel
        newMessageAccessibilityAnnouncement
      }
    }
    stateUpdates {
      chatInput {
        interactionMode
        chatInputStatus
      }
    }
    analytics {
      ... on VirtualAgentControlImpressionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlInteractionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlOutcomeAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlPageViewAnalyticEvent {
        eventName
        eventVersion
        payload
      }
    }
    quickReplies {
      ...VirtualAgentControlQuickReplyItemFragment
    }
    primary
  }
  ${VirtualAgentControlQuickReplyItem.fragment}
`;
var VirtualAgentControlInboundTextMessage = ({
  data
}) => {
  const {
    primary,
    metadata: {
      messageId
    }
  } = data;
  const linkComponentDecorator = (href, text, key) => {
    return getLinkComponentDecorator(href, text, key, "default");
  };
  const transformedMessageText = (messageText, mId) => {
    return messageText.split(/[\n\r]/).map((line, index, array) => {
      const uidString = `${line}+${index}`;
      return /* @__PURE__ */React90.createElement(Linkify, {
        componentDecorator: linkComponentDecorator,
        key: `${mId}-${uidString}`
      }, line, index < array.length - 1 ? /* @__PURE__ */React90.createElement("br", null) : "");
    });
  };
  return /* @__PURE__ */React90.createElement(EGDSLayoutFlex32, {
    direction: "column",
    id: messageId
  }, /* @__PURE__ */React90.createElement(EGDSLayoutFlexItem20, {
    alignSelf: "start",
    maxWidth: "seventy_two"
  }, /* @__PURE__ */React90.createElement(EGDSSpacing20, {
    margin: {
      blockstart: "half"
    }
  }, /* @__PURE__ */React90.createElement(EGDSCard8, {
    padded: true,
    backgroundTheme: "secondary"
  }, /* @__PURE__ */React90.createElement(EGDSText26, {
    size: 300,
    spacing: "half",
    wordBreak: true
  }, transformedMessageText(primary, messageId))))));
};
VirtualAgentControlInboundTextMessage.fragment = fragment59;
VirtualAgentControlInboundTextMessage.displayName = "VirtualAgentControlInboundTextMessage";

// ../internal/src/components/messages/inbound-message/inbound-message.tsx
var fragment60 = gql64`
  fragment VirtualAgentControlInboundMessageFragment on VirtualAgentControlInboundMessage {
    ...VirtualAgentControlInboundTextMessageFragment
    ...VirtualAgentControlInboundCardMessageFragment
    ...VirtualAgentControlInboundDynamicCardMessageFragment
    ...VirtualAgentControlInboundFileMessageFragment
    ...VirtualAgentControlInboundQuickReplyMessageFragment
  }

  fragment VirtualAgentControlInboundQuickReplyMessageFragment on VirtualAgentControlInboundQuickReplyMessage {
    eyebrow
    metadata {
      ... on VirtualAgentControlMessageMetadata {
        timeStamp
        messageId
        messageContextAccessibilityLabel
        newMessageAccessibilityAnnouncement
      }
    }
    stateUpdates {
      chatInput {
        interactionMode
        chatInputStatus
      }
    }
    analytics {
      ... on VirtualAgentControlImpressionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlInteractionAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlOutcomeAnalyticEvent {
        eventName
        eventVersion
        payload
      }
      ... on VirtualAgentControlPageViewAnalyticEvent {
        eventName
        eventVersion
        payload
      }
    }
    quickReplies {
      ...VirtualAgentControlQuickReplyItemFragment
    }
  }

  ${VirtualAgentControlInboundTextMessage.fragment}
  ${VirtualAgentControlInboundCardMessage.fragment}
  ${VirtualAgentControlInboundDynamicCardMessage.fragment}
  ${VirtualAgentControlInboundFileMessage.fragment}
  ${VirtualAgentControlQuickReplyItem.fragment}
`;
var VirtualAgentControlInboundMessage = ({
  data
}) => {
  switch (data.__typename) {
    case "VirtualAgentControlInboundTextMessage":
      return /* @__PURE__ */React91.createElement(VirtualAgentControlInboundTextMessage, {
        data
      });
    case "VirtualAgentControlInboundCardMessage":
      return /* @__PURE__ */React91.createElement(VirtualAgentControlInboundCardMessage, {
        data
      });
    case "VirtualAgentControlInboundDynamicCardMessage":
      return /* @__PURE__ */React91.createElement(VirtualAgentControlInboundDynamicCardMessage, {
        data
      });
    case "VirtualAgentControlInboundFileMessage":
      return /* @__PURE__ */React91.createElement(VirtualAgentControlInboundFileMessage, {
        data
      });
    default:
      return null;
  }
};
VirtualAgentControlInboundMessage.fragment = fragment60;
VirtualAgentControlInboundMessage.displayName = "VirtualAgentControlInboundMessage";

// ../internal/src/components/messages/inbound-message-group/inbound-message-group.tsx
var fragment61 = gql65`
  fragment VirtualAgentControlInboundMessageGroupFragment on VirtualAgentControlInboundMessageGroup {
    eyebrow
    messages {
      ...VirtualAgentControlInboundMessageFragment
    }
    timeStamp
  }
  ${VirtualAgentControlInboundMessage.fragment}
`;
var VirtualAgentControlInboundMessageGroup = ({
  data
}) => {
  var _a;
  const {
    eyebrow,
    messages,
    timeStamp
  } = data;
  const chatBubbleTimestamp = useLocaleDateTimestamp(timeStamp || "");
  const {
    timestampAccessibilityLabelTemplate
  } = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE) || {};
  const containerRef = React92.useRef(null);
  const [, conversationMessages, messagesDispatch] = useMessagesState();
  const groupId = React92.useMemo(() => uuidv410(), []);
  const timeStampId = React92.useMemo(() => `timeStamp-${uuidv410()}`, []);
  const liveAnnouncementId = React92.useMemo(() => `liveAnnounce-${uuidv410()}`, []);
  const visibleMessages = (_a = messages == null ? void 0 : messages.filter(message => message.__typename !== "VirtualAgentControlInboundQuickReplyMessage")) != null ? _a : [];
  const messageIds = visibleMessages.map(message => message.metadata.messageId);
  useEffect9(() => {
    const shouldAnnounceMessage = visibleMessages.findIndex(message => message.metadata.messageId === (conversationMessages == null ? void 0 : conversationMessages.announceMessageId)) !== -1;
    if (shouldAnnounceMessage && visibleMessages[0].metadata.newMessageAccessibilityAnnouncement) {
      ariaAnnounceLive(visibleMessages[0].metadata.newMessageAccessibilityAnnouncement, Level3.POLITE, containerRef, liveAnnouncementId);
      messagesDispatch({
        type: "SET_ANNOUNCE_MESSAGE_ID" /* SET_ANNOUNCE_MESSAGE_ID */,
        payload: {
          messageId: null
        }
      });
    }
  }, []);
  if (!messages.length) {
    return null;
  }
  const getParticipantAccessibilityLabel = () => {
    var _a2, _b;
    return (_b = (_a2 = visibleMessages[0]) == null ? void 0 : _a2.metadata) == null ? void 0 : _b.messageContextAccessibilityLabel;
  };
  return /* @__PURE__ */React92.createElement(EGDSLayoutFlex33, {
    domRef: containerRef,
    direction: "column",
    role: "group",
    "aria-labelledby": `${[groupId, ...messageIds, timeStampId].join(" ")}`
  }, eyebrow && /* @__PURE__ */React92.createElement(EGDSLayoutFlexItem21, {
    alignSelf: "start"
  }, /* @__PURE__ */React92.createElement(EGDSSpacing21, null, getParticipantAccessibilityLabel() ? /* @__PURE__ */React92.createElement(EGDSText27, {
    size: 200,
    spacing: "half",
    theme: "standard",
    "aria-hidden": "true"
  }, eyebrow) : /* @__PURE__ */React92.createElement(EGDSText27, {
    size: 200,
    spacing: "half",
    theme: "standard"
  }, eyebrow))), getParticipantAccessibilityLabel() ? /* @__PURE__ */React92.createElement("span", {
    id: groupId,
    className: "is-visually-hidden"
  }, getParticipantAccessibilityLabel()) : null, visibleMessages.map((message, index) => /* @__PURE__ */React92.createElement(VirtualAgentControlInboundMessage, {
    data: message,
    key: `message-${index}`
  })), timeStamp && /* @__PURE__ */React92.createElement(EGDSLayoutFlexItem21, {
    alignSelf: "start"
  }, /* @__PURE__ */React92.createElement(EGDSText27, {
    size: 200,
    spacing: "half",
    theme: "standard",
    "aria-hidden": "true"
  }, chatBubbleTimestamp)), /* @__PURE__ */React92.createElement("span", {
    id: timeStampId,
    className: "is-visually-hidden"
  }, timestampAccessibilityLabelTemplate && localizeTemplateLiteral(timestampAccessibilityLabelTemplate, "timeStamp", chatBubbleTimestamp)));
};
VirtualAgentControlInboundMessageGroup.fragment = fragment61;
VirtualAgentControlInboundMessageGroup.displayName = "VirtualAgentControlInboundMessageGroup";

// ../internal/src/components/messages/separator/message-separator.tsx
import React93 from "react";
import { v4 as uuidv411 } from "uuid";
import { EGDSLayoutFlex as EGDSLayoutFlex34, EGDSLayoutFlexItem as EGDSLayoutFlexItem22 } from "@egds/react-core/layout-flex";
import { EGDSSpacing as EGDSSpacing22, EGDSSpacingHr as EGDSSpacingHr5 } from "@egds/react-core/spacing";
import { EGDSHeading as EGDSHeading10 } from "@egds/react-core/text";
import { Level as Level4 } from "@egds/react-core/utils";
import { gql as gql66 } from "@shared-ui/apollo-client-wrapper";
var fragment62 = gql66`
  fragment VirtualAgentControlMessageSeparatorFragment on VirtualAgentControlMessageSeparator {
    primary
    timeStamp: timestamp
    meta {
      participantId
    }
  }
`;
var VirtualAgentControlMessageSeparator = ({
  data
}) => {
  const {
    primary,
    meta
  } = data;
  const {
    participantId
  } = meta != null ? meta : {};
  const sessionMarkerLabel = useSessionMarkerLabel(primary);
  const containerRef = React93.useRef(null);
  const liveAnnouncementId = React93.useMemo(() => `liveAnnounce-${uuidv411()}`, []);
  React93.useEffect(() => {
    if (participantId) {
      ariaAnnounceLive(sessionMarkerLabel, Level4.POLITE, containerRef, liveAnnouncementId);
    }
  }, []);
  return /* @__PURE__ */React93.createElement(React93.Fragment, null, primary && /* @__PURE__ */React93.createElement(EGDSLayoutFlex34, {
    domRef: containerRef,
    direction: "row",
    wrap: "wrap",
    alignItems: "center"
  }, /* @__PURE__ */React93.createElement(EGDSLayoutFlexItem22, {
    grow: 10,
    shrink: 0
  }, /* @__PURE__ */React93.createElement("div", {
    "aria-hidden": "true"
  }, /* @__PURE__ */React93.createElement(EGDSSpacingHr5, {
    blockMargin: "two"
  }))), /* @__PURE__ */React93.createElement(EGDSLayoutFlexItem22, null, /* @__PURE__ */React93.createElement(EGDSSpacing22, {
    padding: {
      inline: "two"
    }
  }, /* @__PURE__ */React93.createElement(EGDSHeading10, {
    tag: "h2",
    size: 8
  }, sessionMarkerLabel))), /* @__PURE__ */React93.createElement(EGDSLayoutFlexItem22, {
    grow: 10,
    shrink: 0
  }, /* @__PURE__ */React93.createElement("div", {
    "aria-hidden": "true"
  }, /* @__PURE__ */React93.createElement(EGDSSpacingHr5, {
    blockMargin: "two"
  })))));
};
VirtualAgentControlMessageSeparator.fragment = fragment62;
VirtualAgentControlMessageSeparator.displayName = "VirtualAgentControlMessageSeparator";

// ../internal/src/components/messages/vac-messages-mapper/vac-messages-mapper.tsx
import React99 from "react";

// ../internal/src/components/messages/message-loader/message-loader.tsx
import React94 from "react";
import { useInView } from "react-intersection-observer";
import { v4 as uuidv412 } from "uuid";
import { EGDSIcon as EGDSIcon9 } from "@egds/react-core/icons";
import { EGDSLayoutFlex as EGDSLayoutFlex35, EGDSLayoutFlexItem as EGDSLayoutFlexItem23 } from "@egds/react-core/layout-flex";
import { EGDSLoader as EGDSLoader3 } from "@egds/react-core/loader";
import { Level as Level5 } from "@egds/react-core/utils";
import { gql as gql67 } from "@shared-ui/apollo-client-wrapper";
import { useProvidedState as useProvidedState19 } from "@shared-ui/provided-state-context";
var fragment63 = gql67`
  fragment VirtualAgentControlMessageLoaderFragment on VirtualAgentControlMessageLoader {
    conversationHistoryAttributes {
      key
      value
    }
    loaderText
    accessibilityText
    retryIcon {
      token
      size
      theme
    }
    retryText {
      text {
        ...EGDSSpannableTextFragment
      }
    }
  }
  ${EGDSSpannableText.fragment}
`;
var VirtualAgentControlMessageLoader = ({
  data
}) => {
  var _a, _b, _c;
  const {
    retryIcon,
    retryText,
    loaderText,
    accessibilityText,
    conversationHistoryAttributes
  } = data;
  const {
    ref,
    inView
  } = useInView({
    threshold: 0.7
    // Percentage of the user visibility. 1.0 means every pixel is visible in the viewport.
  });
  const [showRetryIcon, setShowRetryIcon] = React94.useState(false);
  const [, conversationDispatch, conversationContext] = useConversationState();
  const [state, dispatch] = useProvidedState19();
  const fetchHistoryMessagesStatus = (_a = state.messagesState.messagesByConversationId[conversationContext.conversationId]) == null ? void 0 : _a.fetchHistoryMessagesStatus;
  const loaderContainerRef = React94.useRef(null);
  const messageLoadingAnnounceId = React94.useMemo(() => `messageLoadingAnnounce-${uuidv412()}`, []);
  React94.useEffect(() => {
    if (conversationHistoryAttributes) {
      conversationDispatch({
        type: "UPDATE_CONVERSATION" /* UPDATE_CONVERSATION */,
        payload: {
          conversationHistoryAttributes
        }
      });
    } else {
      conversationDispatch({
        type: "UPDATE_CONVERSATION" /* UPDATE_CONVERSATION */,
        payload: {
          conversationHistoryAttributes: null
        }
      });
    }
  }, [conversationHistoryAttributes]);
  const handleLoadingAnnouncement = () => {
    if (accessibilityText) {
      ariaAnnounceLive(accessibilityText, Level5.POLITE, loaderContainerRef, messageLoadingAnnounceId, true);
    }
  };
  React94.useEffect(() => {
    if (inView && (conversationHistoryAttributes == null ? void 0 : conversationHistoryAttributes.length)) {
      dispatch({
        type: "UPDATE_MESSAGE_LOADER_VISIBILITY" /* UPDATE_MESSAGE_LOADER_VISIBILITY */,
        payload: {
          conversationId: conversationContext.conversationId,
          isMessageLoaderVisible: true
        }
      });
      handleLoadingAnnouncement();
    } else {
      dispatch({
        type: "UPDATE_MESSAGE_LOADER_VISIBILITY" /* UPDATE_MESSAGE_LOADER_VISIBILITY */,
        payload: {
          conversationId: conversationContext.conversationId,
          isMessageLoaderVisible: false
        }
      });
    }
  }, [inView]);
  React94.useEffect(() => {
    if (fetchHistoryMessagesStatus === "failed") {
      setShowRetryIcon(true);
    }
  }, [fetchHistoryMessagesStatus]);
  const handleRetry = e => {
    e.preventDefault();
    setShowRetryIcon(false);
    dispatch({
      type: "FETCH_HISTORY_MESSAGES_STATUS" /* FETCH_HISTORY_MESSAGES_STATUS */,
      payload: {
        status: null,
        conversationId: conversationContext.conversationId
      }
    });
  };
  if (showRetryIcon && retryIcon && (retryText == null ? void 0 : retryText.text)) {
    return /* @__PURE__ */React94.createElement(EGDSLayoutFlex35, {
      alignItems: "center",
      direction: "column"
    }, /* @__PURE__ */React94.createElement(EGDSLayoutFlexItem23, null, /* @__PURE__ */React94.createElement(EGDSIcon9, {
      name: retryIcon.token,
      theme: (_b = retryIcon.theme) != null ? _b : "default",
      size: iconSizeMapper[(_c = retryIcon.size) != null ? _c : "small"]
    })), /* @__PURE__ */React94.createElement(EGDSLayoutFlexItem23, null, /* @__PURE__ */React94.createElement(EGDSSpannableText, {
      data: retryText.text,
      onClick: handleRetry
    })));
  }
  return /* @__PURE__ */React94.createElement(EGDSLayoutFlex35, {
    alignItems: "center",
    direction: "column",
    domRef: element => {
      loaderContainerRef.current = element;
      ref(element);
    }
  }, /* @__PURE__ */React94.createElement(EGDSLayoutFlexItem23, null, /* @__PURE__ */React94.createElement(EGDSLoader3, {
    title: loaderText != null ? loaderText : "",
    isSmall: true
  })));
};
VirtualAgentControlMessageLoader.fragment = fragment63;
VirtualAgentControlMessageLoader.displayName = "VirtualAgentControlOutboundMessageElement";

// ../internal/src/components/messages/outbound-message-element-group/outbound-message-element-group.tsx
import React98 from "react";
import { v4 as uuidv415 } from "uuid";
import { EGDSLayoutFlex as EGDSLayoutFlex38, EGDSLayoutFlexItem as EGDSLayoutFlexItem26 } from "@egds/react-core/layout-flex";
import { EGDSText as EGDSText30 } from "@egds/react-core/text";
import { gql as gql71 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/messages/outbound-message-element/outbound-message-element.tsx
import React97 from "react";
import { gql as gql70 } from "@shared-ui/apollo-client-wrapper";

// ../internal/src/components/messages/outbound-message-element/components/outbound-file-message.tsx
import React95 from "react";
import Omit2 from "lodash.omit";
import { v4 as uuidv413 } from "uuid";
import { EGDSLayoutFlex as EGDSLayoutFlex36, EGDSLayoutFlexItem as EGDSLayoutFlexItem24 } from "@egds/react-core/layout-flex";
import { EGDSLink as EGDSLink6 } from "@egds/react-core/link";
import { EGDSSpacing as EGDSSpacing23 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText28 } from "@egds/react-core/text";
import { Level as Level6 } from "@egds/react-core/utils";
import { gql as gql68 } from "@shared-ui/apollo-client-wrapper";
import { SystemEventLevel as SystemEventLevel12 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState20 } from "@shared-ui/provided-state-context";
var fragment64 = gql68`
  fragment VirtualAgentControlOutboundFileMessageFragment on VirtualAgentControlOutboundFileMessage {
    status
    accessibilityLabel
    metadata {
      messageId
      timeStamp
      messageContextAccessibilityLabel
    }
    attachmentLink {
      ...VirtualAgentControlAttachmentCardFragment
    }
    attachmentMetadata {
      uniqueAttachmentId
      mediaType
      mediaUrl
      name
      size
    }
  }
  ${VirtualAgentControlAttachmentCard.fragment}
`;
var VirtualAgentControlOutboundFileMessage = ({
  data
}) => {
  var _a, _b, _c;
  const {
    status,
    attachmentLink,
    attachmentMetadata,
    metadata: {
      messageId
    }
  } = data;
  const [uniqueAttachmentId, setUniqueAttachmentId] = React95.useState("");
  const [mediaUrl, setMediaUrl] = React95.useState("");
  const [conversation] = useConversationState();
  const statusRef = React95.useRef(null);
  const retryLinkRef = React95.useRef(null);
  const {
    trackEvent
  } = useAnalytics();
  const [state, dispatch] = useProvidedState20();
  const [, downloadFile2] = useAttachment();
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  const sendMessage = useOperation("SEND_MESSAGE" /* SEND_MESSAGE */);
  const loggerV2 = useLogging(EVENTS);
  const handleAnnouncement = React95.useCallback(() => {
    var _a2;
    if (!status) {
      return;
    }
    const statusAnnounceId = `statusAnnounceId-${uuidv413()}`;
    let level = Level6.POLITE;
    if (status === (messageContent == null ? void 0 : messageContent.statusNotDelivered)) {
      level = Level6.ASSERTIVE;
      (_a2 = retryLinkRef.current) == null ? void 0 : _a2.focus();
    }
    ariaAnnounceLive(status, level, statusRef, statusAnnounceId);
  }, [messageContent == null ? void 0 : messageContent.statusNotDelivered, status]);
  React95.useEffect(() => {
    handleAnnouncement();
  }, [handleAnnouncement]);
  if (!attachmentLink) {
    return null;
  }
  const {
    messagesState: {
      attachmentStatusMap
    }
  } = state;
  const {
    action: {
      resource: {
        value
      }
    }
  } = attachmentLink;
  const getUniqueAttachmentIdFromResource = url => {
    const urlArray = url.split("/");
    return urlArray[urlArray.length - 1];
  };
  React95.useEffect(() => {
    if (attachmentMetadata == null ? void 0 : attachmentMetadata.uniqueAttachmentId) {
      setUniqueAttachmentId(attachmentMetadata == null ? void 0 : attachmentMetadata.uniqueAttachmentId);
      if (!attachmentStatusMap[attachmentMetadata.uniqueAttachmentId]) {
        dispatch({
          type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
          payload: {
            uniqueAttachmentId: attachmentMetadata.uniqueAttachmentId,
            attachmentStatus: {
              loading: false,
              hasError: false,
              isSuccess: false,
              operationType: AttachmentOperationTypes.DOWNLOAD
            }
          }
        });
      } else {
        setMediaUrl(value);
      }
    } else {
      const id = getUniqueAttachmentIdFromResource(value);
      setUniqueAttachmentId(id);
      dispatch({
        type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
        payload: {
          uniqueAttachmentId: id,
          attachmentStatus: {
            loading: false,
            hasError: false,
            isSuccess: false,
            operationType: AttachmentOperationTypes.DOWNLOAD
          }
        }
      });
    }
  }, [value, attachmentMetadata == null ? void 0 : attachmentMetadata.uniqueAttachmentId]);
  const isViewableUrl = url => {
    return !!(url == null ? void 0 : url.match(/^blob:/));
  };
  const downloadSubmittedEvent = () => {
    trackEvent(buildClickstreamAdditionalContextPayload("ATTACHMENT_FILE_DOWNLOAD_SUBMITTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }));
  };
  const downloadSucessfulEvent = () => {
    trackEvent(buildClickstreamAdditionalContextPayload("ATTACHMENT_FILE_DOWNLOAD_SUCCEEDED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }));
  };
  const downloadFailureEvent = error => {
    trackEvent(buildClickstreamAdditionalContextErrorPayload("ATTACHMENT_FILE_DOWNLOAD_FAILED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }, {
      error_name: error != null ? error : "network_error"
    }));
    loggerV2("RUNTIME", {
      eventName: "vac.runtime" /* RUNTIME */,
      eventlevel: SystemEventLevel12.ERROR,
      operationName: "VAC_DownloadFailureRuntime" /* VAC_DOWNLOAD_FAILURE_RUNTIME */,
      logSource: PREFIX
    });
  };
  const previewFileEvent = () => {
    trackEvent(buildClickstreamAdditionalContextPayload("ATTACHMENT_FILE_PREVIEW_SELECTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, {
      user_interface: {
        component_element_name: attachmentMetadata == null ? void 0 : attachmentMetadata.mediaType
      }
    }));
  };
  const handleDownload = async event => {
    if (isViewableUrl(mediaUrl)) {
      previewFileEvent();
      return Promise.resolve();
    }
    event.stopPropagation();
    event.preventDefault();
    downloadSubmittedEvent();
    dispatch({
      type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
      payload: {
        uniqueAttachmentId,
        attachmentStatus: {
          loading: true,
          hasError: false,
          isSuccess: false,
          operationType: AttachmentOperationTypes.DOWNLOAD
        }
      }
    });
    try {
      let trimmedMediaUrl = value;
      if (value.startsWith("#")) {
        trimmedMediaUrl = value.slice(1);
      }
      const response = await downloadFile2(trimmedMediaUrl);
      const {
        file_data: {
          bytes,
          file_metadata: {
            content_type
          }
        }
      } = response;
      downloadSucessfulEvent();
      const blobUrl = decodeFileBytes({
        bytes,
        mediaType: content_type
      });
      setMediaUrl(blobUrl);
      dispatch({
        type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
        payload: {
          uniqueAttachmentId,
          attachmentStatus: {
            loading: false,
            hasError: false,
            isSuccess: true,
            operationType: AttachmentOperationTypes.DOWNLOAD
          }
        }
      });
    } catch (err) {
      downloadFailureEvent(err == null ? void 0 : err.message);
      dispatch({
        type: "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */,
        payload: {
          uniqueAttachmentId,
          attachmentStatus: {
            loading: false,
            hasError: true,
            isSuccess: false,
            operationType: AttachmentOperationTypes.DOWNLOAD
          }
        }
      });
    }
  };
  const handleResend = e => {
    e.preventDefault();
    if (!attachmentMetadata || +attachmentMetadata.size > maxFileSizeinInteger) {
      loggerV2("RUNTIME", {
        eventName: "vac.runtime" /* RUNTIME */,
        eventlevel: SystemEventLevel12.ERROR,
        operationName: "VAC_FileSizeExceedingLimit" /* VAC_FILE_SIZE_EXCEEDING_LIMIT */,
        logSource: PREFIX
      });
      return;
    }
    sendMessage == null ? void 0 : sendMessage({
      messageType: "FILE" /* FILE */,
      attachment: {
        ...Omit2(attachmentMetadata, ["__typename"])
      },
      umid: messageId,
      operation: "retry"
    });
  };
  return /* @__PURE__ */React95.createElement(EGDSLayoutFlex36, {
    direction: "column",
    id: messageId
  }, /* @__PURE__ */React95.createElement(EGDSLayoutFlexItem24, {
    alignSelf: "end"
  }, attachmentLink && /* @__PURE__ */React95.createElement(VirtualAgentControlAttachmentCard, {
    data: attachmentLink,
    mediaUrl: attachmentMetadata && ((_a = attachmentStatusMap[attachmentMetadata == null ? void 0 : attachmentMetadata.uniqueAttachmentId]) == null ? void 0 : _a.operationType) === "upload" ? (_c = (_b = attachmentLink == null ? void 0 : attachmentLink.action) == null ? void 0 : _b.resource) == null ? void 0 : _c.value : mediaUrl,
    uniqueAttachmentId,
    onAttachmentClick: event => handleDownload(event),
    cardType: data.__typename,
    size: attachmentMetadata == null ? void 0 : attachmentMetadata.size
  })), status && /* @__PURE__ */React95.createElement(EGDSLayoutFlexItem24, {
    alignSelf: "end"
  }, /* @__PURE__ */React95.createElement(EGDSText28, {
    size: 200,
    spacing: "half",
    theme: "standard"
  }, /* @__PURE__ */React95.createElement("span", {
    ref: statusRef
  }, status), status === (messageContent == null ? void 0 : messageContent.statusNotDelivered) && /* @__PURE__ */React95.createElement(EGDSSpacing23, {
    margin: "one"
  }, /* @__PURE__ */React95.createElement(EGDSLink6, {
    domRef: retryLinkRef,
    size: "small",
    inline: true,
    onClick: handleResend
  }, /* @__PURE__ */React95.createElement("a", {
    href: "#"
  }, messageContent == null ? void 0 : messageContent.statusRetry))))));
};
VirtualAgentControlOutboundFileMessage.fragment = fragment64;
VirtualAgentControlOutboundFileMessage.displayName = "VirtualAgentControlOutboundFileMessage";

// ../internal/src/components/messages/outbound-message-element/components/outbound-text-message.tsx
import React96 from "react";
import Linkify2 from "react-linkify";
import { v4 as uuidv414 } from "uuid";
import { EGDSCard as EGDSCard9, EGDSCardFeaturedHeader as EGDSCardFeaturedHeader2 } from "@egds/react-core/cards";
import { EGDSLayoutFlex as EGDSLayoutFlex37, EGDSLayoutFlexItem as EGDSLayoutFlexItem25 } from "@egds/react-core/layout-flex";
import { EGDSLink as EGDSLink7 } from "@egds/react-core/link";
import { EGDSSpacing as EGDSSpacing24 } from "@egds/react-core/spacing";
import { EGDSText as EGDSText29 } from "@egds/react-core/text";
import { Level as Level7 } from "@egds/react-core/utils";
import { gql as gql69 } from "@shared-ui/apollo-client-wrapper";
var fragment65 = gql69`
  fragment VirtualAgentControlOutboundTextMessageFragment on VirtualAgentControlOutboundTextMessage {
    metadata {
      messageId
      timeStamp
      messageContextAccessibilityLabel
    }
    primary
    status
  }
`;
var VirtualAgentControlOutboundTextMessage = ({
  data
}) => {
  const {
    primary,
    status,
    metadata: {
      messageId
    }
  } = data;
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  const statusRef = React96.useRef(null);
  const retryLinkRef = React96.useRef(null);
  const sendMessage = useOperation("SEND_MESSAGE" /* SEND_MESSAGE */);
  const linkComponentDecorator = (href, text, key) => {
    return getLinkComponentDecorator(href, text, key, "inverse");
  };
  const transformedMessageText = (messageText, mId) => {
    return messageText.split(/[\n\r]/).map((line, index, array) => {
      const uidString = `${line}+${index}`;
      return /* @__PURE__ */React96.createElement(Linkify2, {
        componentDecorator: linkComponentDecorator,
        key: `${mId}-${uidString}`
      }, line, index < array.length - 1 ? /* @__PURE__ */React96.createElement("br", null) : "");
    });
  };
  const handleAnnouncement = React96.useCallback(() => {
    var _a;
    if (!status) {
      return;
    }
    const statusAnnounceId = `statusAnnounceId-${uuidv414()}`;
    let level = Level7.POLITE;
    if (status === (messageContent == null ? void 0 : messageContent.statusNotDelivered)) {
      level = Level7.ASSERTIVE;
      (_a = retryLinkRef.current) == null ? void 0 : _a.focus();
    }
    ariaAnnounceLive(status, level, statusRef, statusAnnounceId);
  }, [messageContent == null ? void 0 : messageContent.statusNotDelivered, status]);
  React96.useEffect(() => {
    handleAnnouncement();
  }, [handleAnnouncement]);
  const handleResend = e => {
    e.preventDefault();
    sendMessage == null ? void 0 : sendMessage({
      message: primary,
      operation: "retry",
      umid: messageId
    });
  };
  return /* @__PURE__ */React96.createElement(EGDSLayoutFlex37, {
    direction: "column",
    id: messageId
  }, /* @__PURE__ */React96.createElement(EGDSLayoutFlexItem25, {
    alignSelf: "end",
    maxWidth: "seventy_two"
  }, /* @__PURE__ */React96.createElement(EGDSSpacing24, {
    margin: {
      blockstart: "half"
    }
  }, /* @__PURE__ */React96.createElement("article", null, /* @__PURE__ */React96.createElement(EGDSCard9, {
    padded: true
  }, /* @__PURE__ */React96.createElement(EGDSCardFeaturedHeader2, null, /* @__PURE__ */React96.createElement(EGDSText29, {
    size: 300,
    theme: "inverse"
  }, transformedMessageText(primary, messageId))))))), status && /* @__PURE__ */React96.createElement(EGDSLayoutFlexItem25, {
    alignSelf: "end"
  }, /* @__PURE__ */React96.createElement(EGDSText29, {
    size: 200,
    spacing: "half",
    theme: "standard"
  }, /* @__PURE__ */React96.createElement("span", {
    ref: statusRef
  }, status), status === (messageContent == null ? void 0 : messageContent.statusNotDelivered) && /* @__PURE__ */React96.createElement(EGDSSpacing24, {
    margin: "one"
  }, /* @__PURE__ */React96.createElement(EGDSLink7, {
    domRef: retryLinkRef,
    size: "small",
    inline: true,
    onClick: handleResend
  }, /* @__PURE__ */React96.createElement("a", {
    href: "#"
  }, messageContent == null ? void 0 : messageContent.statusRetry))))));
};
VirtualAgentControlOutboundTextMessage.displayName = "VirtualAgentControlOutboundTextMessage";
VirtualAgentControlOutboundTextMessage.fragment = fragment65;

// ../internal/src/components/messages/outbound-message-element/outbound-message-element.tsx
var fragment66 = gql70`
  fragment VirtualAgentControlOutboundMessageElementFragment on VirtualAgentControlOutboundMessageElement {
    ...VirtualAgentControlOutboundFileMessageFragment
    ...VirtualAgentControlOutboundTextMessageFragment
  }

  ${VirtualAgentControlOutboundFileMessage.fragment}
  ${VirtualAgentControlOutboundTextMessage.fragment}
`;
var VirtualAgentControlOutboundMessageElement = ({
  data
}) => {
  switch (data.__typename) {
    case "VirtualAgentControlOutboundFileMessage":
      return /* @__PURE__ */React97.createElement(VirtualAgentControlOutboundFileMessage, {
        data
      });
    case "VirtualAgentControlOutboundTextMessage":
      return /* @__PURE__ */React97.createElement(VirtualAgentControlOutboundTextMessage, {
        data
      });
    default:
      return null;
  }
};
VirtualAgentControlOutboundMessageElement.fragment = fragment66;
VirtualAgentControlOutboundMessageElement.displayName = "VirtualAgentControlOutboundMessageElement";

// ../internal/src/components/messages/outbound-message-element-group/outbound-message-element-group.tsx
var fragment67 = gql71`
  fragment VirtualAgentControlOutboundMessageElementGroupFragment on VirtualAgentControlOutboundMessageElementGroup {
    messages {
      ...VirtualAgentControlOutboundMessageElementFragment
    }
    status
  }
  ${VirtualAgentControlOutboundMessageElement.fragment}
`;
var VirtualAgentControlOutboundMessageElementGroup = ({
  data
}) => {
  const {
    messages,
    status
  } = data;
  const {
    outboundMessageContextAccessibilityLabel
  } = use_prefetch_localization_content_default(LocalizationContentKeys.PARTICIPANT) || {};
  const groupId = React98.useMemo(() => uuidv415(), []);
  if (!messages.length) {
    return null;
  }
  const messageIds = messages.map(message => message.metadata.messageId);
  const getParticipantAccessibilityLabel = () => {
    var _a, _b;
    return (_b = (_a = messages[0]) == null ? void 0 : _a.metadata) == null ? void 0 : _b.messageContextAccessibilityLabel;
  };
  return /* @__PURE__ */React98.createElement(EGDSLayoutFlex38, {
    direction: "column",
    role: "group",
    "aria-labelledby": `${[groupId, ...messageIds].join(" ")}`
  }, /* @__PURE__ */React98.createElement("span", {
    id: groupId,
    className: "is-visually-hidden"
  }, getParticipantAccessibilityLabel() || outboundMessageContextAccessibilityLabel, ","), messages.map((message, index) => /* @__PURE__ */React98.createElement(VirtualAgentControlOutboundMessageElement, {
    data: {
      ...message,
      status: status ? null : message.status
    },
    key: `message-${index}`
  })), status && /* @__PURE__ */React98.createElement(EGDSLayoutFlexItem26, {
    alignSelf: "end"
  }, /* @__PURE__ */React98.createElement(EGDSText30, {
    size: 200,
    spacing: "half",
    theme: "standard"
  }, status)));
};
VirtualAgentControlOutboundMessageElementGroup.fragment = fragment67;
VirtualAgentControlOutboundMessageElementGroup.displayName = "VirtualAgentControlOutboundMessageElementGroup";

// ../internal/src/components/messages/vac-messages-mapper/vac-messages-mapper.tsx
var VirtualAgentControlMessagesMapper = ({
  data
}) => {
  switch (data == null ? void 0 : data.__typename) {
    case "VirtualAgentControlInboundDynamicCardMessage":
    case "VirtualAgentControlInboundTextMessage":
    case "VirtualAgentControlInboundFileMessage":
      return /* @__PURE__ */React99.createElement(VirtualAgentControlInboundMessage, {
        data
      });
    case "VirtualAgentControlInboundMessageGroup":
      return /* @__PURE__ */React99.createElement(VirtualAgentControlInboundMessageGroup, {
        data
      });
    case "VirtualAgentControlMessageSeparator":
      return /* @__PURE__ */React99.createElement(VirtualAgentControlMessageSeparator, {
        data
      });
    case "VirtualAgentControlOutboundMessageElementGroup":
      return /* @__PURE__ */React99.createElement(VirtualAgentControlOutboundMessageElementGroup, {
        data
      });
    case "VirtualAgentControlMessageLoader":
      return /* @__PURE__ */React99.createElement(VirtualAgentControlMessageLoader, {
        data
      });
    default:
      return null;
  }
};

// src/service/chat/chat.tsx
var __ChatQuery = gql72`
  query ChatQuery(
    $authenticationConfig: AuthenticationConfigInput
    $context: ContextInput!
    $conversationContext: ConversationContextInput
    $embedded: Boolean = false
  ) {
    virtualAgentControl(
      authenticationConfig: $authenticationConfig
      context: $context
      conversationContext: $conversationContext
      embedded: $embedded
    ) {
      trigger @skip(if: $embedded) {
        ...VirtualAgentControlTriggerFragment
      }
      chat {
        analytics {
          ... on VirtualAgentControlImpressionAnalyticEvent {
            eventName
            eventVersion
            payload
          }
          ... on VirtualAgentControlInteractionAnalyticEvent {
            eventName
            eventVersion
            payload
          }
          ... on VirtualAgentControlOutcomeAnalyticEvent {
            eventName
            eventVersion
            payload
          }
          ... on VirtualAgentControlPageViewAnalyticEvent {
            eventName
            eventVersion
            payload
          }
        }
        header {
          ... on VirtualAgentControlChatHeader {
            ...VirtualAgentControlChatHeaderFragment
          }
          ... on VirtualAgentControlToolbar {
            ...VirtualAgentControlToolbarFragment
          }
        }
        conversationIntro {
          primary
          secondary
          bottomBorder {
            ...VirtualAgentControlMessageSeparatorFragment
          }
        }
        footer {
          ...VirtualAgentControlChatFooterFragment
        }
        landmarkChatContentAccessibilityLabel
        skipToMostRecentMessageAccessibilityLabel
      }
      chatConfig {
        channelOrigin {
          partnerGuid
          channelOriginId
        }
        chatCapabilities {
          enableHistory
          notificationConfig {
            enableSound
            audioUrl
          }
          presenceConfig {
            heartbeatIntervalInMillis
            mode
          }
          participantTypesForEventMarkers
          showParticipantEventMarker
        }
      }
      settingsMenu {
        ... on VirtualAgentControlMenuPopoverView {
          ...VirtualAgentControlMenuPopoverViewFragment
        }
        ... on VirtualAgentControlMenuBottomSheetView {
          ...VirtualAgentControlMenuBottomSheetViewFragment
        }
      }
    }
  }

  ${VirtualAgentControlTrigger.fragment}
  ${VirtualAgentControlChatHeader.fragment}
  ${VirtualAgentControlToolbar.fragment}
  ${VirtualAgentControlChatFooter.fragment}
  ${VirtualAgentControlMessageSeparator.fragment}
  ${VirtualAgentControlMenuPopoverView.fragment}
  ${VirtualAgentControlMenuBottomSheetView.fragment}
`;

// src/service/chat/chat-trigger.tsx
import { gql as gql73 } from "@shared-ui/apollo-client-wrapper";
var __ChatTriggerQuery = gql73`
  query ChatTriggerQuery($authenticationConfig: AuthenticationConfigInput, $context: ContextInput!) {
    virtualAgentControl(authenticationConfig: $authenticationConfig, context: $context) {
      trigger {
        ...VirtualAgentControlTriggerFragment
      }
    }
  }
  ${VirtualAgentControlTrigger.fragment}
`;

// src/service/chat/chat-window.tsx
import { gql as gql74 } from "@shared-ui/apollo-client-wrapper";
var __ChatWindowQuery = gql74`
  query ChatWindowQuery($authenticationConfig: AuthenticationConfigInput, $context: ContextInput!) {
    virtualAgentControl(authenticationConfig: $authenticationConfig, context: $context) {
      chat {
        header {
          ... on VirtualAgentControlChatHeader {
            ...VirtualAgentControlChatHeaderFragment
          }
          ... on VirtualAgentControlToolbar {
            ...VirtualAgentControlToolbarFragment
          }
        }
        conversationIntro {
          primary
          secondary
          bottomBorder {
            ...VirtualAgentControlMessageSeparatorFragment
          }
        }
        footer {
          ...VirtualAgentControlChatFooterFragment
        }
      }
    }
  }
  ${VirtualAgentControlChatHeader.fragment}
  ${VirtualAgentControlToolbar.fragment}
  ${VirtualAgentControlMessageSeparator.fragment}
  ${VirtualAgentControlChatFooter.fragment}
`;

// src/hooks/use-chat.tsx
var useChatQuery = (skipSsr, embedded, conversationContext, channelOriginId, handlers = {}) => useVacQuery(__ChatQuery, {
  variables: {
    embedded,
    conversationContext
  },
  ssr: !skipSsr,
  authenticationConfig: {
    channelOriginId
  },
  handlers
});
var useChatTriggerQuery = (skipSsr, authenticationConfig, handlers = {}) => useVacQuery(__ChatTriggerQuery, {
  variables: {},
  ssr: !skipSsr,
  authenticationConfig,
  handlers
});
var useChatWindowQuery = (skipSsr, authenticationConfig, handlers = {}) => useVacQuery(__ChatWindowQuery, {
  variables: {},
  ssr: !skipSsr,
  authenticationConfig,
  handlers
});
function useVacQuery(query, {
  variables,
  ssr,
  authenticationConfig,
  handlers
}) {
  const {
    context
  } = useBexApi5();
  const {
    onCompleted,
    onError
  } = handlers;
  const {
    data,
    error,
    loading
  } = useQuery(query, {
    context: {
      shouldLogPerformance: true
    },
    variables: {
      context,
      ...variables,
      authenticationConfig
    },
    ssr,
    onError,
    onCompleted
  });
  return {
    query: {
      data,
      error,
      loading
    }
  };
}

// src/hooks/use-chat-visible.tsx
import { useCallback as useCallback4, useState as useState5 } from "react";
function useChatVisible(initialVisible = false) {
  const isBrowser = typeof window !== "undefined";
  const [isVisible, _setIsVisible] = useState5(() => {
    if (isBrowser) {
      const storedValue = localStorage.getItem("VAC_CHAT_WIDGET_OPEN");
      return storedValue ? JSON.parse(storedValue) : initialVisible;
    }
    return initialVisible;
  });
  const setIsVisible = useCallback4(value => {
    localStorage.setItem("VAC_CHAT_WIDGET_OPEN", JSON.stringify(value));
    _setIsVisible(value);
  }, []);
  return [isVisible, setIsVisible];
}

// src/hooks/use-conversation-read-event.tsx
import { useEffect as useEffect10, useRef as useRef2 } from "react";
import { useSignalSubscriber as useSignalSubscriber5 } from "@shared-ui/core-signal-manager/context";
import { VacConversationReadSignal } from "@shared-ui/retail-signals-virtual-agent";
function useConversationReadEvent(isChatVisible, embedded) {
  const [conversation] = useConversationState();
  const [, conversationMessages] = useMessagesState();
  const sendEvent = useOperation("TYPING_EVENT" /* TYPING_EVENT */);
  const isThrottling = useRef2(false);
  useEffect10(() => {
    if (isChatVisible && (conversationMessages == null ? void 0 : conversationMessages.unreadMessageCount) > 0 && conversation.conversationId) {
      handleConversationReadEvent(conversation.conversationId);
    }
  }, [isChatVisible]);
  const handleConversationReadEvent = eventConversationId => {
    if (eventConversationId !== conversation.conversationId) {
      return;
    }
    if (isThrottling.current) {
      return;
    }
    if (!isChatVisible && !embedded) {
      return;
    }
    sendEvent == null ? void 0 : sendEvent({
      eventType: "CONVERSATION_READ" /* CONVERSATION_READ */
    });
    isThrottling.current = true;
    setTimeout(() => {
      isThrottling.current = false;
    }, 500);
  };
  useSignalSubscriber5("VAC_CONVERSATION_READ" /* VAC_ConversationRead */, VacConversationReadSignal, {
    onSuccess: ({
      payload: payload2
    }) => {
      handleConversationReadEvent(payload2.conversationId);
    }
  });
}

// src/hooks/use-create-conversation.tsx
import { useCallback as useCallback5 } from "react";
import { useMutation as useMutation2 } from "@shared-ui/apollo-client-wrapper";
import { useInteractionRumTracker as useInteractionRumTracker5 } from "@shared-ui/core-real-user-monitoring/context";
import { useSignalPublisher as useSignalPublisher8 } from "@shared-ui/core-signal-manager/context";
import { SystemEventLevel as SystemEventLevel13 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState21 } from "@shared-ui/provided-state-context";
import { VacConversationCreatedSignal, VacConversationCreateFailedSignal } from "@shared-ui/retail-signals-virtual-agent";
import { TIMERS as TIMERS3, useTimers as useTimers3 } from "@shared-ui/retail-virtual-agent-providers";

// src/service/create-conversation/create-conversation.tsx
import { gql as gql75 } from "@shared-ui/apollo-client-wrapper";
var ConversationIdentifiersFragment = gql75`
  fragment ConversationIdentifiersFragment on ConversationIdentifiers {
    conversationId
    participantId
  }
`;
var CreateConversationErrorBannerFragment = gql75`
  fragment CreateConversationErrorBannerFragment on VirtualAgentControlErrorBanner {
    header
    retryText
    text
  }
`;
var CreateConversationErrorFullPageViewFragment = gql75`
  fragment CreateConversationErrorFullPageViewFragment on VirtualAgentControlErrorFullPageView {
    text
    header
    retryText
    icon {
      description
      id
      title
    }
  }
`;
var __CreateConversationMutation = gql75`
  mutation VirtualAgentControlCreateConversationMutation(
    $authenticationConfig: AuthenticationConfigInput
    $context: ContextInput!
    $conversationInitializationConfigs: ConversationInitializationConfigsInput!
  ) {
    virtualAgentControlCreateConversation(
      authenticationConfig: $authenticationConfig
      context: $context
      conversationInitializationConfigs: $conversationInitializationConfigs
    ) {
      ... on CreateConversationSuccess {
        identifiers {
          ...ConversationIdentifiersFragment
        }
      }
      ... on CreateConversationError {
        banner {
          ...CreateConversationErrorBannerFragment
        }
        fullPage {
          ...CreateConversationErrorFullPageViewFragment
        }
      }
    }
  }
  ${ConversationIdentifiersFragment}
  ${CreateConversationErrorBannerFragment}
  ${CreateConversationErrorFullPageViewFragment}
`;

// src/hooks/use-create-conversation.tsx
function getSuccessHandler(dispatch, publishSignal, startTimer, vacId, activeConversationId) {
  return (results, clientOptions) => {
    var _a, _b;
    startTimer(TIMERS3.CREATE_CONVERSATION);
    if (results.virtualAgentControlCreateConversation.identifiers) {
      const {
        conversationId,
        participantId
      } = results.virtualAgentControlCreateConversation.identifiers;
      dispatch({
        type: "RELOCATE_MESSAGES_TO_NEW_CONV" /* RELOCATE_MESSAGES_TO_NEW_CONV */,
        payload: {
          conversationId,
          activeConversationId
        }
      });
      dispatch({
        type: "UPDATE_CONVERSATION" /* UPDATE_CONVERSATION */,
        payload: {
          conversationId,
          participantId,
          type: clientOptions.variables.conversationInitializationConfigs.conversationType || "MAIN" /* MAIN */
        }
      });
      dispatch({
        type: "SET_ACTIVE_CONVERSATION_ID" /* SET_ACTIVE_CONVERSATION_ID */,
        payload: {
          vacId,
          activeConversationId: conversationId
        }
      });
      publishSignal(new VacConversationCreatedSignal("VAC_CONVERSATION_CREATED" /* VAC_ConversationCreated */, {
        conversationId,
        participantId
      }));
    } else if ((_a = results.virtualAgentControlCreateConversation) == null ? void 0 : _a.banner) {
      const {
        header,
        text,
        retryText
      } = results.virtualAgentControlCreateConversation.banner;
      dispatch({
        type: "SET_ERROR_BANNER" /* SET_ERROR_BANNER */,
        payload: {
          source: vacId,
          errorBanner: {
            header,
            text,
            retryText
          }
        }
      });
      publishSignal(new VacConversationCreateFailedSignal("VAC_CONVERSATION_CREATED_FAIL" /* VAC_ConversationCreateFailed */, {
        header,
        text,
        retryText
      }));
    } else if ((_b = results.virtualAgentControlCreateConversation) == null ? void 0 : _b.fullPage) {
      const {
        header,
        icon,
        text,
        retryText
      } = results.virtualAgentControlCreateConversation.fullPage;
      dispatch({
        type: "SET_FULL_PAGE_VIEW" /* SET_FULL_PAGE_VIEW */,
        payload: {
          source: vacId,
          fullPageView: {
            header,
            icon,
            text,
            retryText
          }
        }
      });
      publishSignal(new VacConversationCreateFailedSignal("VAC_CONVERSATION_CREATED_FAIL" /* VAC_ConversationCreateFailed */, {
        header,
        text,
        retryText
      }));
    }
  };
}
function getErrorHandler(dispatch, publishSignal, startTimer, vacId, errorString, logger) {
  return results => {
    var _a;
    startTimer(TIMERS3.CREATE_CONVERSATION);
    const errorBanner = ((_a = results.virtualAgentControlCreateConversation) == null ? void 0 : _a.banner) || {
      ...defaultErrorBanner,
      header: errorString == null ? void 0 : errorString.somethingWentWrong,
      text: errorString == null ? void 0 : errorString.refreshOrTryLater
    };
    const {
      header,
      text,
      retryText
    } = errorBanner;
    dispatch({
      type: "SET_ERROR_BANNER" /* SET_ERROR_BANNER */,
      payload: {
        source: vacId,
        errorBanner: {
          header,
          text,
          retryText
        }
      }
    });
    publishSignal(new VacConversationCreateFailedSignal("VAC_CONVERSATION_CREATED_FAIL" /* VAC_ConversationCreateFailed */, {
      header,
      text,
      retryText
    }));
  };
}
function useCreateConversation() {
  const {
    startTimer,
    getTime
  } = useTimers3();
  const vacId = useVacId();
  const [, dispatch] = useProvidedState21();
  const [chatInstance] = useChatInstanceState();
  const loggerV2 = useLogging(EVENTS2);
  const {
    publish
  } = useSignalPublisher8();
  const errorString = use_prefetch_localization_content_default(LocalizationContentKeys.ERROR);
  const {
    trackEvent
  } = useInteractionRumTracker5();
  const [_createConversation] = useMutation2(__CreateConversationMutation, {
    onCompleted: (result, clientOptions) => {
      const timestamp = getTime(TIMERS3.CREATE_CONVERSATION_INIT, true);
      loggerV2("API_SUCCESS", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel13.INFO,
        operationName: "VirtualAgentControlCreateConversationMutation" /* VAC_CREATE_CONVERSATION_MUTATION */,
        logSource: PREFIX2,
        loadingDuration: timestamp
      });
      trackEvent(ACTION, {
        action: `${"VirtualAgentControlCreateConversationMutation" /* VAC_CREATE_CONVERSATION_MUTATION */}-success`,
        attributes: {
          loadingDuration: timestamp
        }
      });
      getSuccessHandler(dispatch, publish, startTimer, vacId, chatInstance == null ? void 0 : chatInstance.activeConversationId)(result, clientOptions);
    },
    onError: error => {
      getErrorHandler(dispatch, publish, startTimer, vacId, errorString, loggerV2)(error);
      const timestamp = getTime(TIMERS3.CREATE_CONVERSATION_INIT, true);
      loggerV2("API_ERROR", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel13.ERROR,
        operationName: "VirtualAgentControlCreateConversationMutation" /* VAC_CREATE_CONVERSATION_MUTATION */,
        logSource: PREFIX2,
        loadingDuration: timestamp
      });
      trackEvent(ACTION, {
        action: `${"VirtualAgentControlCreateConversationMutation" /* VAC_CREATE_CONVERSATION_MUTATION */}-failure`,
        attributes: {
          loadingDuration: timestamp
        }
      });
    },
    fetchPolicy: "no-cache"
  });
  const createConversation = useCallback5((...args) => {
    startTimer(TIMERS3.CREATE_CONVERSATION_INIT);
    return _createConversation(...args);
  }, [_createConversation]);
  return [createConversation];
}

// src/hooks/use-download-chat-transcript.tsx
import React101 from "react";
import { useLazyQuery } from "@shared-ui/apollo-client-wrapper";
import { useBexApi as useBexApi6 } from "@shared-ui/bex-api-context";
import { SystemEventLevel as SystemEventLevel14 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState22 } from "@shared-ui/provided-state-context";
import { TIMERS as TIMERS4, useTimers as useTimers4 } from "@shared-ui/retail-virtual-agent-providers";

// src/service/download-chat-transcript/download-chat-transcript.tsx
import { gql as gql76 } from "@shared-ui/apollo-client-wrapper";
var DownloadChatTranscriptErrorBannerFragment = gql76`
  fragment DownloadChatTranscriptErrorBannerFragment on VirtualAgentControlErrorBanner {
    header
    retryText
    text
    dismissText
    icon {
      id
      title
      description
    }
    retryAction {
      ... on VirtualAgentControlActionEvent {
        eventType
      }
    }
  }
`;
var DownloadChatTranscriptSuccessFragment = gql76`
  fragment DownloadChatTranscriptSuccessFragment on VirtualAgentControlChatTranscriptSuccess {
    fileName
    toastSuccess {
      text
    }
    transcriptText
  }
`;
var __VacChatTranscriptQuery = gql76`
  query VacChatTranscriptQuery(
    $authenticationConfig: AuthenticationConfigInput!
    $context: ContextInput!
    $conversationContext: ConversationContextInput!
    $clientContext: ClientContextInput
  ) {
    vacChatTranscript(
      authenticationConfig: $authenticationConfig
      clientContext: $clientContext
      context: $context
      conversationContext: $conversationContext
    ) {
      ... on VirtualAgentControlChatTranscriptSuccess {
        ...DownloadChatTranscriptSuccessFragment
      }
      ... on VirtualAgentControlChatTranscriptError {
        banner {
          ...DownloadChatTranscriptErrorBannerFragment
        }
      }
    }
  }
  ${DownloadChatTranscriptSuccessFragment}
  ${DownloadChatTranscriptErrorBannerFragment}
`;

// src/hooks/use-download-chat-transcript.tsx
function downloadFile(fileName, fileContent) {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + fileContent], {
    type: "text/plain;charset=utf-8"
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
function getErrorHandler2(dispatch, vacId, errorStrings, logger, getTime) {
  dispatch({
    type: "CLEAR_LOADING" /* CLEAR_LOADING */,
    payload: {
      source: vacId
    }
  });
  const errorBanner = {
    ...defaultErrorBanner,
    header: errorStrings == null ? void 0 : errorStrings.somethingWentWrong,
    text: errorStrings == null ? void 0 : errorStrings.refreshOrTryLater
  };
  dispatch({
    type: "SET_ERROR_BANNER" /* SET_ERROR_BANNER */,
    payload: {
      source: vacId,
      errorBanner
    }
  });
  logger("API_ERROR", {
    eventName: "vac.api" /* API */,
    eventlevel: SystemEventLevel14.ERROR,
    operationName: "VacChatTranscriptQuery" /* VAC_CHAT_TRANSCRIPT_QUERY */,
    logSource: PREFIX2,
    loadingDuration: getTime(TIMERS4.CHAT_TRANSCRIPT, true)
  });
}
function getSuccessHandler2(dispatch, vacId, showToast, data, errorStrings, logger, getTime) {
  var _a, _b, _c;
  dispatch({
    type: "CLEAR_LOADING" /* CLEAR_LOADING */,
    payload: {
      source: vacId
    }
  });
  if (((_a = data == null ? void 0 : data.vacChatTranscript) == null ? void 0 : _a.__typename) === "VirtualAgentControlChatTranscriptSuccess") {
    try {
      if (data.vacChatTranscript.transcriptText) {
        downloadFile(data.vacChatTranscript.fileName, data.vacChatTranscript.transcriptText);
      }
      showToast((_b = data.vacChatTranscript.toastSuccess) == null ? void 0 : _b.text);
      logger("API_SUCCESS", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel14.INFO,
        operationName: "VacChatTranscriptQuery" /* VAC_CHAT_TRANSCRIPT_QUERY */,
        logSource: PREFIX2,
        loadingDuration: getTime(TIMERS4.CHAT_TRANSCRIPT, true)
      });
    } catch (error) {
      getErrorHandler2(dispatch, vacId, errorStrings, logger, getTime);
    }
  } else {
    dispatch({
      type: "SET_ERROR_BANNER" /* SET_ERROR_BANNER */,
      payload: {
        source: vacId,
        errorBanner: (_c = data == null ? void 0 : data.vacChatTranscript) == null ? void 0 : _c.banner
      }
    });
  }
}
function useDownloadChatTranscript() {
  var _a;
  const showToast = useOperation("SHOW_TOAST" /* SHOW_TOAST */);
  const [, dispatch] = useProvidedState22();
  const vacId = useVacId();
  const loggerV2 = useLogging(EVENTS2);
  const {
    startTimer,
    getTime
  } = useTimers4();
  const {
    context
  } = useBexApi6();
  const [authenticationConfig] = useAuthenticationConfig();
  const [,, conversationContext] = useConversationState();
  const clientContext = getClientContext();
  const loadingString = (_a = use_prefetch_localization_content_default(LocalizationContentKeys.LOADING)) == null ? void 0 : _a.downloadChatTranscript;
  const errorStrings = use_prefetch_localization_content_default(LocalizationContentKeys.ERROR);
  const variables = {
    authenticationConfig,
    clientContext,
    context,
    conversationContext
  };
  const [getChatTranscript] = useLazyQuery(__VacChatTranscriptQuery, {
    variables,
    onCompleted: queryData => {
      getSuccessHandler2(dispatch, vacId, showToast, queryData, errorStrings, loggerV2, getTime);
    },
    onError: () => {
      getErrorHandler2(dispatch, vacId, errorStrings, loggerV2, getTime);
    },
    fetchPolicy: "no-cache"
  });
  const handleDownloadChatTranscript = React101.useCallback(() => {
    dispatch({
      type: "SET_LOADING_DIALOG" /* SET_LOADING_DIALOG */,
      payload: {
        source: vacId,
        loadingDialog: {
          text: loadingString
        }
      }
    });
    startTimer(TIMERS4.CHAT_TRANSCRIPT);
    getChatTranscript();
  }, [dispatch, vacId, loadingString, getChatTranscript]);
  return [handleDownloadChatTranscript];
}

// src/hooks/use-send-conversation-event.tsx
import React102 from "react";
import { useMutation as useMutation3 } from "@shared-ui/apollo-client-wrapper";
import { useBexApi as useBexApi7 } from "@shared-ui/bex-api-context";
import { useInteractionRumTracker as useInteractionRumTracker6 } from "@shared-ui/core-real-user-monitoring/context";
import { SystemEventLevel as SystemEventLevel15 } from "@shared-ui/logger-context";
import { TIMERS as TIMERS5, useTimers as useTimers5 } from "@shared-ui/retail-virtual-agent-providers";

// src/service/send-conversation-event/send-conversation-event.tsx
import { gql as gql77 } from "@shared-ui/apollo-client-wrapper";
var __SendConversationEventMutation = gql77`
  mutation SendVirtualAgentControlConversationEvent(
    $authenticationConfig: AuthenticationConfigInput
    $context: ContextInput!
    $conversationContext: ConversationContextInput!
    $event: VirtualAgentControlConversationEventInput!
  ) {
    sendConversationEvent(authenticationConfig: $authenticationConfig, context: $context, conversationContext: $conversationContext, event: $event) {
      retryText
    }
  }
`;

// src/hooks/use-send-conversation-event.tsx
function doSendEvent(sendEventMutation, sendEventData, dispatch, logger, getTime, trackEvent) {
  sendEventMutation(sendEventData).then(() => {
    const {
      event: {
        eventType
      }
    } = sendEventData.variables;
    if (eventType === "CONVERSATION_READ" /* CONVERSATION_READ */) {
      dispatch({
        type: "CLEAR_UNREAD_MESSAGE_COUNT" /* CLEAR_UNREAD_MESSAGE_COUNT */
      });
    }
    const timestamp = getTime(TIMERS5.SEND_CONVERSATION_EVENT, true);
    logger("API_SUCCESS", {
      eventName: "vac.api" /* API */,
      eventlevel: SystemEventLevel15.INFO,
      operationName: "SendVirtualAgentControlConversationEvent" /* SEND_VAC_CONVERSATION_EVENT */,
      logSource: PREFIX2,
      loadingDuration: timestamp
    });
    trackEvent(ACTION, {
      action: `${"SendVirtualAgentControlConversationEvent" /* SEND_VAC_CONVERSATION_EVENT */}-success`,
      attributes: {
        loadingDuration: timestamp
      }
    });
  }).catch(err => {
    const timestamp = getTime(TIMERS5.SEND_CONVERSATION_EVENT, true);
    logger("API_ERROR", {
      eventName: "vac.api" /* API */,
      eventlevel: SystemEventLevel15.ERROR,
      operationName: "SendVirtualAgentControlConversationEvent" /* SEND_VAC_CONVERSATION_EVENT */,
      logSource: PREFIX2,
      loadingDuration: timestamp
    });
    trackEvent(ACTION, {
      action: `${"SendVirtualAgentControlConversationEvent" /* SEND_VAC_CONVERSATION_EVENT */}-failure`,
      attributes: {
        loadingDuration: timestamp
      }
    });
  });
}
function useSendConversationEvent() {
  const [sendEvent] = useMutation3(__SendConversationEventMutation);
  const {
    startTimer,
    getTime
  } = useTimers5();
  const {
    context
  } = useBexApi7();
  const [,, conversationContext] = useConversationState();
  const [,, messagesDispatch] = useMessagesState();
  const {
    trackEvent
  } = useInteractionRumTracker6();
  const loggerV2 = useLogging(EVENTS2);
  const [authenticationConfig] = useAuthenticationConfig();
  const handleSendEvent = React102.useCallback(event => {
    const sendEventData = {
      variables: {
        ...((authenticationConfig == null ? void 0 : authenticationConfig.channelOriginId) && {
          authenticationConfig
        }),
        context,
        conversationContext,
        event
      }
    };
    startTimer(TIMERS5.SEND_CONVERSATION_EVENT);
    doSendEvent(sendEvent, sendEventData, messagesDispatch, loggerV2, getTime, trackEvent);
  }, [authenticationConfig, context, messagesDispatch, sendEvent, conversationContext, loggerV2, trackEvent]);
  return [handleSendEvent];
}

// src/components/chat/__private/active-conversation.tsx
import React103 from "react";
import Omit3 from "lodash.omit";
import { v4 as uuidv416 } from "uuid";
import { EGDSLayoutFlex as EGDSLayoutFlex39 } from "@egds/react-core/layout-flex";
import { EGDSLoader as EGDSLoader4 } from "@egds/react-core/loader";
import { EGDSHeading as EGDSHeading11, EGDSText as EGDSText31 } from "@egds/react-core/text";
import { Level as Level8 } from "@egds/react-core/utils";
import { ScrollChatContentToBottomEvent } from "@egds/react-virtual-agent-control";
import { gql as gql79, useLazyQuery as useLazyQuery2, useQuery as useQuery2 } from "@shared-ui/apollo-client-wrapper";
import { useBexApi as useBexApi8 } from "@shared-ui/bex-api-context";
import { useInteractionRumTracker as useInteractionRumTracker7 } from "@shared-ui/core-real-user-monitoring/context";
import { useSignalPublisher as useSignalPublisher9, useSignalSubscriber as useSignalSubscriber6 } from "@shared-ui/core-signal-manager/context";
import { SystemEventLevel as SystemEventLevel16, useLoggerEvents as useLoggerEvents2 } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState23 } from "@shared-ui/provided-state-context";
import { VacConnectedSignal, VacFetchHistoryMessagesFailedSignal } from "@shared-ui/retail-signals-virtual-agent";
import { TIMERS as TIMERS6, useTimers as useTimers6 } from "@shared-ui/retail-virtual-agent-providers";

// src/service/active-conversation/active-conversation.tsx
import { gql as gql78 } from "@shared-ui/apollo-client-wrapper";
var __Query = gql78`
  query ActiveConversationQuery(
    $context: ContextInput!
    $conversationContext: ConversationContextInput!
    $authenticationConfig: AuthenticationConfigInput!
    $clientContext: ClientContextInput
  ) {
    virtualAgentControlMessages(
      authenticationConfig: $authenticationConfig
      context: $context
      conversationContext: $conversationContext
      clientContext: $clientContext
    ) {
      meta {
        participants {
          ...ConversationParticipantFragment
        }
      }
      messages {
        ...VirtualAgentControlInboundMessageGroupFragment
        ...VirtualAgentControlMessageSeparatorFragment
        ...VirtualAgentControlOutboundMessageElementGroupFragment
      }
    }
  }
  fragment ConversationParticipantFragment on ConversationParticipant {
    id
    participantType
    participantState
    cpUserId
    user {
      avatarUrl
      firstName
      lastName
      preferredName
    }
  }
  ${VirtualAgentControlInboundMessageGroup.fragment}
  ${VirtualAgentControlMessageSeparator.fragment}
  ${VirtualAgentControlOutboundMessageElementGroup.fragment}
`;

// src/components/chat/__private/active-conversation.tsx
var __Query2 = gql79`
  query VacChatContentQuery(
    $context: ContextInput!
    $conversationContext: ConversationContextInput!
    $authenticationConfig: AuthenticationConfigInput!
    $clientContext: ClientContextInput
  ) {
    vacChatContent(
      authenticationConfig: $authenticationConfig
      context: $context
      conversationContext: $conversationContext
      clientContext: $clientContext
    ) {
      meta {
        participants {
          ...ConversationParticipantFragment
        }
      }
      messages {
        ...VirtualAgentControlInboundMessageGroupFragment
        ...VirtualAgentControlMessageSeparatorFragment
        ...VirtualAgentControlOutboundMessageElementGroupFragment
        ...VirtualAgentControlMessageLoaderFragment
      }
    }
  }
  fragment ConversationParticipantFragment on ConversationParticipant {
    id
    participantType
    participantState
    cpUserId
    user {
      avatarUrl
      firstName
      lastName
      preferredName
    }
  }
  ${VirtualAgentControlInboundMessageGroup.fragment}
  ${VirtualAgentControlMessageSeparator.fragment}
  ${VirtualAgentControlOutboundMessageElementGroup.fragment}
  ${VirtualAgentControlMessageLoader.fragment}
`;
function getSuccessHandler3(dispatch, vacId) {
  return result => {
    if (result == null ? void 0 : result.vacChatContent) {
      dispatch({
        type: "CLEAR_ERRORS" /* CLEAR_ERRORS */,
        payload: {
          source: vacId
        }
      });
    }
  };
}
function getErrorHandler3(dispatch, vacId, state, conversationId) {
  const conversationsById = state.conversationsState.conversationsById[conversationId];
  if (conversationsById == null ? void 0 : conversationsById.conversationHistoryAttributes) {
    return () => {
      dispatch({
        type: "SET_HISTORY_MESSAGES_FETCHING_ERROR" /* SET_HISTORY_MESSAGES_FETCHING_ERROR */,
        payload: {
          source: vacId
        }
      });
    };
  }
  const errorStrings = state.errorsState.orchestrator.offlineLocalizedErrorStrings || {};
  return result => {
    dispatch({
      type: "SET_FULL_PAGE_VIEW" /* SET_FULL_PAGE_VIEW */,
      payload: {
        source: vacId,
        fullPageView: {
          header: errorStrings == null ? void 0 : errorStrings.somethingWentWrong,
          text: errorStrings == null ? void 0 : errorStrings.refreshOrTryLater
        }
      }
    });
  };
}
var ActiveConversation = ({
  skipSsr,
  conversationIntro,
  autoScroll = false
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  const [isFirstLog, setIsFirstLog] = React103.useState(true);
  const logging = useLoggerEvents2(EVENTS2);
  const {
    getTime
  } = useTimers6();
  const {
    context
  } = useBexApi8();
  const [state, dispatch] = useProvidedState23();
  const vacId = useVacId();
  const [chatInstance] = useChatInstanceState();
  const [conversation, conversationDispatch, conversationContext] = useConversationState();
  const [, conversationMessages, messagesDispatch] = useMessagesState();
  const [, activityIndicators] = useParticipantState();
  const loggerV2 = useLogging(EVENTS2);
  const embedded = Boolean(chatInstance == null ? void 0 : chatInstance.embedded);
  const [authenticationConfig] = useAuthenticationConfig();
  const lastWebSocketDisconnectRef = React103.useRef(null);
  const lastScrollHeight = React103.useRef(0);
  const prevMessageIdSetLength = React103.useRef(0);
  const hasHistoryQueryFired = React103.useRef(false);
  const conversationMessagesFetched = React103.useRef(false);
  const messageLoadedRef = React103.useRef(null);
  const {
    publish
  } = useSignalPublisher9();
  const fetchHistoryMessagesStatus = (_a = state.messagesState.messagesByConversationId[conversationContext.conversationId]) == null ? void 0 : _a.fetchHistoryMessagesStatus;
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  const {
    trackEvent
  } = useInteractionRumTracker7();
  const {
    trackEvent: trackClickstreamEvent
  } = useAnalytics();
  const getDurationExcludingIdleTime = React103.useCallback(() => {
    const {
      INIT,
      CHAT_MOUNTED,
      TRIGGER_CLICKED
    } = TIMERS6;
    if (embedded) {
      return getTime(INIT);
    }
    return embedded ? getTime(INIT) : getTime(INIT) - getTime(CHAT_MOUNTED) + getTime(TRIGGER_CLICKED);
  }, [getTime, embedded]);
  const variables = {
    context,
    conversationContext,
    authenticationConfig,
    clientContext: getClientContext()
  };
  const toMessage = (message, index) => /* @__PURE__ */React103.createElement(VirtualAgentControlMessagesMapper, {
    data: message,
    key: `message_${index}`
  });
  const getLoader = () => {
    return /* @__PURE__ */React103.createElement(EGDSLayoutFlex39, {
      space: "one",
      direction: "column",
      alignItems: "center",
      justifyContent: "center",
      blockSize: "full_size"
    }, /* @__PURE__ */React103.createElement(EGDSLoader4, {
      "data-stid": "active-conversation-loader"
    }));
  };
  const getConversationIntro = data2 => {
    if (!data2) {
      return null;
    }
    const {
      primary,
      secondary,
      bottomBorder
    } = data2;
    return /* @__PURE__ */React103.createElement(EGDSLayoutFlex39, {
      direction: "column",
      space: "four"
    }, primary && /* @__PURE__ */React103.createElement(EGDSHeading11, {
      size: 3,
      typeStyle: {
        customStyle: "headline-extra-large"
      },
      tag: "h4"
    }, primary), secondary && /* @__PURE__ */React103.createElement(EGDSText31, {
      size: 400
    }, secondary), bottomBorder && /* @__PURE__ */React103.createElement(VirtualAgentControlMessageSeparator, {
      data: bottomBorder
    }));
  };
  const {
    data,
    loading,
    refetch
  } = useQuery2(__Query2, {
    context: {
      shouldLogPerformance: true
    },
    variables,
    ssr: !skipSsr,
    fetchPolicy: "no-cache",
    onError: error => {
      loggerV2("API_ERROR", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel16.ERROR,
        operationName: "VacChatContentQuery" /* VAC_CHAT_CONTENT_QUERY */,
        logSource: PREFIX2,
        loadingDuration: getTime(TIMERS6.INIT)
      });
      getErrorHandler3(dispatch, vacId, state, conversationContext.conversationId)(error);
    },
    onCompleted: result => {
      conversationMessagesFetched.current = true;
      loggerV2("API_SUCCESS", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel16.INFO,
        operationName: "VacChatContentQuery" /* VAC_CHAT_CONTENT_QUERY */,
        logSource: PREFIX2,
        loadingDuration: getTime(TIMERS6.INIT)
      });
      getSuccessHandler3(dispatch, vacId)(result);
    }
  });
  const [getPastMessages, pastMessagesResult] = useLazyQuery2(__Query);
  React103.useEffect(() => {
    if (conversationMessages == null ? void 0 : conversationMessages.quickReplyAnalytics) {
      const quickReplyAnalytic = conversationMessages == null ? void 0 : conversationMessages.quickReplyAnalytics.find(analytics => analytics.eventName === "virtual_agent_quick_reply.presented");
      if (quickReplyAnalytic) {
        const qrAnalytics = getEventPayloadAnalytics(quickReplyAnalytic.payload, quickReplyAnalytic.eventName, quickReplyAnalytic.eventVersion);
        trackClickstreamEvent(buildClickstreamAdditionalContextPayload("VIRTUAL_AGENT_QUICK_REPLY_PRESENTED", conversation == null ? void 0 : conversation.conversationId, conversation == null ? void 0 : conversation.type, qrAnalytics == null ? void 0 : qrAnalytics.additionalContext, conversationMessages == null ? void 0 : conversationMessages.lastQuickMessageId));
      }
    }
  }, [conversationMessages == null ? void 0 : conversationMessages.quickReplyAnalytics]);
  React103.useEffect(() => {
    lastWebSocketDisconnectRef.current = state.orchestratorState.lastWebsocketDisconnectTime;
  }, [state.orchestratorState.lastWebsocketDisconnectTime]);
  React103.useEffect(() => {
    const {
      data: pastMessagesData
    } = pastMessagesResult;
    if (pastMessagesData) {
      saveMissedMessages(pastMessagesData);
    }
  }, [pastMessagesResult]);
  React103.useEffect(() => {
    var _a2, _b2, _c2;
    if (data) {
      if (Array.isArray((_a2 = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _a2.messages) && data.vacChatContent.messages.length === 0) {
        dispatch({
          type: "REMOVE_LOADER_MESSAGE" /* REMOVE_LOADER_MESSAGE */,
          payload: {
            conversationId: conversationContext.conversationId
          }
        });
      }
      const messages = ((_b2 = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _b2.messages) || [];
      const stateMessages2 = (_c2 = conversationMessages == null ? void 0 : conversationMessages.messages) != null ? _c2 : [];
      if (messages && (!stateMessages2.length || (conversationMessages == null ? void 0 : conversationMessages.isIBMFlow))) {
        messages.forEach(message => {
          if (message.__typename === "VirtualAgentControlMessageLoader") {
            setTimeout(() => {
              messagesDispatch({
                type: "ADD_LOADER_MESSAGE" /* ADD_LOADER_MESSAGE */,
                payload: {
                  conversationId: conversationContext.conversationId,
                  message
                }
              });
            }, 1e3);
          } else if (message.__typename === "VirtualAgentControlMessageSeparator") {
            const timeStamp = message.timeStamp ? new Date(message.timeStamp).toISOString() : null;
            messagesDispatch({
              type: "ADD_SEPERATOR_MESSAGE" /* ADD_SEPERATOR_MESSAGE */,
              payload: {
                conversationId: conversationContext.conversationId,
                primary: message.primary,
                timeStamp
              }
            });
          } else if (message.__typename !== "VirtualAgentControlOutboundMessageGroup") {
            messagesDispatch({
              type: "ADD_MESSAGE" /* ADD_MESSAGE */,
              payload: {
                conversationId: conversationContext.conversationId,
                message,
                messageContent
              }
            });
          }
          messagesDispatch({
            type: "SET_IBM_FLOW" /* SET_IBM_FLOW */,
            payload: {
              conversationId: conversationContext.conversationId,
              isIBMFlow: false
            }
          });
        });
      }
    }
  }, [data, messagesDispatch, (_b = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _b.messages, conversationMessages == null ? void 0 : conversationMessages.isIBMFlow]);
  function dispatchQuickMessage(lastMessage) {
    var _a2;
    if (lastMessage.__typename === "VirtualAgentControlInboundTextMessage" || lastMessage.__typename === "VirtualAgentControlInboundDynamicCardMessage" || lastMessage.__typename === "VirtualAgentControlInboundQuickReplyMessage") {
      if (lastMessage == null ? void 0 : lastMessage.quickReplies) {
        messagesDispatch({
          type: "SET_QUICK_REPLIES" /* SET_QUICK_REPLIES */,
          payload: {
            message: lastMessage == null ? void 0 : lastMessage.quickReplies,
            messageId: (_a2 = lastMessage == null ? void 0 : lastMessage.metadata) == null ? void 0 : _a2.messageId,
            analytics: lastMessage == null ? void 0 : lastMessage.analytics
          }
        });
      }
    }
  }
  React103.useEffect(() => {
    var _a2, _b2;
    if (data) {
      const activeConversationMessages = ((_a2 = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _a2.messages) || [];
      const lastReceivedMessageTimeStamp = conversationMessages == null ? void 0 : conversationMessages.lastReceivedMessageTimeStamp;
      if (!lastReceivedMessageTimeStamp && activeConversationMessages.length) {
        const filtered = activeConversationMessages.filter(message => message.__typename !== "VirtualAgentControlOutboundMessageGroup");
        const lastMessageGroup = filtered[filtered.length - 1];
        if (lastMessageGroup.__typename !== "VirtualAgentControlMessageSeparator") {
          const {
            messages
          } = lastMessageGroup;
          if (messages.length) {
            const lastMessage = messages[messages.length - 1];
            const timeStamp = (_b2 = lastMessage == null ? void 0 : lastMessage.metadata) == null ? void 0 : _b2.timeStamp;
            messagesDispatch({
              type: "SET_LAST_MESSAGE_TIMESTAMP" /* SET_LAST_MESSAGE_TIMESTAMP */,
              payload: {
                timeStamp
              }
            });
            dispatchQuickMessage(lastMessage);
          }
        } else {
          const {
            primary
          } = lastMessageGroup;
          messagesDispatch({
            type: "SET_LAST_MESSAGE_TIMESTAMP" /* SET_LAST_MESSAGE_TIMESTAMP */,
            payload: {
              timeStamp: primary
            }
          });
        }
      }
    }
  }, [data, messagesDispatch, (_c = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _c.messages, conversationMessages == null ? void 0 : conversationMessages.lastReceivedMessageTimeStamp]);
  const stateMessages = (_d = conversationMessages == null ? void 0 : conversationMessages.messages) != null ? _d : [];
  const stateMessagesLength = stateMessages == null ? void 0 : stateMessages.length;
  const lastGroupMessageCount = stateMessagesLength > 0 ? (_f = (_e = stateMessages[stateMessagesLength - 1]) == null ? void 0 : _e.messages) == null ? void 0 : _f.length : 0;
  const chatContainer = document.getElementById("chat-container-content");
  const messageIdSetLength = (_g = conversationMessages == null ? void 0 : conversationMessages.messageIdSet) == null ? void 0 : _g.size;
  React103.useEffect(() => {
    setTimeout(() => {
      if (chatContainer && lastScrollHeight.current && chatContainer.scrollHeight > lastScrollHeight.current) {
        chatContainer.scrollTop = chatContainer.scrollHeight - lastScrollHeight.current;
      }
    }, 50);
    if (autoScroll && (!lastScrollHeight.current || prevMessageIdSetLength.current < messageIdSetLength || activityIndicators.length)) {
      prevMessageIdSetLength.current = messageIdSetLength;
      lastScrollHeight.current = 0;
      setTimeout(() => {
        document == null ? void 0 : document.dispatchEvent(ScrollChatContentToBottomEvent);
      }, 50);
    }
  }, [autoScroll, (_h = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _h.messages, stateMessagesLength, lastGroupMessageCount, activityIndicators.length]);
  React103.useEffect(() => {
    var _a2;
    if (!isFirstLog) {
      return;
    }
    if ((conversationMessages == null ? void 0 : conversationMessages.messages) || ((_a2 = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _a2.messages)) {
      setIsFirstLog(false);
      logging.INITIALIZATION_MESSAGE.log(payload({
        duration: Math.max(getTime(TIMERS6.CREATE_CONVERSATION), 0),
        ssrEnabled: !skipSsr,
        isEmbedded: Boolean(chatInstance == null ? void 0 : chatInstance.embedded)
      }));
      logging.INITIALIZATION_OVERALL.log(payload({
        duration: getDurationExcludingIdleTime(),
        ssrEnabled: !skipSsr,
        isEmbedded: Boolean(chatInstance == null ? void 0 : chatInstance.embedded)
      }));
      loggerV2("PERFORMANCE", {
        eventName: "vac.performance" /* PERFORMANCE */,
        eventlevel: SystemEventLevel16.INFO,
        operationName: "VAC_PERF_FirstMessageShown" /* VAC_PERF_FIRST_MESSAGE_SHOWN */,
        logSource: PREFIX2,
        performanceEventTime: (/* @__PURE__ */new Date()).getTime()
      });
      trackEvent(ACTION, {
        action: "VAC_PERF_FirstMessageShown" /* VAC_PERF_FIRST_MESSAGE_SHOWN */,
        attributes: {
          performanceEventTime: (/* @__PURE__ */new Date()).getTime()
        }
      });
    }
  }, [conversationMessages == null ? void 0 : conversationMessages.messages, (_i = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _i.messages, isFirstLog, setIsFirstLog, getDurationExcludingIdleTime, logging, skipSsr, getTime, chatInstance == null ? void 0 : chatInstance.embedded]);
  React103.useEffect(() => {
    var _a2, _b2;
    const messages = ((_a2 = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _a2.messages) || [];
    const lastMessageGroup = messages.length > 0 ? messages[messages.length - 1] : null;
    if ((lastMessageGroup == null ? void 0 : lastMessageGroup.__typename) === "VirtualAgentControlInboundMessageGroup") {
      const lastInboundMessageList = lastMessageGroup.messages || [];
      const lastInboundMessage = lastInboundMessageList[lastInboundMessageList.length - 1];
      if ((_b2 = lastInboundMessage == null ? void 0 : lastInboundMessage.stateUpdates) == null ? void 0 : _b2.chatInput) {
        conversationDispatch({
          type: "SET_FOOTER_STATE" /* SET_FOOTER_STATE */,
          payload: {
            chatFooterState: lastInboundMessage.stateUpdates.chatInput
          }
        });
      }
    }
  }, [data, conversationDispatch]);
  React103.useEffect(() => {
    var _a2, _b2, _c2;
    const participants = (_c2 = (_b2 = (_a2 = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _a2.meta) == null ? void 0 : _b2.participants) != null ? _c2 : [];
    if (participants == null ? void 0 : participants.length) {
      dispatch({
        type: "SET_PARTICIPANTS" /* SET_PARTICIPANTS */,
        payload: {
          participants,
          conversationId: conversationContext == null ? void 0 : conversationContext.conversationId
        }
      });
    }
  }, [(_k = (_j = data == null ? void 0 : data.vacChatContent) == null ? void 0 : _j.meta) == null ? void 0 : _k.participants, dispatch, conversationContext == null ? void 0 : conversationContext.conversationId]);
  const saveMissedMessages = React103.useCallback(refetchedData => {
    var _a2;
    const refetchedMessages = ((_a2 = refetchedData == null ? void 0 : refetchedData.virtualAgentControlMessages) == null ? void 0 : _a2.messages) || [];
    if (refetchedMessages.length) {
      refetchedMessages.forEach(message => {
        if (message.__typename === "VirtualAgentControlMessageSeparator") {
          const timeStamp = message.timeStamp ? new Date(message.timeStamp).toISOString() : null;
          messagesDispatch({
            type: "ADD_SEPERATOR_MESSAGE" /* ADD_SEPERATOR_MESSAGE */,
            payload: {
              primary: message.primary,
              timeStamp
            }
          });
        } else {
          messagesDispatch({
            type: "ADD_MESSAGE" /* ADD_MESSAGE */,
            payload: {
              conversationId: conversationContext.conversationId,
              message,
              messageContent
            }
          });
          dispatchQuickMessage(message);
        }
      });
    }
  }, []);
  const saveHistoryMessages = React103.useCallback(historyMessages => {
    dispatch({
      type: "ADD_HISTORY_MESSAGES" /* ADD_HISTORY_MESSAGES */,
      payload: {
        conversationId: conversationContext.conversationId,
        messages: historyMessages
      }
    });
  }, []);
  React103.useEffect(() => {
    if (fetchHistoryMessagesStatus === "failed") {
      dispatch({
        type: "SET_HISTORY_MESSAGES_FETCHING_ERROR" /* SET_HISTORY_MESSAGES_FETCHING_ERROR */,
        payload: {
          source: vacId,
          chatHistoryFetchingError: true
        }
      });
    } else {
      dispatch({
        type: "CLEAR_ERRORS" /* CLEAR_ERRORS */,
        payload: {
          source: vacId
        }
      });
    }
  }, [fetchHistoryMessagesStatus]);
  const handleMessageLoadedAnnouncement = () => {
    const messageLoadedAnnounceId = `messageLoadedAnnounce-${uuidv416()}`;
    ariaAnnounceLive(messageContent.messagesLoadedNotificationAnnouncement, Level8.POLITE, messageLoadedRef, messageLoadedAnnounceId, true);
  };
  const fetchHistoryMessages = React103.useCallback(async conversationHistoryAttributes => {
    var _a2;
    const updatedVariables = {
      context,
      conversationContext: {
        ...conversationContext,
        conversationHistoryAttributes
      },
      authenticationConfig,
      clientContext: getClientContext()
    };
    try {
      const {
        data: fetchedHistoryData,
        loading: historyLoading
      } = await refetch({
        ...updatedVariables
      });
      if (fetchedHistoryData && !historyLoading) {
        hasHistoryQueryFired.current = false;
        handleMessageLoadedAnnouncement();
        saveHistoryMessages((_a2 = fetchedHistoryData == null ? void 0 : fetchedHistoryData.vacChatContent) == null ? void 0 : _a2.messages);
        dispatch({
          type: "FETCH_HISTORY_MESSAGES_STATUS" /* FETCH_HISTORY_MESSAGES_STATUS */,
          payload: {
            status: "success",
            conversationId: conversationContext.conversationId
          }
        });
      }
    } catch (e) {
      publish(new VacFetchHistoryMessagesFailedSignal("VAC_FETCH_HISTORY_MESSAGES_FAILED" /* VAC_FetchHistoryMessagesFailed */, null));
      hasHistoryQueryFired.current = false;
      dispatch({
        type: "FETCH_HISTORY_MESSAGES_STATUS" /* FETCH_HISTORY_MESSAGES_STATUS */,
        payload: {
          status: "failed",
          conversationId: conversationContext.conversationId
        }
      });
    }
  }, []);
  useSignalSubscriber6("VAC_CONNECTED" /* VAC_Connected */, VacConnectedSignal, {
    onSuccess: () => {
      if (!lastWebSocketDisconnectRef.current) {
        return;
      }
      const historyPaddingMillis = 3e5;
      const lastWebSocketDisconnectTime = lastWebSocketDisconnectRef.current;
      const sentAfter = (/* @__PURE__ */new Date()).getTime() - lastWebSocketDisconnectTime + historyPaddingMillis;
      const updatedVariables = {
        context,
        conversationContext: {
          ...conversationContext,
          sentAfterOffset: sentAfter
        },
        authenticationConfig,
        clientContext: getClientContext()
      };
      getPastMessages({
        variables: updatedVariables,
        ssr: !skipSsr,
        fetchPolicy: "no-cache",
        onError: getErrorHandler3(dispatch, vacId, state, updatedVariables),
        onCompleted: getSuccessHandler3(dispatch, vacId)
      });
    }
  });
  React103.useEffect(() => {
    var _a2;
    const conversationHistoryAttributes = (_a2 = conversation == null ? void 0 : conversation.conversationHistoryAttributes) == null ? void 0 : _a2.map(attr => Omit3(attr, "__typename"));
    if (!hasHistoryQueryFired.current && chatContainer && (conversationMessages == null ? void 0 : conversationMessages.isMessageLoaderVisible)) {
      hasHistoryQueryFired.current = true;
      lastScrollHeight.current = chatContainer.scrollHeight;
      fetchHistoryMessages(conversationHistoryAttributes);
    }
  }, [conversationMessages == null ? void 0 : conversationMessages.isMessageLoaderVisible]);
  if (loading && !conversationMessagesFetched.current) {
    return /* @__PURE__ */React103.createElement(React103.Fragment, null, conversationIntro ? /* @__PURE__ */React103.createElement(React103.Fragment, null, getConversationIntro(conversationIntro)) : getLoader());
  }
  return /* @__PURE__ */React103.createElement("div", null, getConversationIntro(conversationIntro), /* @__PURE__ */React103.createElement("div", {
    className: "is-visually-hidden",
    ref: messageLoadedRef
  }), /* @__PURE__ */React103.createElement(EGDSLayoutFlex39, {
    direction: "column",
    space: "four"
  }, (_l = conversationMessages == null ? void 0 : conversationMessages.messages) == null ? void 0 : _l.map(toMessage)));
};
ActiveConversation.displayName = "ActiveConversation";

// src/components/chat/chat.tsx
function getLoaderComponent(isVisible, embedded) {
  return /* @__PURE__ */React104.createElement(ChatContainer, {
    visible: isVisible,
    embedded,
    padded: true
  }, /* @__PURE__ */React104.createElement(ChatContainerContent, null, /* @__PURE__ */React104.createElement(EGDSLayoutFlex40, {
    space: "one",
    direction: "column",
    alignItems: "center",
    justifyContent: "center",
    blockSize: "full_size"
  }, /* @__PURE__ */React104.createElement(EGDSLoader5, null))));
}
function getSuccessHandler4(dispatch, vacId, modeOptions) {
  return result => {
    var _a, _b, _c, _d, _e, _f;
    if ((_a = result == null ? void 0 : result.virtualAgentControl) == null ? void 0 : _a.chatConfig) {
      const {
        chatConfig
      } = result.virtualAgentControl;
      const setChatConfigData = {
        vacId,
        chatConfig: {
          partnerGuid: chatConfig.channelOrigin.partnerGuid,
          channelOriginId: chatConfig.channelOrigin.channelOriginId,
          enableHistory: ((_b = chatConfig.chatCapabilities) == null ? void 0 : _b.enableHistory) || false,
          notificationConfig: ((_c = chatConfig.chatCapabilities) == null ? void 0 : _c.notificationConfig) || {},
          presenceConfig: ((_d = chatConfig.chatCapabilities) == null ? void 0 : _d.presenceConfig) || {},
          participantTypesForEventMarkers: ((_e = chatConfig.chatCapabilities) == null ? void 0 : _e.participantTypesForEventMarkers) || {},
          showParticipantEventMarker: ((_f = chatConfig.chatCapabilities) == null ? void 0 : _f.showParticipantEventMarker) || {},
          modeOptions
        }
      };
      dispatch({
        type: "SET_CHAT_CONFIG" /* SET_CHAT_CONFIG */,
        payload: setChatConfigData
      });
    }
    dispatch({
      type: "SET_INITIALIZATION_LOADING" /* SET_INITIALIZATION_LOADING */,
      payload: {
        vacId,
        initializationLoading: false
      }
    });
  };
}
function getErrorHandler4(dispatch, vacId, state) {
  var _a;
  const errorStrings = ((_a = state.errorsState.orchestrator) == null ? void 0 : _a.offlineLocalizedErrorStrings) || {};
  return result => {
    dispatch({
      type: "SET_INITIALIZATION_LOADING" /* SET_INITIALIZATION_LOADING */,
      payload: {
        vacId,
        initializationLoading: false
      }
    });
    dispatch({
      type: "SET_FULL_PAGE_VIEW" /* SET_FULL_PAGE_VIEW */,
      payload: {
        source: vacId,
        fullPageView: {
          header: errorStrings == null ? void 0 : errorStrings.somethingWentWrong,
          text: errorStrings == null ? void 0 : errorStrings.refreshOrTryLater
        }
      }
    });
  };
}
function getVirtualAgentOpenedAnalytic(chatAnalytics) {
  if (chatAnalytics == null ? void 0 : chatAnalytics.length) {
    const virtualAgentOpenedAnalytic = chatAnalytics.find(analytics => analytics.eventName === "virtual_agent.opened");
    if (virtualAgentOpenedAnalytic) {
      return getEventPayloadAnalytics(virtualAgentOpenedAnalytic.payload, virtualAgentOpenedAnalytic.eventName, virtualAgentOpenedAnalytic.eventVersion);
    }
  }
  return null;
}
var Chat = ({
  skipSsr,
  embedded = false,
  autoScroll = false,
  channelOriginId,
  modeOptions
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  const [isFirstLog, setIsFirstLog] = React104.useState(true);
  const isIBMFlow = React104.useRef(null);
  const logging = useLoggerEvents3(EVENTS2);
  const loggerV2 = useLogging(EVENTS2);
  const {
    publish
  } = useSignalPublisher10();
  const {
    startTimer,
    getTime
  } = useTimers7();
  const chatDialogRoot = React104.useRef();
  const dialogProviderConfig = getDialogProviderConfig(chatDialogRoot);
  const [isIBMFlowCalledOnce, setIsIBMFlowCalledOnce] = React104.useState(false);
  const {
    trackEvent
  } = useInteractionRumTracker8();
  React104.useEffect(() => {
    var _a2, _b2;
    startTimer(TIMERS7.CHAT_MOUNTED);
    loggerV2("PERFORMANCE", {
      eventName: "vac.performance" /* PERFORMANCE */,
      eventlevel: SystemEventLevel17.INFO,
      operationName: "VAC_PERF_Start" /* VAC_PERF_START */,
      logSource: PREFIX2,
      conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
      performanceEventTime: (/* @__PURE__ */new Date()).getTime()
    });
    trackEvent(ACTION, {
      action: "VAC_PERF_Start" /* VAC_PERF_START */,
      attributes: {
        conversationType: (_b2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _b2.conversationType,
        performanceEventTime: (/* @__PURE__ */new Date()).getTime()
      }
    });
  }, []);
  useSignalSubscriber7("VAC_CONNECTED" /* VAC_Connected */, VacConnectedSignal2, {
    onSuccess: (/* istanbul ignore next */
    () => {
      var _a2;
      logging.INITIALIZATION_CONNECTED.log(payload({
        duration: getTime(TIMERS7.INIT),
        ssrEnabled: !skipSsr,
        isEmbedded: embedded
      }));
      loggerV2("PERFORMANCE", {
        eventName: "vac.performance" /* PERFORMANCE */,
        eventlevel: SystemEventLevel17.INFO,
        operationName: "VAC_PERF_WebsocketConnected" /* VAC_PERF_WEBSOCKET_CONNECTED */,
        logSource: PREFIX2,
        conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
        performanceEventTime: (/* @__PURE__ */new Date()).getTime()
      });
      trackEvent(ACTION, {
        action: "VAC_PERF_WebsocketConnected" /* VAC_PERF_WEBSOCKET_CONNECTED */,
        attributes: {
          performanceEventTime: (/* @__PURE__ */new Date()).getTime()
        }
      });
    })
  });
  useSignalSubscriber7("VAC_CHAT_MESSAGE_SENT" /* VAC_ChatMsgSent */, VacChatMessageSentSignal2, {
    onSuccess: (/* istanbul ignore next */
    signal => {
      const {
        isRetry
      } = signal.payload;
      if (isRetry) {
        document.dispatchEvent(ScrollChatContentToBottomEvent2);
      }
    })
  });
  const {
    context
  } = useBexApi9();
  const vacId = useVacId();
  const [state, dispatch] = useProvidedState24();
  const [isSettingsVisible, setIsSettingsVisible] = useState6(false);
  const [toastText, setToastText] = useState6("");
  const [conversation, conversationDispatch, conversationContext] = useConversationState();
  const [, conversationMessages, messagesDispatch] = useMessagesState();
  const [chatInstance] = useChatInstanceState();
  const [authenticationConfig] = useAuthenticationConfig(channelOriginId);
  useHeartbeat();
  useParticipantEventMarkers();
  const handlers = {
    onCompleted: result => {
      var _a2, _b2, _c2, _d2;
      loggerV2("API_SUCCESS", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel17.INFO,
        operationName: "VacChatQuery" /* VAC_CHAT_QUERY */,
        logSource: PREFIX2,
        conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
        loadingDuration: getTime(TIMERS7.CHAT_MOUNTED)
      });
      if ((_b2 = result.virtualAgentControl) == null ? void 0 : _b2.trigger) {
        loggerV2("PERFORMANCE", {
          eventName: "vac.performance" /* PERFORMANCE */,
          eventlevel: SystemEventLevel17.INFO,
          operationName: "VAC_PERF_FabShown" /* VAC_PERF_FAB_SHOWN */,
          logSource: PREFIX2,
          conversationType: (_c2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _c2.conversationType,
          performanceEventTime: (/* @__PURE__ */new Date()).getTime()
        });
        trackEvent(ACTION, {
          action: "VAC_PERF_FabShown" /* VAC_PERF_FAB_SHOWN */,
          attributes: {
            conversationType: (_d2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _d2.conversationType,
            performanceEventTime: (/* @__PURE__ */new Date()).getTime()
          }
        });
      }
      getSuccessHandler4(dispatch, vacId, modeOptions == null ? void 0 : modeOptions.context)(result);
    },
    onError: error2 => {
      var _a2;
      loggerV2("API_ERROR", {
        eventName: "vac.api" /* API */,
        eventlevel: SystemEventLevel17.ERROR,
        operationName: "VacChatQuery" /* VAC_CHAT_QUERY */,
        logSource: PREFIX2,
        conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
        loadingDuration: getTime(TIMERS7.CHAT_MOUNTED)
      });
      getErrorHandler4(dispatch, vacId, state)(error2);
    }
  };
  const {
    query: {
      data,
      error,
      loading
    }
  } = useChatQuery(skipSsr, embedded, modeOptions == null ? void 0 : modeOptions.context, channelOriginId, handlers);
  const [isVisible, setIsVisible] = useChatVisible(false);
  const [handleSend] = useSendMessage();
  const [handleSendEvent] = useSendConversationEvent();
  const [createConversation] = useCreateConversation();
  const [handleDownloadChatTranscript] = useDownloadChatTranscript();
  const {
    conversationId: activeConversationId,
    attributes
  } = conversationContext || {};
  const activeParticipantId = conversationContext.participantId;
  const wasCreateConversationCalled = React104.useRef(false);
  const wasSignalHandled = React104.useRef(false);
  const wasContainerStartFired = React104.useRef(false);
  const [lastSignalPayload, setLastSignalPayload] = React104.useState(null);
  const subscriptionId = (_c = (_b = (_a = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _a.vacConfig) == null ? void 0 : _b.websocket) == null ? void 0 : _c.subscriptionId;
  useConversationReadEvent(isVisible, embedded);
  const handleCreateConversation = customInitConfig => {
    var _a2;
    const baseConfig = {
      forceNewConversation: false,
      mode: "CREATE_CONVERSATION" /* CREATE_CONVERSATION */,
      conversationAttributes: Object.entries(attributes || {}).map(([key, value]) => ({
        key,
        value
      })),
      participant: {
        subscriptionId
      },
      conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType
    };
    const finalConfig = {
      ...baseConfig,
      ...customInitConfig
    };
    return createConversation == null ? void 0 : createConversation({
      variables: {
        ...((authenticationConfig == null ? void 0 : authenticationConfig.channelOriginId) && {
          authenticationConfig
        }),
        context,
        conversationInitializationConfigs: finalConfig
      }
    });
  };
  useOperationsRegistry({
    ["SEND_MESSAGE" /* SEND_MESSAGE */]: messageData => handleSend(messageData),
    ["TYPING_EVENT" /* TYPING_EVENT */]: event => handleSendEvent(event),
    ["SHOW_TOAST" /* SHOW_TOAST */]: text => setToastText(text),
    ["CREATE_CONVERSATION" /* CREATE_CONVERSATION */]: (/* istanbul ignore next */
    () => handleCreateConversation()),
    ["DOWNLOAD_TRANSCRIPT" /* DOWNLOAD_TRANSCRIPT */]: () => handleDownloadChatTranscript()
  });
  const handleMenuOpenSettings = () => {
    setIsSettingsVisible(true);
  };
  const handleMenuCloseSettings = () => {
    setIsSettingsVisible(false);
  };
  const handleSignal = () => {
    var _a2, _b2, _c2;
    if (!isIBMFlow.current) {
      return;
    }
    const {
      conversationAttributes,
      intentData,
      silent = false
    } = isIBMFlow.current.payload;
    const {
      messageType
    } = intentData;
    const uuid2 = uuidv417();
    if (messageType === VirtualAgentControlMessageType.POSTBACK) {
      if (!activeConversationId && data && !wasCreateConversationCalled.current) {
        wasCreateConversationCalled.current = true;
        handleCreateConversation({
          forceNewConversation: false,
          mode: "CREATE_CONVERSATION" /* CREATE_CONVERSATION */,
          conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
          conversationAttributes: [...conversationAttributes, {
            key: "traceId",
            value: uuid2
          }]
        });
        setLastSignalPayload(intentData);
      } else if (activeConversationId && activeParticipantId && data && wasCreateConversationCalled.current) {
        const messageData = {
          ...intentData,
          message: intentData.text,
          messageTriggerLocation: "EXTERNAL" /* EXTERNAL */
        };
        handleSend == null ? void 0 : handleSend(messageData);
      }
    } else if (!activeConversationId && data && !wasCreateConversationCalled.current) {
      handleCreateConversation({
        forceNewConversation: false,
        mode: "CREATE_CONVERSATION" /* CREATE_CONVERSATION */,
        conversationType: (_b2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _b2.conversationType,
        conversationAttributes: [...conversationAttributes, {
          key: "traceId",
          value: uuid2
        }],
        intentData: {
          ...intentData,
          umid: uuid2
        }
      });
      wasCreateConversationCalled.current = true;
    } else {
      handleCreateConversation({
        forceNewConversation: false,
        mode: "RESUME_CONVERSATION" /* RESUME_CONVERSATION */,
        conversation: {
          conversationId: activeConversationId
        },
        participant: {
          participantId: activeParticipantId,
          subscriptionId
        },
        conversationType: (_c2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _c2.conversationType,
        conversationAttributes: [...conversationAttributes, {
          key: "traceId",
          value: uuid2
        }],
        intentData: {
          ...intentData,
          umid: uuid2
        }
      });
    }
  };
  const handleSkipLink = e => {
    e.preventDefault();
    document == null ? void 0 : document.dispatchEvent(ScrollChatContentToBottomEvent2);
  };
  use_notification_sound_default(vacId);
  const sendEvent = useOperation("TYPING_EVENT" /* TYPING_EVENT */);
  React104.useEffect(() => {
    if ((embedded || isVisible) && subscriptionId) {
      publish(new VacChatWindowVisibleSignal("VAC_CHAT_WINDOW_VISIBLE" /* VAC_ChatWindowVisible */, null));
    }
  }, [embedded, subscriptionId, isVisible]);
  React104.useEffect(() => {
    var _a2, _b2, _c2, _d2, _e2, _f2;
    if ((modeOptions == null ? void 0 : modeOptions.mode) === "RESUME_CONVERSATION" /* RESUME_CONVERSATION */) {
      dispatch({
        type: "CLEAR_MESSAGES" /* CLEAR_MESSAGES */,
        payload: {
          conversationId: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationId
        }
      });
      dispatch({
        type: "UPDATE_CONVERSATION" /* UPDATE_CONVERSATION */,
        payload: {
          conversationId: (_b2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _b2.conversationId,
          participantId: (_c2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _c2.participantId,
          type: (_d2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _d2.conversationType
        }
      });
      if (((_e2 = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _e2.initializationStatus) === "DONE" /* DONE */) {
        dispatch({
          type: "SET_ACTIVE_CONVERSATION_ID" /* SET_ACTIVE_CONVERSATION_ID */,
          payload: {
            vacId,
            activeConversationId: (_f2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _f2.conversationId
          }
        });
      }
    }
  }, [(_d = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _d.initializationStatus, (_e = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _e.conversationId, (_f = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _f.participantId, modeOptions == null ? void 0 : modeOptions.mode, dispatch, vacId]);
  useSignalSubscriber7("VAC_IBM_CREATE_CONVERSATION" /* VAC_IBM_Create_Conversation */, VacCreateConversationSignal, {
    onSuccess: (/* istanbul ignore next */
    async signal => {
      isIBMFlow.current = signal;
      if (!wasCreateConversationCalled.current) {
        publish(new VacChatWindowVisibleSignal("VAC_CHAT_WINDOW_VISIBLE" /* VAC_ChatWindowVisible */, null));
      } else {
        handleSignal();
      }
      if (!signal.payload.silent && !isVisible) {
        setIsVisible(true);
      }
    })
  });
  useSignalSubscriber7("VAC_CHAT_WINDOW_VISIBLE" /* VAC_ChatWindowVisible */, VacChatWindowVisibleSignal, {
    onSuccess: (/* istanbul ignore next */
    () => {
      var _a2;
      if (!wasContainerStartFired.current) {
        wasContainerStartFired.current = true;
        loggerV2("PERFORMANCE", {
          eventName: "vac.performance" /* PERFORMANCE */,
          eventlevel: SystemEventLevel17.INFO,
          operationName: "VAC_PERF_ContainerStart" /* VAC_PERF_CONTAINER_START */,
          logSource: PREFIX2,
          conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
          performanceEventTime: (/* @__PURE__ */new Date()).getTime()
        });
        trackEvent(ACTION, {
          action: "VAC_PERF_ContainerStart" /* VAC_PERF_CONTAINER_START */,
          attributes: {
            performanceEventTime: (/* @__PURE__ */new Date()).getTime()
          }
        });
      }
    })
  });
  useSignalSubscriber7("VAC_CONNECT_WEBSOCKET" /* VAC_ConnectWebsocket */, VacConnectWebsocketSignal, {
    onSuccess: (/* istanbul ignore next */
    () => {
      var _a2;
      loggerV2("PERFORMANCE", {
        eventName: "vac.performance" /* PERFORMANCE */,
        eventlevel: SystemEventLevel17.INFO,
        operationName: "VAC_PERF_WebsocketStart" /* VAC_PERF_WEBSOCKET_START */,
        logSource: PREFIX2,
        conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
        performanceEventTime: (/* @__PURE__ */new Date()).getTime()
      });
      trackEvent(ACTION, {
        action: "VAC_PERF_WebsocketStart" /* VAC_PERF_WEBSOCKET_START */,
        attributes: {
          performanceEventTime: (/* @__PURE__ */new Date()).getTime()
        }
      });
    })
  });
  useSignalSubscriber7("VAC_ORCHESTRATOR_QUERY_SIGNAL" /* VAC_OrchestatorQuery */, VacOrchestratorQuerySignal, {
    onSuccess: (/* istanbul ignore next */
    signal => {
      var _a2, _b2;
      if (signal.payload.success) {
        loggerV2("API_SUCCESS", {
          eventName: "vac.api" /* API */,
          eventlevel: SystemEventLevel17.INFO,
          operationName: "VacOrchestratorQuery" /* VAC_ORCHESTRATOR_QUERY */,
          logSource: PREFIX2,
          conversationType: (_a2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _a2.conversationType,
          loadingDuration: getTime(TIMERS7.INIT)
        });
      } else {
        loggerV2("API_ERROR", {
          eventName: "vac.api" /* API */,
          eventlevel: SystemEventLevel17.ERROR,
          operationName: "VacOrchestratorQuery" /* VAC_ORCHESTRATOR_QUERY */,
          logSource: PREFIX2,
          conversationType: (_b2 = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _b2.conversationType,
          loadingDuration: getTime(TIMERS7.INIT)
        });
      }
    })
  });
  useSignalSubscriber7("VAC_CONVERSATION_CREATED" /* VAC_ConversationCreated */, VacConversationCreatedSignal2, {
    onSuccess: (/* istanbul ignore next */
    () => {
      if (isIBMFlow.current && !isIBMFlowCalledOnce) {
        setIsIBMFlowCalledOnce(true);
        messagesDispatch({
          type: "SET_IBM_FLOW" /* SET_IBM_FLOW */,
          payload: {
            activeConversationId,
            isIBMFlow: true
          }
        });
      }
      const {
        intentData
      } = isIBMFlow.current.payload;
      if (activeConversationId && (intentData == null ? void 0 : intentData.messageType) === VirtualAgentControlMessageType.POSTBACK && lastSignalPayload) {
        const messageData = {
          ...intentData,
          message: intentData.text,
          messageTriggerLocation: "EXTERNAL" /* EXTERNAL */
        };
        const timeStamp = startOfDayUTC(/* @__PURE__ */new Date()).toISOString();
        dispatch({
          type: "ADD_SEPERATOR_MESSAGE" /* ADD_SEPERATOR_MESSAGE */,
          payload: {
            primary: timeStamp,
            conversationId: activeConversationId,
            timeStamp
          }
        });
        handleSend == null ? void 0 : handleSend(messageData);
        setLastSignalPayload(null);
      }
    })
  });
  React104.useEffect(() => {
    var _a2;
    if (((_a2 = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _a2.initializationStatus) === "DONE" /* DONE */ && isIBMFlow.current && !wasSignalHandled.current) {
      handleSignal();
      wasSignalHandled.current = true;
    }
  }, [(_g = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _g.initializationStatus, isIBMFlow.current, wasSignalHandled.current]);
  React104.useEffect(() => {
    var _a2;
    if (!activeConversationId && data && (isVisible || embedded) && (authenticationConfig == null ? void 0 : authenticationConfig.authToken) && (
    // provided by orchestrator
    authenticationConfig == null ? void 0 : authenticationConfig.channelOriginId) &&
    // provided by virtualAgentControl query
    ((_a2 = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _a2.initializationStatus) === "DONE" /* DONE */ && (modeOptions == null ? void 0 : modeOptions.mode) === "CREATE_CONVERSATION" /* CREATE_CONVERSATION */ && !wasCreateConversationCalled.current) {
      handleCreateConversation();
      wasCreateConversationCalled.current = true;
    }
  }, [data, isVisible, conversationContext, context, attributes, activeConversationId, authenticationConfig, subscriptionId, embedded, (_h = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _h.initializationStatus, modeOptions == null ? void 0 : modeOptions.mode, (_i = modeOptions == null ? void 0 : modeOptions.context) == null ? void 0 : _i.conversationType]);
  React104.useEffect(() => {
    dispatch({
      type: "SET_EMBEDDED" /* SET_EMBEDDED */,
      payload: {
        vacId,
        embedded
      }
    });
  }, [embedded]);
  React104.useEffect(() => {
    if (!isFirstLog) {
      return;
    }
    const {
      INITIALIZATION_CHAT,
      INITIALIZATION_TRIGGER
    } = logging;
    if (embedded) {
      INITIALIZATION_CHAT.log(payload({
        duration: getTime(TIMERS7.INIT),
        ssrEnabled: !skipSsr,
        isEmbedded: embedded
      }));
    } else {
      INITIALIZATION_TRIGGER.log(payload({
        duration: getTime(TIMERS7.INIT),
        ssrEnabled: !skipSsr,
        isEmbedded: embedded
      }));
    }
    setIsFirstLog(false);
  }, [embedded, isFirstLog, setIsFirstLog, logging]);
  const getHeader = (header2, isEmbedded) => {
    if (header2.__typename === "VirtualAgentControlToolbar") {
      return isEmbedded ? null : /* @__PURE__ */React104.createElement(VirtualAgentControlToolbar, {
        data: header2,
        onClose: () => setIsVisible(false)
      });
    }
    return /* @__PURE__ */React104.createElement(VirtualAgentControlChatHeader, {
      data: header2,
      onMinimize: () => setIsVisible(false),
      embedded: isEmbedded,
      onOpenSettings: handleMenuOpenSettings
    });
  };
  if ((chatInstance == null ? void 0 : chatInstance.initializationLoading) || !authenticationConfig.channelOriginId) {
    return getLoaderComponent(isVisible, embedded);
  }
  if (((_k = (_j = state == null ? void 0 : state.errorsState) == null ? void 0 : _j[ORCHESTRATOR]) == null ? void 0 : _k.active) && ((_l = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _l.initializationStatus) === "FAILED" /* FAILED */ || !(data == null ? void 0 : data.virtualAgentControl)) {
    return /* @__PURE__ */React104.createElement(ChatContainer, {
      visible: isVisible,
      embedded,
      padded: true
    }, /* @__PURE__ */React104.createElement(ChatContainerContent, null, /* @__PURE__ */React104.createElement(VirtualAgentControlFullPageErrorView, null)));
  }
  const {
    virtualAgentControl
  } = data;
  const {
    trigger,
    chat: {
      header,
      conversationIntro,
      footer,
      analytics,
      landmarkChatContentAccessibilityLabel,
      skipToMostRecentMessageAccessibilityLabel
    },
    settingsMenu
  } = virtualAgentControl;
  const impressionAnalytics = getVirtualAgentOpenedAnalytic(analytics);
  const {
    visuallyHiddenHeaderAccessibilityLabel,
    landmarkHeaderAccessibilityLabel
  } = header || {};
  const {
    landmarkFooterAccessibilityLabel
  } = footer;
  return /* @__PURE__ */React104.createElement(React104.Fragment, null, !isVisible && !embedded && trigger && /* @__PURE__ */React104.createElement(VirtualAgentControlTrigger, {
    data: trigger,
    onClick: () => {
      setIsVisible(true);
      startTimer(TIMERS7.TRIGGER_CLICKED);
      publish(new VacChatWindowVisibleSignal("VAC_CHAT_WINDOW_VISIBLE" /* VAC_ChatWindowVisible */, null));
    }
  }), /* @__PURE__ */React104.createElement(EGDSHeading12, {
    tag: "h1",
    className: "is-visually-hidden"
  }, visuallyHiddenHeaderAccessibilityLabel), /* @__PURE__ */React104.createElement(ChatContainer, {
    visible: isVisible,
    embedded,
    padded: true
  }, header && /* @__PURE__ */React104.createElement(ChatContainerHeader, {
    ariaLabel: landmarkHeaderAccessibilityLabel
  }, getHeader(header, embedded), /* @__PURE__ */React104.createElement(VirtualAgentControlErrorBanner, null), embedded && /* @__PURE__ */React104.createElement(VirtualAgentControlMenuSettings, {
    settingsMenu,
    handleMenuCloseSettings,
    isSettingsVisible
  })), /* @__PURE__ */React104.createElement(ChatContainerContent, {
    ariaLabel: landmarkChatContentAccessibilityLabel
  }, /* @__PURE__ */React104.createElement(DialogProvider, {
    value: dialogProviderConfig
  }, /* @__PURE__ */React104.createElement(EGDSSkipLink, {
    items: [{
      attributes: {
        tabindex: 0
      },
      className: "uitk-list-item",
      onClick: handleSkipLink,
      label: skipToMostRecentMessageAccessibilityLabel,
      typeAnchor: true
    }]
  }), activeConversationId && /* @__PURE__ */React104.createElement(EgClickstreamImpressionTracker, {
    eventData: buildClickstreamAdditionalContextPayload("VIRTUAL_AGENT_OPENED", conversation.conversationId, conversation.type, impressionAnalytics == null ? void 0 : impressionAnalytics.additionalContext)
  }, /* @__PURE__ */React104.createElement(ActiveConversation, {
    inputs: {},
    conversationIntro,
    autoScroll
  })), /* @__PURE__ */React104.createElement(VirtualAgentControlTypingIndicator, null), /* @__PURE__ */React104.createElement(VirtualAgentControlFullPageErrorView, {
    embedded,
    onClose: () => setIsVisible(false)
  }), /* @__PURE__ */React104.createElement(VirtualAgentControlLoadingDialog, null), !embedded && /* @__PURE__ */React104.createElement(VirtualAgentControlMenuSettings, {
    settingsMenu,
    handleMenuCloseSettings,
    isSettingsVisible
  }))), /* @__PURE__ */React104.createElement(ChatContainerFooter, {
    ariaLabel: landmarkFooterAccessibilityLabel
  }, /* @__PURE__ */React104.createElement("div", {
    id: "chat-dialog",
    ref: chatDialogRoot
  }), /* @__PURE__ */React104.createElement(DialogProvider, {
    value: dialogProviderConfig
  }, /* @__PURE__ */React104.createElement(EGDSSpacing25, {
    padding: "two"
  }, /* @__PURE__ */React104.createElement("div", null, /* @__PURE__ */React104.createElement(VirtualAgentControlQuickReplies, null), /* @__PURE__ */React104.createElement(VirtualAgentControlChatFooter, {
    data: footer
  }))), /* @__PURE__ */React104.createElement(EGDSToast, {
    show: toastText.length > 0,
    onDismiss: () => setToastText(""),
    verticalOffset: 50
  }, toastText)))), activeConversationId && /* @__PURE__ */React104.createElement(VacSignalSubscriber, null));
};
Chat.displayName = "sharedui-Chat";

// src/components/chat/chat-trigger.tsx
import React105 from "react";
var ChatTrigger = ({
  skipSsr,
  onClick,
  inputs
}) => {
  var _a, _b;
  const {
    authenticationConfig: authenticationConfigInput
  } = inputs || {};
  const channelOriginId = (_a = authenticationConfigInput == null ? void 0 : authenticationConfigInput.channelOriginId) != null ? _a : void 0;
  const [authenticationConfig] = useAuthenticationConfig(channelOriginId);
  const {
    query: {
      data,
      error,
      loading
    }
  } = useChatTriggerQuery(skipSsr, authenticationConfig);
  if (loading || error || !((_b = data == null ? void 0 : data.virtualAgentControl) == null ? void 0 : _b.trigger)) {
    return null;
  }
  const {
    virtualAgentControl: {
      trigger
    }
  } = data;
  return /* @__PURE__ */React105.createElement(VirtualAgentControlTrigger, {
    data: trigger,
    onClick
  });
};
ChatTrigger.displayName = "sharedui-ChatTrigger";

// src/components/chat/chat-window.tsx
import React106 from "react";
import { EGDSSpacing as EGDSSpacing26 } from "@egds/react-core/spacing";
import { ChatContainer as ChatContainer2, ChatContainerContent as ChatContainerContent2, ChatContainerFooter as ChatContainerFooter2, ChatContainerHeader as ChatContainerHeader2 } from "@egds/react-virtual-agent-control";
import { useBexApi as useBexApi10 } from "@shared-ui/bex-api-context";
import { useProvidedState as useProvidedState25 } from "@shared-ui/provided-state-context";
var ChatWindow = ({
  skipSsr,
  onClose,
  channelOriginId
}) => {
  var _a, _b, _c;
  const {
    context
  } = useBexApi10();
  const [authenticationConfig] = useAuthenticationConfig(channelOriginId);
  const {
    query: {
      data,
      error,
      loading
    }
  } = useChatWindowQuery(skipSsr, authenticationConfig);
  const [state] = useProvidedState25();
  const [,, conversationContext] = useConversationState();
  const [handleSend] = useSendMessage();
  const [handleSendEvent] = useSendConversationEvent();
  const [createConversation] = useCreateConversation();
  const {
    conversationId: activeConversationId,
    attributes
  } = conversationContext || {};
  const subscriptionId = (_c = (_b = (_a = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _a.vacConfig) == null ? void 0 : _b.websocket) == null ? void 0 : _c.subscriptionId;
  useOperationsRegistry({
    ["SEND_MESSAGE" /* SEND_MESSAGE */]: messageData => handleSend(messageData),
    ["TYPING_EVENT" /* TYPING_EVENT */]: event => handleSendEvent(event)
  });
  React106.useEffect(() => {
    if (!activeConversationId && data) {
      createConversation == null ? void 0 : createConversation({
        variables: {
          ...((authenticationConfig == null ? void 0 : authenticationConfig.channelOriginId) && {
            authenticationConfig
          }),
          context,
          conversationInitializationConfigs: {
            forceNewConversation: false,
            mode: "CREATE_CONVERSATION" /* CREATE_CONVERSATION */,
            participant: {
              subscriptionId
            }
          }
        }
      });
    }
  }, [data, conversationContext, createConversation, context, attributes, activeConversationId, authenticationConfig, subscriptionId]);
  const handleClose = () => onClose == null ? void 0 : onClose();
  const getHeader = header2 => {
    if (header2.__typename === "VirtualAgentControlToolbar") {
      return /* @__PURE__ */React106.createElement(VirtualAgentControlToolbar, {
        data: header2,
        onClose: handleClose
      });
    }
    return /* @__PURE__ */React106.createElement(VirtualAgentControlChatHeader, {
      data: header2,
      embedded: true,
      onMinimize: handleClose
    });
  };
  if (loading || error || !(data == null ? void 0 : data.virtualAgentControl)) {
    return null;
  }
  const {
    virtualAgentControl
  } = data;
  const {
    chat: {
      header,
      conversationIntro,
      footer
    }
  } = virtualAgentControl;
  return /* @__PURE__ */React106.createElement(React106.Fragment, null, /* @__PURE__ */React106.createElement(ChatContainer2, {
    visible: true,
    embedded: true,
    padded: true
  }, header && /* @__PURE__ */React106.createElement(ChatContainerHeader2, null, getHeader(header), /* @__PURE__ */React106.createElement(VirtualAgentControlErrorBanner, null)), /* @__PURE__ */React106.createElement(ChatContainerContent2, null, activeConversationId && /* @__PURE__ */React106.createElement(ActiveConversation, {
    inputs: {},
    conversationIntro
  }), /* @__PURE__ */React106.createElement(VirtualAgentControlTypingIndicator, null), /* @__PURE__ */React106.createElement(VirtualAgentControlFullPageErrorView, null), /* @__PURE__ */React106.createElement(VirtualAgentControlLoadingDialog, null)), /* @__PURE__ */React106.createElement(ChatContainerFooter2, null, /* @__PURE__ */React106.createElement(EGDSSpacing26, {
    padding: "two"
  }, /* @__PURE__ */React106.createElement("div", null, /* @__PURE__ */React106.createElement(VirtualAgentControlQuickReplies, null), /* @__PURE__ */React106.createElement(VirtualAgentControlChatFooter, {
    data: footer
  }))))), activeConversationId && /* @__PURE__ */React106.createElement(VacSignalSubscriber, null));
};
ChatWindow.displayName = "sharedui-ChatWindow";

// src/components/index.ts
var Chat2 = withRegisteredOperations(withVacId(withAttachment(Chat)));
var ChatTrigger2 = withRegisteredOperations(withVacId(ChatTrigger));
var ChatWindow2 = withRegisteredOperations(withVacId(ChatWindow));
export { Chat2 as Chat, ChatTrigger2 as ChatTrigger, ChatWindow2 as ChatWindow, ConversationMode, ConversationType };
/* istanbul ignore file -- @preserve */
// istanbul ignore next -- @preserve
/* istanbul ignore next -- @preserve */
//# sourceMappingURL=index.js.map